import React, { useState, useEffect } from "react";
import { generateClient } from 'aws-amplify/api';
import { uploadData, getUrl } from 'aws-amplify/storage';

// Suppress ResizeObserver errors (harmless React warning)
// Method 1: Debounce wrapper
const debounce = (callback, delay) => {
  let tid;
  return function (...args) {
    const ctx = this;
    tid && clearTimeout(tid);
    tid = setTimeout(() => {
      callback.apply(ctx, args);
    }, delay);
  };
};

const _ = window.ResizeObserver;
window.ResizeObserver = class ResizeObserver extends _ {
  constructor(callback) {
    callback = debounce(callback, 20);
    super(callback);
  }
};

// Method 2: Suppress the error globally
const originalError = console.error;
console.error = (...args) => {
  if (
    typeof args[0] === 'string' && 
    args[0].includes('ResizeObserver loop')
  ) {
    return; // Ignore ResizeObserver errors
  }
  originalError.apply(console, args);
};

// Method 3: Window error handler - prevent error overlay
window.addEventListener('error', (e) => {
  if (e.message && e.message.includes('ResizeObserver loop')) {
    e.stopImmediatePropagation();
    e.preventDefault();
    return false;
  }
});

// Method 4: Unhandled rejection handler
window.addEventListener('unhandledrejection', (e) => {
  if (e.reason && e.reason.message && e.reason.message.includes('ResizeObserver')) {
    e.stopImmediatePropagation();
    e.preventDefault();
  }
});

// Method 5: Override React's error handler hook
const ignoreResizeObserver = () => {
  const noop = () => {};
  ['error', 'unhandledrejection'].forEach(event => {
    const original = window[`on${event}`];
    window[`on${event}`] = function(e) {
      if (e && (e.message || e.reason?.message || '').includes('ResizeObserver')) {
        return false;
      }
      return original ? original.apply(this, arguments) : true;
    };
  });
};
ignoreResizeObserver();

const POSITIONS = ["Attack", "Midfielder", "Defense", "Goalie"];

// Calendar API endpoint
const CALENDAR_API_URL = 'https://oghttrqbwl.execute-api.us-east-2.amazonaws.com/dev/calendar';

// Role hierarchy
const ROLES = {
  SUPER_ADMIN: 'super_admin',
  CLUB_OWNER: 'club_owner',
  TEAM_MANAGER: 'team_manager',  // Was HEAD_COACH
  COACH: 'coach',                 // Was ASSISTANT_COACH
  PARENT: 'parent',
  PLAYER: 'player'
};

// Haptic feedback utility
const haptic = {
  light: () => {
    if ('vibrate' in navigator) {
      navigator.vibrate(10);
    }
  },
  medium: () => {
    if ('vibrate' in navigator) {
      navigator.vibrate(20);
    }
  },
  heavy: () => {
    if ('vibrate' in navigator) {
      navigator.vibrate(30);
    }
  },
  success: () => {
    if ('vibrate' in navigator) {
      navigator.vibrate([10, 50, 10]);
    }
  },
  error: () => {
    if ('vibrate' in navigator) {
      navigator.vibrate([30, 50, 30]);
    }
  }
};

// PWA install prompt
let deferredPrompt;
const setupPWA = () => {
  // TEMPORARILY DISABLED - Service worker might be causing caching issues
  // Register service worker
  /*
  if ('serviceWorker' in navigator) {
    window.addEventListener('load', () => {
      navigator.serviceWorker.register('/service-worker.js')
        .then((registration) => {
        })
        .catch((error) => {
        });
    });
  }
  */

  // Capture install prompt
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferredPrompt = e;
  });

  // Log when installed
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
  });
};

// Call setup on module load
setupPWA();


const palette = {
  bg: "#FFFFFF",              // White background
  surface: "#F8FAFC",         // Very light gray for cards
  surfaceAlt: "#F1F5F9",      // Slightly darker gray for alternating
  border: "#E2E8F0",          // Light gray borders
  accent: "#14B8A6",          // Teal accent (primary action color)
  accentSoft: "rgba(20,184,166,0.15)",  // Soft teal background
  accentGlow: "rgba(20,184,166,0.3)",   // Teal glow
  green: "#10B981",           // Green for success/confirmed
  red: "#EF4444",             // Red for errors/declined
  yellow: "#F59E0B",          // Yellow for warnings/maybe
  blue: "#0EA5E9",            // Bright blue for games
  navy: "#1E3A8A",            // Navy for headings/primary text
  teal: "#14B8A6",            // Teal (same as accent)
  text: "#1E293B",            // Dark navy/slate for main text
  textMuted: "#64748B",       // Medium gray for secondary text
  textDim: "#94A3B8",         // Light gray for tertiary text
};

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@400;600;700;900&family=Inter:wght@300;400;500;600&display=swap');

  * { margin: 0; padding: 0; box-sizing: border-box; }

  body {
    font-family: 'Inter', sans-serif;
    background: ${palette.bg};
    color: ${palette.text};
    min-height: 100vh;
    /* Safe area insets for iOS notch/home bar */
    padding-top: env(safe-area-inset-top);
    padding-bottom: env(safe-area-inset-bottom);
    padding-left: env(safe-area-inset-left);
    padding-right: env(safe-area-inset-right);
  }

  /* Support for pull-to-refresh and smooth scrolling */
  html, body {
    overscroll-behavior-y: contain;
    -webkit-overflow-scrolling: touch;
  }

  ::-webkit-scrollbar { width: 4px; }
  ::-webkit-scrollbar-track { background: ${palette.bg}; }
  ::-webkit-scrollbar-thumb { background: ${palette.border}; border-radius: 2px; }

  .top-banner {
    background: ${palette.surface};
    border-bottom: 1px solid ${palette.border};
    color: ${palette.textMuted};
    padding: 8px 24px;
    text-align: center;
    font-family: 'Inter', sans-serif;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    position: sticky;
    top: 0;
    z-index: 50;
  }

  .app {
    display: flex;
    min-height: calc(100vh - 33px);
    max-width: 1400px;
    margin: 0 auto;
    overflow-x: hidden; /* Prevent horizontal scroll */
  }

  /* SIDEBAR */
  .sidebar {
    width: 240px;
    background: ${palette.surface};
    border-right: 1px solid ${palette.border};
    display: flex;
    flex-direction: column;
    position: sticky;
    top: 0;
    height: 100vh;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch; /* iOS smooth scrolling */
    flex-shrink: 0;
  }

  .sidebar-brand {
    padding: 20px 20px 16px;
    border-bottom: 1px solid ${palette.border};
  }

  .brand-logo {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 4px;
  }

  .logo-badge {
    width: 36px;
    height: 36px;
    background: ${palette.accent};
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    box-shadow: 0 4px 12px ${palette.accentGlow};
  }

  .brand-name {
    font-family: 'Barlow Condensed', sans-serif;
    font-weight: 900;
    font-size: 18px;
    letter-spacing: 0.5px;
    color: ${palette.text};
  }

  .brand-meta {
    font-size: 11px;
    color: ${palette.textMuted};
    padding-left: 46px;
    margin-top: 2px;
  }

  .sidebar-nav {
    flex: 1;
    padding: 12px 0;
  }

  .nav-section {
    padding: 8px 20px 4px;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    color: ${palette.textDim};
  }

  .nav-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 9px 20px;
    cursor: pointer;
    font-size: 14px;
    color: ${palette.textMuted};
    transition: all 0.15s;
    border-left: 3px solid transparent;
    font-weight: 500;
  }

  .nav-item:hover {
    background: ${palette.surfaceAlt};
    color: ${palette.text};
  }

  .nav-item.active {
    background: ${palette.accentSoft};
    color: ${palette.accent};
    border-left-color: ${palette.accent};
  }

  .nav-icon { font-size: 16px; width: 20px; text-align: center; }

  .sidebar-footer {
    padding: 16px 20px;
    border-top: 1px solid ${palette.border};
  }

  .coach-badge {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    background: ${palette.surfaceAlt};
    border-radius: 10px;
  }

  .coach-avatar {
    width: 32px;
    height: 32px;
    background: linear-gradient(135deg, ${palette.accent}, #FF8C5A);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: 700;
    color: white;
    flex-shrink: 0;
  }

  .coach-info { overflow: hidden; }
  .coach-name { font-size: 13px; font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .coach-role { font-size: 11px; color: ${palette.textMuted}; }

  /* MAIN */
  .main { flex: 1; overflow-y: auto; min-height: 100vh; }

  .page-header {
    padding: 28px 32px 20px;
    border-bottom: 1px solid ${palette.border};
    background: ${palette.surface};
    position: sticky;
    top: 0;
    z-index: 10;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .page-title {
    font-family: 'Barlow Condensed', sans-serif;
    font-size: 28px;
    font-weight: 900;
    letter-spacing: 0.5px;
    color: ${palette.navy};
  }

  .page-sub { font-size: 13px; color: ${palette.textMuted}; margin-top: 2px; }

  .header-actions { display: flex; gap: 8px; align-items: center; }

  .btn {
    padding: 8px 16px;
    border-radius: 8px;
    border: none;
    cursor: pointer;
    font-size: 13px;
    font-weight: 600;
    font-family: 'Inter', sans-serif;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    display: flex;
    align-items: center;
    gap: 6px;
    /* Remove default tap highlight */
    -webkit-tap-highlight-color: transparent;
    user-select: none;
    /* Prevent text selection */
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
  }

  .btn:active {
    transform: scale(0.96);
  }

  .btn-primary {
    background: ${palette.accent};
    color: white;
    box-shadow: 0 2px 8px ${palette.accentGlow};
  }

  .btn-primary:hover { 
    background: #0D9488; 
    transform: translateY(-1px); 
    box-shadow: 0 4px 12px ${palette.accentGlow}; 
  }
  
  .btn-primary:active {
    transform: scale(0.96);
    box-shadow: 0 1px 4px ${palette.accentGlow};
  }

  .btn-ghost {
    background: transparent;
    color: ${palette.textMuted};
    border: 1px solid ${palette.border};
  }

  .btn-ghost:hover { background: ${palette.surfaceAlt}; color: ${palette.text}; }
  .btn-ghost:active { background: ${palette.border}; }

  .btn-secondary {
    background: ${palette.navy};
    color: white;
    border: none;
  }

  .btn-secondary:hover { 
    background: #1E40AF;
    transform: translateY(-1px);
  }
  
  .btn-secondary:active {
    transform: scale(0.96);
  }

  .btn-sm { padding: 5px 12px; font-size: 12px; }
  .btn-danger { background: #EF444420; color: ${palette.red}; border: 1px solid #EF444440; }
  .btn-danger:hover { background: #EF444430; }
  .btn-danger:active { background: #EF444440; }
  .btn-success { background: #22C55E20; color: ${palette.green}; border: 1px solid #22C55E40; }
  .btn-success:hover { background: #22C55E30; }
  .btn-success:active { background: #22C55E40; }

  .content { padding: 28px 32px; }

  /* DASHBOARD */
  .stats-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
    margin-bottom: 28px;
  }

  .stat-card {
    background: ${palette.surface};
    border: 1px solid ${palette.border};
    border-radius: 14px;
    padding: 20px;
    position: relative;
    overflow: hidden;
    transition: border-color 0.2s;
  }

  .stat-card:hover { border-color: ${palette.accent}; }

  .stat-card::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: var(--accent-color, ${palette.accent});
  }

  .stat-icon { font-size: 24px; margin-bottom: 10px; }
  .stat-value {
    font-family: 'Barlow Condensed', sans-serif;
    font-size: 36px;
    font-weight: 900;
    line-height: 1;
    margin-bottom: 4px;
  }
  .stat-label { font-size: 12px; color: ${palette.textMuted}; font-weight: 500; text-transform: uppercase; letter-spacing: 0.5px; }
  .stat-change { font-size: 12px; margin-top: 6px; font-weight: 500; }

  .dashboard-grid {
    display: grid;
    grid-template-columns: 1fr 360px;
    gap: 20px;
  }

  .card {
    background: ${palette.surface};
    border: 1px solid ${palette.border};
    border-radius: 14px;
    overflow: hidden;
  }

  .card-header {
    padding: 16px 20px;
    border-bottom: 1px solid ${palette.border};
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .card-title {
    font-family: 'Barlow Condensed', sans-serif;
    font-size: 18px;
    font-weight: 700;
    letter-spacing: 0.3px;
  }

  .card-body { padding: 20px; }

  /* EVENTS */
  .event-item {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 12px 0;
    border-bottom: 1px solid ${palette.border};
  }

  .event-item:last-child { border-bottom: none; }

  .event-date-badge {
    width: 46px;
    text-align: center;
    flex-shrink: 0;
    background: ${palette.surfaceAlt};
    border-radius: 10px;
    padding: 6px 4px;
  }

  .event-month { font-size: 9px; text-transform: uppercase; letter-spacing: 1px; color: ${palette.textMuted}; }
  .event-day { font-family: 'Barlow Condensed', sans-serif; font-size: 24px; font-weight: 900; line-height: 1; }

  .event-info { flex: 1; }
  .event-title { font-size: 14px; font-weight: 600; margin-bottom: 2px; }
  .event-meta { font-size: 12px; color: ${palette.textMuted}; }

  .event-type-badge {
    font-size: 10px;
    font-weight: 700;
    padding: 3px 8px;
    border-radius: 20px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .type-game { background: rgba(59,130,246,0.15); color: ${palette.blue}; }
  .type-practice { background: rgba(34,197,94,0.15); color: ${palette.green}; }

  /* ROSTER */
  .search-bar {
    display: flex;
    align-items: center;
    gap: 8px;
    background: ${palette.surface};
    border: 1px solid ${palette.border};
    border-radius: 10px;
    padding: 8px 14px;
    margin-bottom: 20px;
    transition: border-color 0.2s;
  }

  .search-bar:focus-within { border-color: ${palette.accent}; }

  .search-bar input {
    background: none;
    border: none;
    outline: none;
    color: ${palette.text};
    font-size: 14px;
    font-family: 'Inter', sans-serif;
    flex: 1;
  }

  .search-bar input::placeholder { color: ${palette.textDim}; }

  .roster-table { width: 100%; border-collapse: collapse; min-width: 900px; }

  .roster-table th {
    text-align: left;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 1px;
    color: ${palette.textDim};
    font-weight: 600;
    padding: 10px 16px;
    border-bottom: 1px solid ${palette.border};
    white-space: nowrap;
  }

  .roster-table td {
    padding: 12px 16px;
    border-bottom: 1px solid ${palette.border};
    font-size: 14px;
  }

  .roster-table tr:last-child td { border-bottom: none; }

  .roster-table tr:hover td { background: ${palette.surfaceAlt}; }

  .player-cell { display: flex; align-items: center; gap: 12px; }

  .player-avatar {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: 700;
    flex-shrink: 0;
    color: white;
  }

  .status-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    display: inline-block;
    margin-right: 6px;
  }

  .status-active { background: ${palette.green}; }
  .status-injured { background: ${palette.yellow}; }
  .status-inactive { background: ${palette.textDim}; }

  .position-badge {
    font-size: 11px;
    padding: 2px 8px;
    border-radius: 20px;
    background: ${palette.surfaceAlt};
    color: ${palette.textMuted};
    font-weight: 500;
    border: 1px solid ${palette.border};
  }

  .number-badge {
    font-family: 'Barlow Condensed', sans-serif;
    font-size: 20px;
    font-weight: 900;
    color: ${palette.accent};
    width: 32px;
    text-align: center;
  }

  /* SCHEDULE */
  .schedule-filters {
    display: flex;
    gap: 8px;
    margin-bottom: 20px;
  }

  .filter-btn {
    padding: 6px 14px;
    border-radius: 20px;
    border: 1px solid ${palette.border};
    background: transparent;
    color: ${palette.textMuted};
    font-size: 13px;
    cursor: pointer;
    font-family: 'Inter', sans-serif;
    font-weight: 500;
    transition: all 0.15s;
  }

  .filter-btn.active, .filter-btn:hover {
    background: ${palette.accentSoft};
    border-color: ${palette.accent};
    color: ${palette.accent};
  }

  .schedule-card {
    background: ${palette.surface};
    border: 1px solid ${palette.border};
    border-radius: 12px;
    padding: 12px 16px;
    margin-bottom: 10px;
    display: flex;
    align-items: center;
    gap: 12px;
    transition: border-color 0.2s, transform 0.15s;
    cursor: pointer;
  }

  .schedule-card:hover { border-color: ${palette.accent}; transform: translateY(-1px); }

  .schedule-date-block {
    width: 48px;
    text-align: center;
    flex-shrink: 0;
  }

  .sch-month { font-size: 9px; text-transform: uppercase; letter-spacing: 0.5px; color: ${palette.textMuted}; font-weight: 600; }
  .sch-day { font-family: 'Barlow Condensed', sans-serif; font-size: 28px; font-weight: 900; line-height: 1; margin: 2px 0; }
  .sch-year { font-size: 9px; color: ${palette.textDim}; }

  .schedule-divider { width: 1px; height: 40px; background: ${palette.border}; flex-shrink: 0; }

  .schedule-main { flex: 1; min-width: 0; }
  .schedule-title { 
    font-size: 15px; 
    font-weight: 700; 
    margin-bottom: 3px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .schedule-details { 
    font-size: 12px; 
    color: ${palette.textMuted}; 
    display: flex; 
    gap: 12px; 
    flex-wrap: wrap;
  }

  .result-badge {
    padding: 6px 14px;
    border-radius: 8px;
    font-family: 'Barlow Condensed', sans-serif;
    font-size: 20px;
    font-weight: 900;
    text-align: center;
    flex-shrink: 0;
  }

  .result-win { background: rgba(34,197,94,0.15); color: ${palette.green}; }
  .result-loss { background: rgba(239,68,68,0.15); color: ${palette.red}; }
  .result-upcoming { background: ${palette.accentSoft}; color: ${palette.accent}; font-size: 11px; font-weight: 600; padding: 6px 12px; }

  /* MESSAGES */
  .messages-container {
    display: flex;
    flex-direction: column;
    height: calc(100vh - 200px);
    min-height: 400px;
    max-height: calc(100vh - 150px);
  }

  .messages-list {
    flex: 1;
    overflow-y: auto;
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .message-bubble {
    display: flex;
    gap: 12px;
    align-items: flex-start;
  }

  .msg-avatar {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: 700;
    flex-shrink: 0;
    color: white;
  }

  .msg-content { flex: 1; }

  .msg-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 4px;
  }

  .msg-author { font-size: 13px; font-weight: 600; }
  .msg-time { font-size: 11px; color: ${palette.textDim}; }

  .msg-pin {
    font-size: 10px;
    background: rgba(245,158,11,0.15);
    color: ${palette.yellow};
    padding: 2px 6px;
    border-radius: 4px;
    font-weight: 600;
  }

  .msg-text {
    background: ${palette.surface};
    border: 1px solid ${palette.border};
    border-radius: 0 12px 12px 12px;
    padding: 10px 14px;
    font-size: 14px;
    line-height: 1.5;
  }

  .msg-input-area {
    padding: 16px 20px;
    border-top: 1px solid ${palette.border};
    display: flex;
    gap: 10px;
    background: ${palette.bg};
    position: sticky;
    bottom: 0;
    z-index: 10;
  }

  .msg-input {
    flex: 1;
    background: ${palette.surface};
    border: 1px solid ${palette.border};
    border-radius: 10px;
    padding: 10px 14px;
    color: ${palette.text};
    font-size: 14px;
    font-family: 'Inter', sans-serif;
    outline: none;
    transition: border-color 0.2s;
  }

  .msg-input:focus { border-color: ${palette.accent}; }
  .msg-input::placeholder { color: ${palette.textDim}; }

  /* ATTENDANCE */
  .attendance-grid {
    display: grid;
    gap: 12px;
  }

  .attendance-row {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 12px 16px;
    background: ${palette.surface};
    border: 1px solid ${palette.border};
    border-radius: 10px;
  }

  .att-name { flex: 1; font-size: 14px; font-weight: 500; }

  .att-btns { display: flex; gap: 6px; }

  .att-btn {
    padding: 5px 12px;
    border-radius: 6px;
    border: 1px solid;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    font-family: 'Inter', sans-serif;
    transition: all 0.15s;
  }

  .att-yes { background: rgba(34,197,94,0.1); border-color: rgba(34,197,94,0.3); color: ${palette.green}; }
  .att-yes:hover { background: rgba(34,197,94,0.15); border-color: rgba(34,197,94,0.5); }
  .att-yes.selected { background: ${palette.green}; border-color: ${palette.green}; color: white; box-shadow: 0 2px 8px rgba(34,197,94,0.4); transform: scale(1.05); }
  
  .att-no { background: rgba(239,68,68,0.1); border-color: rgba(239,68,68,0.3); color: ${palette.red}; }
  .att-no:hover { background: rgba(239,68,68,0.15); border-color: rgba(239,68,68,0.5); }
  .att-no.selected { background: ${palette.red}; border-color: ${palette.red}; color: white; box-shadow: 0 2px 8px rgba(239,68,68,0.4); transform: scale(1.05); }
  
  .att-maybe { background: rgba(245,158,11,0.1); border-color: rgba(245,158,11,0.3); color: ${palette.yellow}; }
  .att-maybe:hover { background: rgba(245,158,11,0.15); border-color: rgba(245,158,11,0.5); }
  .att-maybe.selected { background: ${palette.yellow}; border-color: ${palette.yellow}; color: ${palette.bg}; box-shadow: 0 2px 8px rgba(245,158,11,0.4); transform: scale(1.05); }

  /* MODAL */
  .modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,0.7);
    display: flex;
    align-items: flex-start; /* Changed from center - allows scrolling */
    justify-content: center;
    z-index: 100;
    backdrop-filter: blur(4px);
    overflow-y: auto; /* Allow overlay itself to scroll */
    padding: 20px; /* Add padding all around for mobile */
  }

  .modal {
    background: ${palette.surface};
    border: 1px solid ${palette.border};
    border-radius: 16px;
    padding: 28px;
    width: 480px;
    max-width: 100%; /* Changed from 90vw to 100% since padding is on overlay */
    max-height: none; /* Remove max-height constraint */
    margin: auto; /* Center vertically when content is shorter than viewport */
    animation: modalIn 0.2s ease;
  }

  @keyframes modalIn {
    from { opacity: 0; transform: scale(0.95) translateY(10px); }
    to { opacity: 1; transform: scale(1) translateY(0); }
  }

  .modal-title {
    font-family: 'Barlow Condensed', sans-serif;
    font-size: 24px;
    font-weight: 900;
    margin-bottom: 20px;
  }

  .form-group { margin-bottom: 16px; }

  .form-label {
    display: block;
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: ${palette.textMuted};
    margin-bottom: 6px;
  }

  .form-input {
    width: 100%;
    background: ${palette.bg};
    border: 1px solid ${palette.border};
    border-radius: 8px;
    padding: 10px 12px;
    color: ${palette.text};
    font-size: 14px;
    font-family: 'Inter', sans-serif;
    outline: none;
    transition: border-color 0.2s;
  }

  .form-input:focus { border-color: ${palette.accent}; }

  .form-select {
    width: 100%;
    background: ${palette.bg};
    border: 1px solid ${palette.border};
    border-radius: 8px;
    padding: 10px 12px;
    color: ${palette.text};
    font-size: 14px;
    font-family: 'Inter', sans-serif;
    outline: none;
    cursor: pointer;
    transition: border-color 0.2s;
  }

  .form-select:focus { border-color: ${palette.accent}; }

  .form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
  .modal-actions { display: flex; gap: 10px; justify-content: flex-end; margin-top: 24px; }

  /* RECORD */
  .record-stats {
    display: grid;
    grid-template-columns: repeat(3,1fr);
    gap: 12px;
    margin-bottom: 20px;
  }

  .record-stat {
    background: ${palette.surfaceAlt};
    border-radius: 10px;
    padding: 14px;
    text-align: center;
  }

  .record-num {
    font-family: 'Barlow Condensed', sans-serif;
    font-size: 32px;
    font-weight: 900;
    line-height: 1;
  }

  .record-lbl { font-size: 11px; color: ${palette.textMuted}; text-transform: uppercase; letter-spacing: 0.5px; margin-top: 4px; }

  /* AVATARS */
  .avatar-colors {
    background: var(--av-color);
  }

  /* WIN RATE BAR */
  .win-bar-container {
    background: ${palette.surfaceAlt};
    border-radius: 4px;
    height: 8px;
    overflow: hidden;
    margin-top: 8px;
  }

  .win-bar { height: 100%; border-radius: 4px; background: linear-gradient(90deg, ${palette.green}, #16A34A); transition: width 0.6s ease; }

  /* LOGIN */
  .login-page {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${palette.bg};
    position: relative;
    overflow: hidden;
  }

  .login-bg-glow {
    position: absolute;
    width: 600px;
    height: 600px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(255,107,53,0.08) 0%, transparent 70%);
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    pointer-events: none;
  }

  .login-card {
    background: ${palette.surface};
    border: 1px solid ${palette.border};
    border-radius: 20px;
    padding: 40px 44px;
    width: 420px;
    max-width: 92vw;
    position: relative;
    z-index: 1;
    box-shadow: 0 24px 64px rgba(0,0,0,0.4);
    animation: loginIn 0.3s ease;
  }

  @keyframes loginIn {
    from { opacity: 0; transform: translateY(20px) scale(0.97); }
    to   { opacity: 1; transform: translateY(0)    scale(1); }
  }

  .login-brand {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 28px;
  }

  .login-logo {
    width: 48px;
    height: 48px;
    background: ${palette.accent};
    border-radius: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 24px;
    box-shadow: 0 6px 20px ${palette.accentGlow};
    flex-shrink: 0;
  }

  .login-brand-text { }
  .login-team-name {
    font-family: 'Barlow Condensed', sans-serif;
    font-size: 22px;
    font-weight: 900;
    letter-spacing: 0.3px;
  }
  .login-team-sub { font-size: 12px; color: ${palette.textMuted}; margin-top: 1px; }

  .login-heading {
    font-family: 'Barlow Condensed', sans-serif;
    font-size: 30px;
    font-weight: 900;
    margin-bottom: 6px;
  }

  .login-sub {
    font-size: 13px;
    color: ${palette.textMuted};
    margin-bottom: 28px;
    line-height: 1.5;
  }

  .login-tabs {
    display: flex;
    background: ${palette.surfaceAlt};
    border-radius: 10px;
    padding: 4px;
    margin-bottom: 24px;
    gap: 4px;
  }

  .login-tab {
    flex: 1;
    padding: 8px;
    text-align: center;
    border-radius: 7px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    color: ${palette.textMuted};
    border: none;
    background: none;
    font-family: 'Inter', sans-serif;
  }

  .login-tab.active {
    background: ${palette.surface};
    color: ${palette.text};
    box-shadow: 0 2px 8px rgba(0,0,0,0.3);
  }

  .login-error {
    background: rgba(239,68,68,0.1);
    border: 1px solid rgba(239,68,68,0.3);
    border-radius: 8px;
    padding: 10px 14px;
    font-size: 13px;
    color: ${palette.red};
    margin-bottom: 16px;
    animation: shake 0.3s ease;
  }

  @keyframes shake {
    0%,100% { transform: translateX(0); }
    25%      { transform: translateX(-6px); }
    75%      { transform: translateX(6px); }
  }

  .login-success {
    background: rgba(34,197,94,0.1);
    border: 1px solid rgba(34,197,94,0.3);
    border-radius: 8px;
    padding: 10px 14px;
    font-size: 13px;
    color: ${palette.green};
    margin-bottom: 16px;
  }

  .login-btn {
    width: 100%;
    padding: 12px;
    background: ${palette.accent};
    color: white;
    border: none;
    border-radius: 10px;
    font-size: 15px;
    font-weight: 700;
    font-family: 'Inter', sans-serif;
    cursor: pointer;
    margin-top: 8px;
    transition: all 0.2s;
    box-shadow: 0 4px 14px ${palette.accentGlow};
  }

  .login-btn:hover { background: #FF7D45; transform: translateY(-1px); box-shadow: 0 6px 18px ${palette.accentGlow}; }
  .login-btn:active { transform: translateY(0); }

  .login-divider {
    display: flex;
    align-items: center;
    gap: 12px;
    margin: 20px 0;
    color: ${palette.textDim};
    font-size: 12px;
  }

  .login-divider::before, .login-divider::after {
    content: '';
    flex: 1;
    height: 1px;
    background: ${palette.border};
  }

  .demo-accounts {
    background: ${palette.surfaceAlt};
    border: 1px solid ${palette.border};
    border-radius: 10px;
    overflow: hidden;
  }

  .demo-account-row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 14px;
    border-bottom: 1px solid ${palette.border};
    cursor: pointer;
    transition: background 0.15s;
  }

  .demo-account-row:last-child { border-bottom: none; }
  .demo-account-row:hover { background: rgba(255,107,53,0.06); }

  .demo-av {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    font-weight: 700;
    color: white;
    flex-shrink: 0;
  }

  .demo-info { flex: 1; }
  .demo-name { font-size: 13px; font-weight: 600; }
  .demo-email { font-size: 11px; color: ${palette.textMuted}; }
  .demo-role {
    font-size: 10px;
    font-weight: 700;
    padding: 2px 7px;
    border-radius: 20px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  .demo-role-coach { background: rgba(255,107,53,0.15); color: ${palette.accent}; }
  .demo-role-player { background: rgba(59,130,246,0.15); color: ${palette.blue}; }
  .demo-role-owner { background: rgba(139,92,246,0.15); color: #8B5CF6; }
  .demo-role-parent { background: rgba(34,197,94,0.15); color: ${palette.green}; }
  .demo-role-assistant { background: rgba(245,158,11,0.15); color: ${palette.yellow}; }

  /* MOBILE STYLES */
  .mobile-header {
    display: none;
  }

  .hamburger {
    background: none;
    border: none;
    color: ${palette.text};
    font-size: 24px;
    cursor: pointer;
    padding: 8px;
  }

  .mobile-overlay {
    display: none;
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,0.5);
    z-index: 1000; /* Below sidebar but above everything else */
  }

  .mobile-overlay.active {
    display: block;
  }

  /* TOAST NOTIFICATIONS */
  .toast-container {
    position: fixed;
    bottom: calc(80px + env(safe-area-inset-bottom));
    left: 50%;
    transform: translateX(-50%);
    z-index: 9999;
    pointer-events: none;
  }

  .toast {
    background: ${palette.surface};
    border: 1px solid ${palette.border};
    border-radius: 12px;
    padding: 14px 20px;
    margin-bottom: 12px;
    box-shadow: 0 8px 24px rgba(0,0,0,0.4);
    min-width: 280px;
    max-width: 400px;
    pointer-events: all;
    animation: slideUp 0.3s ease-out;
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .toast.success {
    border-left: 3px solid ${palette.green};
  }

  .toast.error {
    border-left: 3px solid ${palette.red};
  }

  .toast.info {
    border-left: 3px solid ${palette.blue};
  }

  @keyframes slideUp {
    from {
      transform: translateY(100%);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }

  /* LOADING STATES */
  .loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 20px;
    color: ${palette.textMuted};
  }

  .loading-spinner {
    width: 40px;
    height: 40px;
    border: 3px solid ${palette.border};
    border-top-color: ${palette.accent};
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
    margin-bottom: 16px;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  /* OFFLINE INDICATOR */
  .offline-banner {
    position: fixed;
    top: calc(33px + env(safe-area-inset-top));
    left: 0;
    right: 0;
    background: ${palette.red};
    color: white;
    padding: 8px 16px;
    text-align: center;
    font-size: 13px;
    font-weight: 600;
    z-index: 9998;
    animation: slideDown 0.3s ease-out;
  }

  @keyframes slideDown {
    from {
      transform: translateY(-100%);
    }
    to {
      transform: translateY(0);
    }
  }

  /* BOTTOM TAB NAVIGATION (Mobile Only) */
  .bottom-nav {
    display: none;
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    background: ${palette.surface};
    border-top: 1px solid ${palette.border};
    padding-bottom: env(safe-area-inset-bottom);
    z-index: 1000;
  }

  .bottom-nav-content {
    display: flex;
    justify-content: space-around;
    align-items: center;
    height: 64px;
    max-width: 600px;
    margin: 0 auto;
  }

  .bottom-nav-item {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    padding: 8px;
    color: ${palette.textMuted};
    font-size: 11px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    text-decoration: none;
    background: none;
    border: none;
    min-height: 44px;
  }

  .bottom-nav-item.active {
    color: ${palette.accent};
  }

  .bottom-nav-icon {
    font-size: 24px;
  }

  /* PULL TO REFRESH */
  .pull-to-refresh {
    position: relative;
  }

  .pull-to-refresh-indicator {
    position: absolute;
    top: -50px;
    left: 50%;
    transform: translateX(-50%);
    font-size: 24px;
    transition: all 0.3s;
  }

  .pull-to-refresh-indicator.pulling {
    top: 10px;
  }

  @media (max-width: 768px) {
    .app {
      flex-direction: column;
      padding-bottom: calc(64px + env(safe-area-inset-bottom));
    }

    /* Show bottom nav on mobile */
    .bottom-nav {
      display: block;
    }

    /* Sidebar slides in from left on mobile when opened */
    .sidebar {
      position: fixed;
      left: -240px;
      top: 0;
      height: 100vh;
      transition: left 0.3s ease;
      z-index: 1001; /* Above overlay */
      width: 240px;
    }

    .sidebar.mobile-open {
      left: 0;
    }

    .mobile-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 12px 16px;
      background: ${palette.surface};
      border-bottom: 1px solid ${palette.border};
      position: sticky;
      top: calc(33px + env(safe-area-inset-top));
      z-index: 100;
    }

    .mobile-header-title {
      font-family: 'Barlow Condensed', sans-serif;
      font-weight: 700;
      font-size: 18px;
    }

    .main {
      width: 100%;
      padding: 16px;
      overflow-x: hidden; /* Prevent horizontal scroll */
    }

    .content {
      overflow-x: hidden; /* Prevent horizontal scroll */
    }

    .page-header {
      padding: 16px 16px 12px; /* Much smaller padding */
      flex-direction: column;
      gap: 10px;
      align-items: flex-start;
    }

    .page-title {
      font-size: 22px; /* Smaller title */
    }

    .page-sub {
      font-size: 12px;
      margin-top: 0;
    }

    .header-actions {
      width: 100%;
      display: flex;
      flex-direction: row; /* Horizontal instead of vertical */
      gap: 6px;
      flex-wrap: wrap;
    }

    .page-actions {
      width: 100%;
    }

    .btn {
      flex: 1; /* Buttons share space horizontally */
      min-width: fit-content;
      justify-content: center;
      min-height: 40px; /* Slightly smaller tap target */
      font-size: 13px; /* Smaller button text */
      padding: 8px 12px;
    }

    .stats-grid {
      grid-template-columns: 1fr 1fr;
    }

    .stat-card {
      padding: 16px;
    }

    /* Hide table on mobile, use cards instead */
    .roster-table-wrapper {
      display: none;
    }

    .roster-table table {
      display: none;
    }

    /* Show mobile card view */
    .roster-mobile-view {
      display: block !important;
    }

    /* Compact mobile player rows */
    .player-card-mobile {
      background: ${palette.surface};
      border-bottom: 1px solid ${palette.border};
      padding: 10px 12px;
      cursor: pointer;
      transition: background 0.15s;
    }

    .player-card-mobile:active {
      background: ${palette.surfaceAlt};
    }

    .player-card-mobile-header {
      display: flex;
      align-items: center;
      gap: 10px;
    }

    .player-card-mobile-avatar {
      width: 36px;
      height: 36px;
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: 700;
      font-size: 14px;
      color: white;
      flex-shrink: 0;
    }

    .player-card-mobile-info {
      flex: 1;
      min-width: 0;
      overflow: hidden;
    }

    .player-card-mobile-name {
      font-weight: 600;
      font-size: 15px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      display: flex;
      align-items: baseline;
      gap: 6px;
    }
    
    .player-card-mobile-name .player-name-text {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      flex: 1;
      min-width: 0;
    }
    
    .player-card-mobile-name .player-number {
      color: ${palette.textMuted};
      font-weight: 400;
      flex-shrink: 0;
      white-space: nowrap;
    }

    .player-card-mobile-actions {
      display: flex;
      gap: 8px;
      margin-top: 12px;
      padding-top: 12px;
      border-top: 1px solid ${palette.border};
    }

    .player-card-mobile-actions .btn {
      flex: 1;
      min-height: 40px;
    }

    .event-card {
      flex-direction: column;
      gap: 12px;
    }

    .event-actions {
      width: 100%;
      justify-content: flex-start;
    }

    /* Dashboard event cards - better mobile layout */
    .dashboard-upcoming .card > div:first-child {
      flex-direction: column;
      align-items: flex-start !important;
      gap: 12px;
    }

    .dashboard-upcoming .card .btn {
      width: 100%;
      margin-top: 8px;
    }

    /* Event details - wrap properly */
    .dashboard-upcoming .card > div:first-child > div:nth-child(2) {
      width: 100%;
    }

    /* Attendance avatars - prevent overflow */
    .dashboard-upcoming .card > div:last-child > div:last-child {
      overflow-x: auto;
      -webkit-overflow-scrolling: touch;
    }

    /* Prevent modals from being too wide */
    .modal {
      max-width: 95vw;
      max-height: 90vh;
      overflow-y: auto;
    }

    /* Make form inputs touch-friendly */
    .form-input,
    .form-select {
      min-height: 44px;
      font-size: 16px; /* Prevents iOS zoom */
    }

    .form-row {
      flex-direction: column;
    }

    /* Teams tab - prevent overflow */
    .team-card {
      overflow-x: hidden;
    }

    /* Messages - sticky input at bottom */
    .messages-container {
      height: calc(100vh - 140px);
      max-height: calc(100vh - 140px);
      position: relative;
      margin-bottom: calc(64px + env(safe-area-inset-bottom));
    }

    .messages-list {
      padding-bottom: 80px; /* Space for sticky input */
    }

    .msg-input-area {
      position: fixed;
      bottom: calc(64px + env(safe-area-inset-bottom));
      left: 0;
      right: 0;
      background: ${palette.bg};
      padding: 12px 16px;
      border-top: 1px solid ${palette.border};
      z-index: 100;
    }

    .msg-input {
      font-size: 16px; /* Prevents iOS zoom */
    }

    /* Toast container adjustment for mobile */
    .toast-container {
      bottom: calc(64px + 20px + env(safe-area-inset-bottom));
    }

    /* Attendance buttons - smaller on mobile */
    .att-btn {
      font-size: 14px;
      padding: 8px;
      min-width: 36px;
      white-space: nowrap;
    }

    .attendance-player-row {
      gap: 8px !important;
      padding: 8px 12px !important;
    }

    .attendance-buttons {
      gap: 4px !important;
    }

    /* Attendance card - prevent overflow */
    .content > div > div {
      overflow-x: hidden;
    }
  }

  @media (max-width: 480px) {
    .stats-grid {
      grid-template-columns: 1fr;
    }

    .card {
      padding: 16px;
    }

    /* Full screen modals on very small screens */
    .modal {
      max-width: 100vw;
      max-height: 100vh;
      margin: 0;
      border-radius: 0;
    }
  }
`;


const avatarColors = [
  "#FF6B35","#3B82F6","#22C55E","#F59E0B","#8B5CF6","#EC4899","#14B8A6","#F97316"
];

function getAvatarColor(id) {
  return avatarColors[(id - 1) % avatarColors.length];
}

function formatDate(dateStr) {
  const d = new Date(dateStr + "T00:00:00");
  return {
    month: d.toLocaleString("default", { month: "short" }).toUpperCase(),
    day: d.getDate(),
    year: d.getFullYear(),
  };
}

function formatPhone(phoneStr) {
  if (!phoneStr) return '';
  
  // Remove all non-digit characters
  const digits = phoneStr.replace(/\D/g, '');
  
  // Format based on length
  if (digits.length === 10) {
    // US format: (503) 555-1234
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  } else if (digits.length === 11 && digits[0] === '1') {
    // US with country code: +1 (503) 555-1234
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  } else if (digits.length > 0) {
    // Just return the digits if it's a different format
    return digits;
  }
  
  return phoneStr; // Return original if empty or invalid
}

// ─── iCal helpers ───────────────────────────────────────────────────────────

function pad(n) { return String(n).padStart(2, "0"); }

function toICalDate(dateStr, timeStr) {
  // returns a UTC datetime string YYYYMMDDTHHMMSSZ
  const [y, m, d] = dateStr.split("-");
  const [h, min] = timeStr ? timeStr.split(":") : ["09","00"];
  return `${y}${m}${d}T${h}${min}00`;
}

function buildICS(events, teamName) {
  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//TeamManager//EN",
    `X-WR-CALNAME:${teamName} Schedule`,
    "X-WR-CALDESC:Team schedule with games and practices",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
  ];

  events.forEach(ev => {
    const dtStart = toICalDate(ev.date, ev.time);
    // default 90-minute duration
    const [h, m] = (ev.time || "09:00").split(":").map(Number);
    const endH = h + 1;
    const endM = m + 30;
    const endHAdj = endM >= 60 ? endH + 1 : endH;
    const endMAdj = endM >= 60 ? endM - 60 : endM;
    const dtEnd = toICalDate(ev.date, `${pad(endHAdj)}:${pad(endMAdj)}`);

    const summary = ev.type === "game"
      ? `${teamName}: ${ev.title}`
      : `${teamName} Practice${ev.notes ? ` – ${ev.notes}` : ""}`;

    lines.push("BEGIN:VEVENT");
    lines.push(`UID:teammanager-${ev.id}@thunderhawksfc`);
    lines.push(`DTSTAMP:${toICalDate(new Date().toISOString().split("T")[0], new Date().toTimeString().slice(0,5))}`);
    lines.push(`DTSTART:${dtStart}`);
    lines.push(`DTEND:${dtEnd}`);
    lines.push(`SUMMARY:${summary}`);
    if (ev.location) lines.push(`LOCATION:${ev.location}`);
    if (ev.type === "game") {
      lines.push(`DESCRIPTION:${ev.homeAway === "home" ? "Home" : "Away"} game vs ${ev.opponent || "TBD"}`);
    }
    lines.push("END:VEVENT");
  });

  lines.push("END:VCALENDAR");
  return lines.join("\r\n");
}

function downloadICS(events, teamName) {
  const ics = buildICS(events, teamName);
  const blob = new Blob([ics], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${teamName.replace(/\s+/g, "_")}_Schedule.ics`;
  a.click();
  URL.revokeObjectURL(url);
}

function buildGoogleCalUrl(event, teamName) {
  const fmt = (dateStr, timeStr) => {
    const [y,m,d] = dateStr.split("-");
    const [h,min] = (timeStr || "09:00").split(":");
    return `${y}${m}${d}T${h}${min}00`;
  };
  const [h,m] = (event.time || "09:00").split(":").map(Number);
  const endH = h + 1; const endM = m + 30;
  const endHAdj = endM >= 60 ? endH+1 : endH;
  const endMAdj = endM >= 60 ? endM-60 : endM;
  const title = encodeURIComponent(event.type === "game" ? `${teamName}: ${event.title}` : `${teamName} Practice`);
  const loc = encodeURIComponent(event.location || "");
  const details = encodeURIComponent(event.type === "game" ? `${event.homeAway === "home" ? "Home":"Away"} game vs ${event.opponent||"TBD"}` : event.notes || "");
  return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${fmt(event.date,event.time)}/${fmt(event.date,`${String(endHAdj).padStart(2,"0")}:${String(endMAdj).padStart(2,"0")}`)}&location=${loc}&details=${details}`;
}

// ─────────────────────────────────────────────────────────────────────────────

export default function TeamManager({ user, signOut }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [club, setClub] = useState(null);
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const [hasFetchedData, setHasFetchedData] = useState(false);
  const [allClubs, setAllClubs] = useState([]); // For super_admin
  const [selectedClubId, setSelectedClubId] = useState(null); // For super_admin club switching
  const [tab, setTab] = useState("dashboard");
  const [search, setSearch] = useState("");
  const [scheduleFilter, setScheduleFilter] = useState("all");
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showAddPlayer, setShowAddPlayer] = useState(false);
  const [showEditPlayer, setShowEditPlayer] = useState(false);
  const [showEmailPicker, setShowEmailPicker] = useState(false);
  const [selectedEmailRecipients, setSelectedEmailRecipients] = useState([]);
  const [editingPlayer, setEditingPlayer] = useState(null);
  const [showAddEvent, setShowAddEvent] = useState(false);
  const [showEditEvent, setShowEditEvent] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [showSubscribe, setShowSubscribe] = useState(false);
  const [showAddTeam, setShowAddTeam] = useState(false);
  const [showEditTeam, setShowEditTeam] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [teamToDelete, setTeamToDelete] = useState(null);
  const [showAssignCoach, setShowAssignCoach] = useState(false);
  const [teamForCoachAssignment, setTeamForCoachAssignment] = useState(null);
  const [newTeam, setNewTeam] = useState({ name: "", sport: "", season: "", ageGroup: "", color: "#FF6B35", logo: "⚽" });
  const [newMessage, setNewMessage] = useState("");
  const [newPlayer, setNewPlayer] = useState({ 
    name: "", number: "", position: "Attack", email: "", phone: "",
    headshot: null, parent1Name: "", parent1Phone: "", parent1Email: "",
    parent2Name: "", parent2Phone: "", parent2Email: ""
  });
  const [newEvent, setNewEvent] = useState({ type: "game", title: "", date: "", time: "", endTime: "", arrivalTime: "", location: "", opponent: "", homeAway: "home", notes: "", volunteerSlotsTotal: 0, volunteerSignups: [], volunteerTeamIds: [] });
  const [attendance, setAttendance] = useState({});
  const [expandedParent, setExpandedParent] = useState(null); // format: "playerId-parent1" or "playerId-parent2"
  const [expandedPlayerCard, setExpandedPlayerCard] = useState(null); // for mobile roster cards
  const [headshotUrls, setHeadshotUrls] = useState({}); // Cache of path -> URL mappings
  const [loading, setLoading] = useState(true);
  const [availableCoaches, setAvailableCoaches] = useState([]);
  const [teamCoachList, setTeamCoachList] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("player");
  const [inviteSending, setInviteSending] = useState(false);
  const [showCreateClub, setShowCreateClub] = useState(false);
  const [newClubName, setNewClubName] = useState("");
  const [newClubOwnerEmail, setNewClubOwnerEmail] = useState("");
  const [showAssignOwner, setShowAssignOwner] = useState(false);
  const [clubForOwnerAssignment, setClubForOwnerAssignment] = useState(null);
  const [assignOwnerEmail, setAssignOwnerEmail] = useState("");
  const client = generateClient();

  // Phase 1: New Mobile Features State
  const [toasts, setToasts] = useState([]);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isPulling, setIsPulling] = useState(false);
  const [pullStartY, setPullStartY] = useState(0);

  useEffect(() => {
    
    if (user && user.signInDetails && user.signInDetails.loginId) {
      const email = user.signInDetails.loginId;
      
      // Determine initial role
      let initialRole = ROLES.PLAYER;
      if (email === "bryan.leinwand@gmail.com") {
        initialRole = ROLES.SUPER_ADMIN; // You are super admin
      }
      
      // Fetch User record from DynamoDB to get defaultTeamId
      const fetchUserRecord = async () => {
        try {
          const getUserQuery = `
            query GetUserByUserId($userId: String!) {
              listUsers(filter: {userId: {eq: $userId}}) {
                items {
                  id
                  userId
                  email
                  name
                  role
                  defaultTeamId
                  clubId
                  clubIds
                  teamIds
                  playerIds
                }
              }
            }
          `;
          
          const result = await client.graphql({
            query: getUserQuery,
            variables: {
              userId: user.userId
            }
          });
          
          const users = result.data?.listUsers?.items || [];
          
          if (users.length > 0) {
            const userRecord = users[0];
            
            setCurrentUser({
              id: user.userId,
              email: email,
              name: userRecord.name || email.split('@')[0],
              role: userRecord.role || initialRole,
              defaultTeamId: userRecord.defaultTeamId,  // ← Get from DB!
              clubId: userRecord.clubId,
              clubIds: userRecord.clubIds,
              teamIds: userRecord.teamIds,
              playerIds: userRecord.playerIds
            });
          } else {
            setCurrentUser({
              id: user.userId,
              email: email,
              name: email.split('@')[0],
              role: initialRole
            });
          }
        } catch (error) {
          // Fall back to basic user object
          setCurrentUser({
            id: user.userId,
            email: email,
            name: email.split('@')[0],
            role: initialRole
          });
        }
      };
      
      fetchUserRecord();
    } else {
    }
  }, [user]);

  // Load club data on mount
  useEffect(() => {
    if (currentUser && !hasFetchedData) {
      setHasFetchedData(true);
      fetchClubData();
    } else {
    }
  }, [currentUser]);

  // Load users if owner or super admin
  useEffect(() => {
    if (currentUser && (currentUser.role === ROLES.SUPER_ADMIN || currentUser.role === ROLES.CLUB_OWNER || currentUser.role === "owner") && club) {
      loadAllUsers();
    }
  }, [currentUser, club]);

  // Load headshot URLs when current team changes
  useEffect(() => {
    async function loadHeadshotUrls() {
      if (!currentUser) return;
      
      // Get roster from accessible teams
      const teams = club?.teams?.items || [];
      const accessibleTeams = teams.filter(t => {
        // Include logic to determine accessible teams
        return true; // Simplified for now - will use actual roster later
      });
      
      const selectedTeam = accessibleTeams.find(t => t.id === selectedTeamId) || accessibleTeams[0];
      const roster = selectedTeam?.roster?.items || selectedTeam?.roster || [];
      
      const urls = {};
      for (const player of roster) {
        if (player.headshot && !player.headshot.startsWith('http')) {
          try {
            urls[player.id] = await getHeadshotUrl(player.headshot);
          } catch (error) {
            console.error(`Error loading headshot for ${player.name}:`, error);
          }
        } else if (player.headshot) {
          // Already a full URL (legacy)
          urls[player.id] = player.headshot;
        }
      }
      setHeadshotUrls(urls);
    }
    
    if (currentUser && selectedTeamId) {
      loadHeadshotUrls();
    }
  }, [currentUser, selectedTeamId, club]); // Re-run when team changes

  // Phase 1: Online/Offline Detection (must be before early returns)
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      showToast('Back online', 'success');
    };
    
    const handleOffline = () => {
      setIsOnline(false);
    };
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Load coaches when modal opens
  useEffect(() => {
    if (showAssignCoach && teamForCoachAssignment) {
      console.log('Modal opened, loading coaches for team:', teamForCoachAssignment.name);
      loadCoaches();
    }
    // When modal closes (showAssignCoach = false), don't do anything
  }, [showAssignCoach, teamForCoachAssignment?.id]);

  // Load coaches when selected team changes (for displaying in roster)
  useEffect(() => {
    async function loadCurrentTeamCoaches() {
      if (selectedTeamId) {
        console.log('Loading coaches for selected team:', selectedTeamId);
        
        // Fetch fresh team data from database
        const getTeamQuery = `
          query GetTeam($id: ID!) {
            getTeam(id: $id) {
              id
              name
              coachIds
            }
          }
        `;
        
        try {
          const teamResult = await client.graphql({
            query: getTeamQuery,
            variables: { id: selectedTeamId }
          });
          
          const freshTeam = teamResult.data?.getTeam;
          console.log('Selected team fresh data:', freshTeam);
          
          if (freshTeam) {
            const coaches = await getTeamCoachesWithFreshData(freshTeam);
            setTeamCoachList(coaches);
            console.log('Loaded coaches for display:', coaches.map(c => c.name));
          }
        } catch (error) {
          console.error('Error loading team coaches:', error);
        }
      }
    }
    
    loadCurrentTeamCoaches();
  }, [selectedTeamId]);

  if (!currentUser) {
    return (
      <div style={{
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '100vh',
        background: palette.bg,
        color: palette.text
      }}>
        <div style={{textAlign: 'center'}}>
          <div style={{fontSize: '48px', marginBottom: '16px'}}>⏳</div>
          <div style={{fontSize: '18px', fontWeight: '600'}}>Setting up your account...</div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <>
        <style>{css}</style>
        <div className="loading-container" style={{minHeight: '100vh', background: palette.bg}}>
          <div className="loading-spinner"></div>
          <div style={{fontSize: '16px', fontWeight: '600', color: palette.text}}>Loading your data...</div>
        </div>
      </>
    );
  }

  if (!club) {
    return (
      <>
        <style>{css}</style>
        <div style={{
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          minHeight: '100vh',
          background: palette.bg,
          color: palette.text,
          padding: '20px'
        }}>
          <div style={{textAlign: 'center', maxWidth: '500px'}}>
            <div style={{fontSize: '64px', marginBottom: '24px'}}>👋</div>
            <div style={{fontSize: '28px', fontWeight: '900', marginBottom: '12px', fontFamily: 'Barlow Condensed, sans-serif'}}>
              Welcome to Team Manager!
            </div>
            <div style={{fontSize: '16px', color: palette.textMuted, marginBottom: '32px', lineHeight: '1.6'}}>
              You don't have access to any teams yet. Ask your coach or team admin to add you to a team, or create your own club to get started.
            </div>
            
            <div style={{display: 'flex', flexDirection: 'column', gap: '12px', maxWidth: '300px', margin: '0 auto'}}>
              <button 
                className="btn btn-primary" 
                onClick={fetchClubData}
              >
                🔄 Refresh
              </button>
              
              <button 
                className="btn btn-ghost" 
                onClick={() => {
                  const email = user?.signInDetails?.loginId;
                  if (email) {
                    navigator.clipboard.writeText(email);
                    showToast('Email copied to clipboard!', 'success');
                  }
                }}
              >
                📋 Copy My Email
              </button>
              
              <div style={{
                marginTop: '20px',
                padding: '16px',
                background: palette.surfaceAlt,
                borderRadius: '8px',
                fontSize: '13px',
                color: palette.textMuted,
                textAlign: 'left'
              }}>
                <div style={{fontWeight: '700', marginBottom: '8px', color: palette.text}}>
                  Need help?
                </div>
                <div style={{marginBottom: '4px'}}>
                  📧 Share your email with your coach: <strong>{user?.signInDetails?.loginId}</strong>
                </div>
                <div>
                  They can add you to the team roster or invite you.
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  async function loadAllUsers() {
    try {
      const listUsersQuery = `
        query ListUsers {
          listUsers {
            items {
              id
              userId
              email
              name
              role
              avatar
              playerIds
            }
          }
        }
      `;
      
      const result = await client.graphql({
        query: listUsersQuery
      });
      
      const users = result.data?.listUsers?.items || [];
      setAllUsers(users);
    } catch (error) {
    }
  }

  async function updateUserRole(userId, newRole) {
    try {
      // Find the user
      const userToUpdate = allUsers.find(u => u.userId === userId);
      
      if (!userToUpdate) {
        showToast('User not found', 'error');
        return;
      }
      
      // Update in database
      const updateUserMutation = `
        mutation UpdateUser($input: UpdateUserInput!) {
          updateUser(input: $input) {
            id
            userId
            email
            name
            role
            avatar
          }
        }
      `;
      
      const result = await client.graphql({
        query: updateUserMutation,
        variables: {
          input: {
            id: userToUpdate.id,
            role: newRole
          }
        }
      });
      
      if (result.errors) {
        showToast('Failed to update role', 'error');
        return;
      }
      
      // Update local state
      setAllUsers(allUsers.map(u => 
        u.userId === userId ? {...u, role: newRole} : u
      ));
      
      showToast(`Updated ${userToUpdate.name} to ${newRole}`, 'success');
    } catch (error) {
      showToast('Failed to update role', 'error');
    }
  }
  
  async function loadCoaches() {
    if (!teamForCoachAssignment?.id) {
      console.warn('Cannot load coaches: no team selected');
      return;
    }
    
    try {
      // Fetch fresh team data from database
      const getTeamQuery = `
        query GetTeam($id: ID!) {
          getTeam(id: $id) {
            id
            name
            coachIds
          }
        }
      `;
      
      const teamResult = await client.graphql({
        query: getTeamQuery,
        variables: { id: teamForCoachAssignment.id }
      });
      
      const freshTeam = teamResult.data?.getTeam;
      console.log('loadCoaches: Fresh team data from DB:', freshTeam);
      
      const available = await getAvailableCoaches();
      const teamCoaches = await getTeamCoachesWithFreshData(freshTeam);
      
      setAvailableCoaches(available);
      setTeamCoachList(teamCoaches);
    } catch (error) {
      console.error('Error loading coaches:', error);
      // Don't throw - just log and continue
    }
  }
  
  async function getTeamCoachesWithFreshData(team) {
    try {
      if (!team || !team.coachIds || team.coachIds.length === 0) {
        console.log('getTeamCoachesWithFreshData: No coachIds', { team: team?.name, coachIds: team?.coachIds });
        return [];
      }
      
      console.log('getTeamCoachesWithFreshData: Looking for coaches with userIds:', team.coachIds);
      
      // Fetch all coaches using GraphQL
      const listCoachesQuery = `
        query ListCoaches {
          listCoaches {
            items {
              id
              userId
              name
              email
              role
              avatar
              teamIds
            }
          }
        }
      `;
      
      const result = await client.graphql({
        query: listCoachesQuery
      });
      
      const allCoaches = result.data?.listCoaches?.items || [];
      
      console.log('getTeamCoachesWithFreshData: All coaches:', allCoaches.map(c => ({ name: c.name, userId: c.userId, role: c.role })));
      
      // Filter to coaches assigned to this team
      const teamCoaches = allCoaches.filter(coach => 
        team.coachIds.includes(coach.userId)
      );
      
      console.log('getTeamCoachesWithFreshData: Matched coaches:', teamCoaches.map(c => c.name));
      
      return teamCoaches;
    } catch (error) {
      console.error('Error fetching team coaches:', error);
      return [];
    }
  }

  async function fetchClubData() {
    try {
      setLoading(true);

      const userEmail = user.signInDetails.loginId;
      let userRole = userEmail === "bryan.leinwand@gmail.com" ? ROLES.SUPER_ADMIN : ROLES.PARENT;
      let userPlayerIds = [];
      let userDbId = null;

      // Try to get/create user profile in database
      try {
        
        const listUsersQuery = `
          query ListUsers($filter: ModelUserFilterInput) {
            listUsers(filter: $filter) {
              items {
                id
                userId
                email
                name
                role
                avatar
                playerIds
              }
            }
          }
        `;
        
        // Check by userId first
        const usersResult = await client.graphql({
          query: listUsersQuery,
          variables: {
            filter: { userId: { eq: user.userId } }
          }
        });
        
        
        if (usersResult.errors && usersResult.errors.length > 0) {
          usersResult.errors.forEach((err, idx) => {
          });
        }
        
        let users = usersResult.data?.listUsers?.items || [];
        
        // Also check by email as backup
        if (users.length === 0) {
          const emailResult = await client.graphql({
            query: listUsersQuery,
            variables: {
              filter: { email: { eq: userEmail } }
            }
          });
          users = emailResult.data?.listUsers?.items || [];
        }
        
        if (users.length === 0) {
          
          // Check if this email matches any parent emails in players
          let linkedPlayerIds = [];
          try {
            
            const listPlayersQuery = `
              query ListPlayers {
                listPlayers {
                  items {
                    id
                    name
                    email
                    parent1Email
                    parent2Email
                  }
                }
              }
            `;
            
            const playersResult = await client.graphql({
              query: listPlayersQuery
            });
            
            const players = playersResult.data?.listPlayers?.items || [];
            
            // Normalize email for case-insensitive comparison
            const normalizedUserEmail = userEmail.toLowerCase();
            
            linkedPlayerIds = players
              .filter(p => {
                const playerMatch = p.email && p.email.toLowerCase() === normalizedUserEmail;
                const parent1Match = p.parent1Email && p.parent1Email.toLowerCase() === normalizedUserEmail;
                const parent2Match = p.parent2Email && p.parent2Email.toLowerCase() === normalizedUserEmail;
                const match = playerMatch || parent1Match || parent2Match;
                if (match) {
                }
                return match;
              })
              .map(p => p.id);
            
            // If matched as a player (not parent), set role to PLAYER
            const matchedAsPlayer = players.some(p => 
              p.email && p.email.toLowerCase() === normalizedUserEmail
            );
            if (matchedAsPlayer && linkedPlayerIds.length > 0) {
              userRole = ROLES.PLAYER;
            } else if (linkedPlayerIds.length > 0) {
              // Found kids - they're a parent (already default role)
            } else {
              // No kids found - stays as PARENT (can be updated later)
            }
          } catch (error) {
          }
          
          const createUserMutation = `
            mutation CreateUser($input: CreateUserInput!) {
              createUser(input: $input) {
                id
                userId
                email
                name
                role
                avatar
                playerIds
              }
            }
          `;
          
          const newUserResult = await client.graphql({
            query: createUserMutation,
            variables: {
              input: {
                userId: user.userId,
                email: userEmail,
                name: userEmail.split('@')[0],
                role: userRole,
                avatar: userEmail.substring(0, 2).toUpperCase(),
                playerIds: linkedPlayerIds
              }
            }
          });
          
          if (newUserResult.errors) {
          }
          
          userRole = newUserResult.data?.createUser?.role || userRole;
          userPlayerIds = linkedPlayerIds; // Use the playerIds we just created the user with
        } else {
          
          if (users.length > 1) {
          }
          
          userRole = users[0].role;
          userPlayerIds = users[0].playerIds || [];
          userDbId = users[0].id;
          
          
          // RE-SCAN for linked players on every login in case new players were added
          if (userRole === ROLES.PARENT || userRole === ROLES.PLAYER) {
            try {
              
              const listPlayersQuery = `
                query ListPlayers {
                  listPlayers {
                    items {
                      id
                      name
                      email
                      parent1Email
                      parent2Email
                    }
                  }
                }
              `;
              
              const playersResult = await client.graphql({
                query: listPlayersQuery
              });
              
              const players = playersResult.data?.listPlayers?.items || [];
              const normalizedUserEmail = userEmail.toLowerCase();
              const freshPlayerIds = players
                .filter(p => {
                  const playerMatch = p.email && p.email.toLowerCase() === normalizedUserEmail;
                  const parent1Match = p.parent1Email && p.parent1Email.toLowerCase() === normalizedUserEmail;
                  const parent2Match = p.parent2Email && p.parent2Email.toLowerCase() === normalizedUserEmail;
                  return playerMatch || parent1Match || parent2Match;
                })
                .map(p => p.id);
              
              // If matched as a player (not parent), ensure role is PLAYER
              const matchedAsPlayer = players.some(p =>
                p.email && p.email.toLowerCase() === normalizedUserEmail
              );
              if (matchedAsPlayer && freshPlayerIds.length > 0) {
                userRole = ROLES.PLAYER;
              }
              
              // If we found different/more players, update the User record
              const hasNewPlayers = freshPlayerIds.length !== userPlayerIds.length || 
                                   freshPlayerIds.some(id => !userPlayerIds.includes(id));
              
              if (hasNewPlayers && freshPlayerIds.length > 0) {
                
                const updateUserMutation = `
                  mutation UpdateUser($input: UpdateUserInput!) {
                    updateUser(input: $input) {
                      id
                      playerIds
                    }
                  }
                `;
                
                await client.graphql({
                  query: updateUserMutation,
                  variables: {
                    input: {
                      id: userDbId,
                      playerIds: freshPlayerIds
                    }
                  }
                });
                
                userPlayerIds = freshPlayerIds;
              } else {
              }
            } catch (rescanError) {
              // Continue with stored playerIds if re-scan fails
            }
          }
          
          // OVERRIDE: Always use super_admin for bryan.leinwand@gmail.com
          if (userEmail === "bryan.leinwand@gmail.com") {
            userRole = ROLES.SUPER_ADMIN;
            
            // Update database to match
            if (users[0].role !== ROLES.SUPER_ADMIN) {
              try {
                await client.graphql({
                  query: `
                    mutation UpdateUser($input: UpdateUserInput!) {
                      updateUser(input: $input) {
                        id
                        role
                      }
                    }
                  `,
                  variables: {
                    input: {
                      id: users[0].id,
                      role: ROLES.SUPER_ADMIN
                    }
                  }
                });
              } catch (updateError) {
              }
            }
          }
        }
      } catch (userError) {
        if (userError.errors) {
        }
      }
      
      // Update currentUser with the actual role and playerIds from database
      
      setCurrentUser(prev => ({
        ...prev,
        userId: user.userId,  // ← ADD THIS
        role: userRole,
        playerIds: userPlayerIds
      }));
      
      // Fetch club data
      const listClubsQuery = `
        query ListClubs($filter: ModelClubFilterInput) {
          listClubs(filter: $filter) {
            items {
              id
              name
              ownerId
              teams {
                items {
                  id
                  name
                  sport
                  season
                  ageGroup
                  color
                  logo
                  coachIds
                  financialSheetUrl
                  rosterSize
                  roster(limit: 1000) {
                    items {
                      id
                      name
                      number
                      position
                      status
                      avatar
                      email
                      phone
                      joined
                      headshot
                      parent1Name
                      parent1Phone
                      parent1Email
                      parent2Name
                      parent2Phone
                      parent2Email
                    }
                  }
                  events(limit: 1000) {
                    items {
                      id
                      type
                      title
                      date
                      time
                      endTime
                      arrivalTime
                      location
                      opponent
                      homeAway
                      notes
                      result
                      attendance
                      volunteerSlotsTotal
                      volunteerSignups
                      volunteerTeamIds
                    }
                    nextToken
                  }
                  messages {
                    items {
                      id
                      author
                      avatar
                      text
                      time
                      pinned
                    }
                  }
                }
              }
            }
          }
        }
      `;
      
      // Super admin sees ALL clubs
      // Parents/Players need to see clubs where they have roster access
      const isSuperAdmin = userRole === ROLES.SUPER_ADMIN;
      
      let clubsResult;
      
      if (isSuperAdmin) {
        // Super admin - get all clubs
        clubsResult = await client.graphql({
          query: listClubsQuery,
          variables: {}
        });
      } else if (userRole === ROLES.CLUB_OWNER || userRole === "owner") {
        // Club owners - filter by ownerId
        clubsResult = await client.graphql({
          query: listClubsQuery,
          variables: {
            filter: { ownerId: { eq: user.userId } }
          }
        });
      } else {
        // Parents/Players/Coaches - need to get ALL clubs then filter by access
        // This is because they don't own clubs, they have access through roster
        clubsResult = await client.graphql({
          query: listClubsQuery,
          variables: {}
        });
      }
      
      if (clubsResult.errors) {
        clubsResult.errors.forEach((err, idx) => {
        });
      }
      
      let clubs = clubsResult.data?.listClubs?.items || [];
      
      // For parents/players, filter clubs to only show ones where they have access
      if (!isSuperAdmin && userRole !== ROLES.CLUB_OWNER && userRole !== "owner") {
        const userEmail = user.signInDetails.loginId;
        
        const normalizedUserEmail = userEmail.toLowerCase();
        
        clubs = clubs.filter(club => {
          // Check if any team in this club has them as a player or parent
          return club.teams?.items?.some(team => {
            return team.roster?.items?.some(player => {
              return (
                (player.email && player.email.toLowerCase() === normalizedUserEmail) ||
                (player.parent1Email && player.parent1Email.toLowerCase() === normalizedUserEmail) ||
                (player.parent2Email && player.parent2Email.toLowerCase() === normalizedUserEmail)
              );
            }) || team.coachIds?.includes(user.userId);
          });
        });
        
      }

      // Store all clubs for everyone (not just super_admin)
      setAllClubs(clubs);
      
      if (isSuperAdmin) {
        // If no club selected yet, select the first one
        if (clubs.length > 0 && !selectedClubId) {
          setSelectedClubId(clubs[0].id);
          setClub(clubs[0]);
          // Don't auto-select team - let accessibleTeams logic handle it later
        } else if (selectedClubId) {
          // Load the selected club
          const selected = clubs.find(c => c.id === selectedClubId);
          if (selected) {
            setClub(selected);
            // Don't auto-select team - let accessibleTeams logic handle it later
          } else if (clubs.length > 0) {
            // Fallback to first club
            setSelectedClubId(clubs[0].id);
            setClub(clubs[0]);
          }
        }
      } else {
        // Regular users (club_owner, coach, parent, etc)
        if (clubs.length > 0) {
          // If no club selected yet, select the first one
          if (!selectedClubId) {
            setSelectedClubId(clubs[0].id);
            setClub(clubs[0]);
          } else {
            // Load the selected club
            const selected = clubs.find(c => c.id === selectedClubId);
            if (selected) {
              setClub(selected);
            } else {
              // Fallback to first club
              setSelectedClubId(clubs[0].id);
              setClub(clubs[0]);
            }
          }
          // Don't auto-select team - let accessibleTeams logic handle it later
        } else if (userRole === ROLES.CLUB_OWNER || userRole === "owner") {
          await createInitialClub();
        } else {
          setClub(null);
        }
      }

      setLoading(false);
    } catch (error) {
      
      // More user-friendly error message
      let errorMsg = 'Unable to load data. ';
      if (error.message.includes('Network')) {
        errorMsg += 'Please check your internet connection.';
      } else if (error.message.includes('GraphQL')) {
        errorMsg += 'There was a problem connecting to the server.';
      } else {
        errorMsg += 'Please try refreshing the page.';
      }
      
      setLoading(false);
      showToast(errorMsg, 'error');
      
      // Still log to console for debugging
    }
  }

  async function createInitialClub() {
    try {
      
      const userEmail = user.signInDetails.loginId;
      const clubName = `${userEmail.split('@')[0]}'s Club`;
      
      const createClubMutation = `
        mutation CreateClub($input: CreateClubInput!) {
          createClub(input: $input) {
            id
            name
            ownerId
          }
        }
      `;
      
      const newClubResult = await client.graphql({
        query: createClubMutation,
        variables: {
          input: {
            name: clubName,
            ownerId: user.userId
          }
        }
      });
      
      const newClub = newClubResult.data.createClub;
      
      setClub({
        ...newClub,
        teams: [] // Start with no teams
      });
      
    } catch (error) {
      alert('Failed to create your club. Error: ' + (error.message || error));
    }
  }

  async function uploadHeadshot(file) {
    try {
      if (!file) {
        console.error('No file provided');
        return null;
      }

      console.log('Starting upload for file:', file.name, 'Type:', file.type, 'Size:', file.size);

      // Create unique filename
      const filename = `public/headshots/${Date.now()}-${file.name.replace(/[^a-zA-Z0-9.]/g, '_')}`;
      console.log('Upload path:', filename);
           
      // Upload to S3 using Amplify v6 API
      console.log('Calling uploadData...');
      const result = await uploadData({
        path: filename,
        data: file
      }).result;
      
      console.log('Upload successful, result:', result);
      
      // Return just the path, not the URL
      // URLs will be generated on-demand when displaying
      return filename;
    } catch (error) {
      console.error('Photo upload error:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
        name: error.name
      });
      alert(`Failed to upload photo: ${error.message}`);
      return null;
    }
  }

  // Helper function to get URL from S3 path
  async function getHeadshotUrl(path) {
    if (!path) return null;
    
    // If it's already a full URL (legacy data), return as-is
    if (path.startsWith('http')) return path;
    
    try {
      const result = await getUrl({ path });
      return result.url.toString();
    } catch (error) {
      console.error('Error getting headshot URL:', error);
      return null;
    }
  }

  async function createCoachProfile(userId, name, email, role) {
    try {
      const createCoachMutation = `
        mutation CreateCoach($input: CreateCoachInput!) {
          createCoach(input: $input) {
            id
            userId
            name
            email
            role
            avatar
            teamIds
          }
        }
      `;
      
      const result = await client.graphql({
        query: createCoachMutation,
        variables: {
          input: {
            userId: userId,
            name: name,
            email: email,
            role: role,
            avatar: name.split(" ").map(n=>n[0]).join("").toUpperCase(),
            teamIds: []
          }
        }
      });
      
      return result.data?.createCoach;
    } catch (error) {
      console.error('Error creating coach profile:', error);
      throw error; // Re-throw so caller knows it failed
    }
  }

  // Permissions based on role - DEFINE THESE FIRST
  const isSuperAdmin = currentUser.role === ROLES.SUPER_ADMIN;
  const isClubOwner = currentUser.role === ROLES.CLUB_OWNER;
  const isTeamManager = currentUser.role === ROLES.TEAM_MANAGER;
  const isCoach = currentUser.role === ROLES.COACH;
  const isAnyCoach = isTeamManager || isCoach;  // Either team manager or coach
  const isParent = currentUser.role === ROLES.PARENT;
  const isPlayer = currentUser.role === ROLES.PLAYER;
  
  // Backwards compatibility
  const isOwner = isClubOwner || isSuperAdmin;

  // Determine which teams user can access based on role
  const accessibleTeams = (() => {
    // If club or teams don't exist, return empty array
    if (!club || !club.teams || !club.teams.items || club.teams.items.length === 0) {
      return [];
    }
    
    console.log('Determining accessible teams for user:', {
      userId: currentUser.userId,
      role: currentUser.role,
      playerIds: currentUser.playerIds,
      allTeams: club.teams.items.map(t => ({ id: t.id, name: t.name, coachIds: t.coachIds }))
    });
    
    if (isSuperAdmin || isClubOwner) {
      return club.teams.items; // Super admin and club owner see all teams
    }
    if (isAnyCoach) {
      // Coaches see teams they're assigned to (check if team's coachIds includes this user)
      const coachedTeams = club.teams.items.filter(t => 
        t.coachIds && t.coachIds.includes(currentUser.userId)
      );
      
      console.log('Coach team access:', {
        userId: currentUser.userId,
        role: currentUser.role,
        coachedTeams: coachedTeams.map(t => ({ id: t.id, name: t.name, coachIds: t.coachIds }))
      });
      
      // ALSO see teams where their kids play (if they have kids)
      const parentTeams = [];
      if (currentUser.playerIds && currentUser.playerIds.length > 0) {
        club.teams.items.forEach(team => {
          const teamPlayerIds = team.roster?.items?.map(p => p.id) || [];
          const hasKidOnTeam = currentUser.playerIds.some(playerId => teamPlayerIds.includes(playerId));
          if (hasKidOnTeam && !coachedTeams.find(t => t.id === team.id)) {
            parentTeams.push(team);
          }
        });
      }
      
      console.log('Parent teams:', parentTeams.map(t => t.name));
      console.log('Total accessible teams:', [...coachedTeams, ...parentTeams].map(t => t.name));
      
      return [...coachedTeams, ...parentTeams];
    }
    if (isParent) {
      
      // Parents see teams where their kids play
      if (!currentUser.playerIds || currentUser.playerIds.length === 0) {
        return []; // No kids linked yet
      }
      
      
      // Find teams that have players with IDs in playerIds
      const accessibleTeams = club.teams.items.filter(team => {
        const teamPlayerIds = team.roster?.items?.map(p => p.id) || [];
        const hasAccess = currentUser.playerIds.some(playerId => teamPlayerIds.includes(playerId));
        
        return hasAccess;
      });
      
      return accessibleTeams;
    }
    if (isPlayer) {
      // Find teams where the player's ID appears in the roster
      const teams = club.teams.items.filter(team => {
        const rosterIds = team.roster?.items?.map(p => p.id) || [];
        return currentUser.playerIds?.some(pid => rosterIds.includes(pid));
      });
      return teams;
    }
    
    return [];
  })();
  
  // Can switch teams if user has access to multiple teams
  const canSwitchTeams = accessibleTeams.length > 1;
  

  // Auto-select team if none selected
  if (!selectedTeamId && accessibleTeams.length > 0) {
    // First try user's saved default team
    if (currentUser?.defaultTeamId && accessibleTeams.find(t => t.id === currentUser.defaultTeamId)) {
      setSelectedTeamId(currentUser.defaultTeamId);
    } else {
      // Fall back to first accessible team
      setSelectedTeamId(accessibleTeams[0].id);
    }
  }
  
  // Get current team data - ONLY from accessible teams
  const currentTeam = accessibleTeams.find(t => t.id === selectedTeamId) || accessibleTeams[0];
  
  // Permissions - team-specific for coaches (must be after currentTeam is defined)
  const canManageClub = isSuperAdmin || isClubOwner;
  const canManageTeams = isSuperAdmin || isClubOwner;
  
  // Team managers can only manage players/events on THEIR teams
  const isCoachOfCurrentTeam = currentTeam && currentTeam.coachIds && currentTeam.coachIds.includes(currentUser.userId);
  
  const canManagePlayers = isSuperAdmin || isClubOwner || (isTeamManager && isCoachOfCurrentTeam);
  const canManageEvents = isSuperAdmin || isClubOwner || (isAnyCoach && isCoachOfCurrentTeam);
  
  console.log('Permissions for current team:', {
    team: currentTeam?.name,
    userRole: currentUser.role,
    isCoachOfCurrentTeam,
    canManagePlayers,
    canManageEvents
  });
  
  
  // If no current team, show empty state for owners or "no access" for others
  if (!currentTeam) {
    if (!canManageClub) {
      return <div>No team access</div>;
    }
    // Owner with no teams - allow them to continue (they'll see "Create First Team" button)
  }
  
  const roster = currentTeam?.roster?.items || currentTeam?.roster || [];
  const events = currentTeam?.events?.items || currentTeam?.events || [];

  // Volunteer events: events from OTHER teams that have currentTeam in their volunteerTeamIds
  const volunteerEvents = (club?.teams?.items || [])
    .filter(t => t.id !== currentTeam?.id)
    .flatMap(t => (t.events?.items || t.events || [])
      .filter(e => 
        e.volunteerSlotsTotal > 0 && 
        e.volunteerTeamIds?.includes(currentTeam?.id) &&
        e.date >= new Date().toISOString().split('T')[0]
      )
      .map(e => ({ ...e, teamName: t.name }))
    )
    .sort((a, b) => a.date.localeCompare(b.date));
  const messages = currentTeam?.messages?.items || currentTeam?.messages || [];
  
  // Helper to safely parse attendance (it's stored as JSON string in DB)
  function getAttendance(event) {
    if (!event.attendance) return {};
    
    let parsed = {};
    
    // If it's already an object, use it
    if (typeof event.attendance === 'object' && !Array.isArray(event.attendance)) {
      parsed = event.attendance;
    } else if (typeof event.attendance === 'string') {
      // Try to parse JSON string
      try {
        parsed = JSON.parse(event.attendance);
      } catch (e) {
        return {};
      }
    } else {
      return {};
    }
    
    // Filter out any non-player keys (metadata, __typename, etc.)
    const cleanAttendance = {};
    for (const key in parsed) {
      // Only include keys that look like player IDs (not __typename, etc.)
      if (!key.startsWith('__') && key !== 'id' && key !== 'createdAt' && key !== 'updatedAt') {
        cleanAttendance[key] = parsed[key];
      }
    }
    
    return cleanAttendance;
  }
  
  // Update team data helper
  const updateTeam = (updates) => {
    setClub({
      ...club,
      teams: {
        items: club.teams.items.map(t => t.id === selectedTeamId ? { ...t, ...updates } : t)
      }
    });
  };

  // Get today's date at midnight (local timezone) for proper comparison
  const today = new Date();
  today.setHours(0, 0, 0, 0);  // Set to midnight local time
  

  const upcomingEvents = events
    .filter(e => {
      // Parse date in LOCAL timezone, not UTC
      // If date is "2026-03-29", we want March 29 LOCAL, not March 29 UTC
      const [year, month, day] = e.date.split('-').map(Number);
      const eventDate = new Date(year, month - 1, day);  // month is 0-indexed
      
      const isUpcoming = eventDate >= today;
      
      if (!isUpcoming) {
      }
      
      return isUpcoming;
    })
    .sort((a,b) => {
      const [yearA, monthA, dayA] = a.date.split('-').map(Number);
      const [yearB, monthB, dayB] = b.date.split('-').map(Number);
      return new Date(yearA, monthA - 1, dayA) - new Date(yearB, monthB - 1, dayB);
    });

  const pastGames = events.filter(e => e.type === "game" && e.result);
  const wins = pastGames.filter(e => e.result?.won).length;
  const losses = pastGames.filter(e => !e.result?.won).length;
  const draws = 0;
  const activeCount = roster.filter(p => p.status === "active").length;
  const winRate = pastGames.length ? Math.round((wins / pastGames.length) * 100) : 0;

  const filteredRoster = roster
    .filter(p =>
      p.name.toLowerCase().includes(search.toLowerCase()) ||
      p.position.toLowerCase().includes(search.toLowerCase())
    )
    .sort((a, b) => a.name.localeCompare(b.name)); // Sort alphabetically by name

  const filteredEvents = events
    .filter(e => {
      // Parse date in LOCAL timezone
      const [year, month, day] = e.date.split('-').map(Number);
      const eventDate = new Date(year, month - 1, day);
      
      if (scheduleFilter === "all") return eventDate >= today;
      if (scheduleFilter === "game") return e.type === "game" && eventDate >= today;
      if (scheduleFilter === "practice") return e.type === "practice" && eventDate >= today;
      if (scheduleFilter === "past") return eventDate < today;
      return true;
    })
    .sort((a,b) => {
      const [yearA, monthA, dayA] = a.date.split('-').map(Number);
      const [yearB, monthB, dayB] = b.date.split('-').map(Number);
      return new Date(yearA, monthA - 1, dayA) - new Date(yearB, monthB - 1, dayB);
    });

  // Phase 1: Toast Notification System
  function showToast(message, type = 'info') {
    const id = Date.now();
    const toast = { id, message, type };
    setToasts(prev => [...prev, toast]);
    
    // Auto-dismiss after 3 seconds
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 3000);
  }

  // Phase 1: Pull-to-Refresh Handler
  function handleTouchStart(e) {
    if (window.scrollY === 0) {
      setPullStartY(e.touches[0].clientY);
    }
  }

  function handleTouchMove(e) {
    if (pullStartY > 0) {
      const currentY = e.touches[0].clientY;
      const pullDistance = currentY - pullStartY;
      
      if (pullDistance > 0 && pullDistance < 100) {
        setIsPulling(true);
      } else if (pullDistance >= 100) {
        // Trigger refresh
        fetchClubData();
        showToast('Refreshing...', 'info');
        setIsPulling(false);
        setPullStartY(0);
      }
    }
  }

  function handleTouchEnd() {
    setIsPulling(false);
    setPullStartY(0);
  }

  async function linkParentsToPlayer(player) {
    // Link parent accounts to this player
    const parentEmails = [];
    if (player.parent1Email) parentEmails.push(player.parent1Email);
    if (player.parent2Email) parentEmails.push(player.parent2Email);
    
    for (const email of parentEmails) {
      try {
        // Find user with this email
        const listUsersQuery = `
          query ListUsers($filter: ModelUserFilterInput) {
            listUsers(filter: $filter) {
              items {
                id
                userId
                email
                role
                playerIds
              }
            }
          }
        `;
        
        const usersResult = await client.graphql({
          query: listUsersQuery,
          variables: {
            filter: { email: { eq: email } }
          }
        });
        
        const users = usersResult.data?.listUsers?.items || [];
        
        if (users.length > 0) {
          const parentUser = users[0];
          const currentPlayerIds = parentUser.playerIds || [];
          
          // Add this player to parent's playerIds if not already there
          if (!currentPlayerIds.includes(player.id)) {
            const updateUserMutation = `
              mutation UpdateUser($input: UpdateUserInput!) {
                updateUser(input: $input) {
                  id
                  playerIds
                }
              }
            `;
            
            await client.graphql({
              query: updateUserMutation,
              variables: {
                input: {
                  id: parentUser.id,
                  playerIds: [...currentPlayerIds, player.id]
                }
              }
            });
            
          }
        }
      } catch (error) {
        // Don't fail the whole operation if linking fails
      }
    }
  }

  async function addPlayer() {
    if (!newPlayer.name) {
      showToast('Please enter player name', 'error');
      return;
    }
    
    if (!currentTeam || !currentTeam.id) {
      showToast('No team selected', 'error');
      return;
    }
    
    try {
      // Create player in database
      const createPlayerMutation = `
        mutation CreatePlayer($input: CreatePlayerInput!) {
          createPlayer(input: $input) {
            id
            name
            number
            position
            status
            avatar
            email
            phone
            joined
            headshot
            parent1Name
            parent1Phone
            parent1Email
            parent2Name
            parent2Phone
            parent2Email
            teamID
          }
        }
      `;
      
      const playerResult = await client.graphql({
        query: createPlayerMutation,
        variables: {
          input: {
            name: newPlayer.name,
            number: newPlayer.number !== '' ? parseInt(newPlayer.number, 10) : 99,
            position: newPlayer.position || "Attack",
            status: "active",
            avatar: newPlayer.name.split(" ").map(n=>n[0]).join("").slice(0,2).toUpperCase(),
            email: newPlayer.email || "",
            phone: newPlayer.phone || "",
            joined: new Date().toISOString().split("T")[0],
            headshot: newPlayer.headshot || "",
            parent1Name: newPlayer.parent1Name || "",
            parent1Phone: newPlayer.parent1Phone || "",
            parent1Email: newPlayer.parent1Email || "",
            parent2Name: newPlayer.parent2Name || "",
            parent2Phone: newPlayer.parent2Phone || "",
            parent2Email: newPlayer.parent2Email || "",
            teamID: currentTeam.id
          }
        }
      });
      
      if (playerResult.errors) {
        showToast('Failed to create player', 'error');
        return;
      }
      
      const createdPlayer = playerResult.data.createPlayer;
      
      // Update team rosterSize
      try {
        const updateTeamMutation = `
          mutation UpdateTeam($input: UpdateTeamInput!) {
            updateTeam(input: $input) {
              id
              rosterSize
            }
          }
        `;
        
        await client.graphql({
          query: updateTeamMutation,
          variables: {
            input: {
              id: currentTeam.id,
              rosterSize: roster.length + 1
            }
          }
        });
      } catch (err) {
        // Non-critical error, continue
      }
      
      showToast(`${newPlayer.name} added successfully!`, 'success');
      
      // Reload club data to get updated roster
      await fetchClubData();
      
      // Check if parent emails exist and link them
      await linkParentsToPlayer(createdPlayer);
      
      setNewPlayer({ 
        name: "", number: "", position: "Attack", email: "", phone: "",
        headshot: null, parent1Name: "", parent1Phone: "", parent1Email: "",
        parent2Name: "", parent2Phone: "", parent2Email: ""
      });
      setShowAddPlayer(false);
      
    } catch (error) {
      showToast('Failed to create player', 'error');
    }
  }

  async function deletePlayer(playerId) {
    if (!window.confirm('Are you sure you want to remove this player?')) {
      return;
    }
    
    try {
      // Delete player from database
      const deletePlayerMutation = `
        mutation DeletePlayer($input: DeletePlayerInput!) {
          deletePlayer(input: $input) {
            id
          }
        }
      `;
      
      const playerResult = await client.graphql({
        query: deletePlayerMutation,
        variables: {
          input: { id: playerId }
        }
      });
      
      if (playerResult.errors) {
        showToast('Failed to delete player', 'error');
        return;
      }
      
      
      // Update team rosterSize
      try {
        const updateTeamMutation = `
          mutation UpdateTeam($input: UpdateTeamInput!) {
            updateTeam(input: $input) {
              id
              rosterSize
            }
          }
        `;
        
        await client.graphql({
          query: updateTeamMutation,
          variables: {
            input: {
              id: currentTeam.id,
              rosterSize: Math.max(0, roster.length - 1)
            }
          }
        });
      } catch (err) {
        // Non-critical error, continue
      }
      
      showToast('Player removed successfully', 'success');
      
      // Reload club data to get updated roster
      await fetchClubData();
      
    } catch (error) {
      showToast('Failed to delete player', 'error');
    }
  }

  async function editPlayer() {
    if (!editingPlayer.name) {
      showToast('Please enter player name', 'error');
      return;
    }
    
    try {
      // Update player in database
      const updatePlayerMutation = `
        mutation UpdatePlayer($input: UpdatePlayerInput!) {
          updatePlayer(input: $input) {
            id
            name
            number
            position
            status
            avatar
            email
            phone
            headshot
            parent1Name
            parent1Phone
            parent1Email
            parent2Name
            parent2Phone
            parent2Email
          }
        }
      `;
      
      // Prepare fields to update
      const fieldsToUpdate = {
        id: editingPlayer.id,
        name: editingPlayer.name,
        number: editingPlayer.number !== '' ? parseInt(editingPlayer.number, 10) : 99,
        position: editingPlayer.position,
        avatar: editingPlayer.name.split(" ").map(n=>n[0]).join("").slice(0,2).toUpperCase(),
        email: editingPlayer.email || "",
        phone: editingPlayer.phone || "",
        headshot: editingPlayer.headshot || ""
      };
      
      // Track if parent info changed (for syncing)
      let parent1InfoChanged = false;
      let parent2InfoChanged = false;
      const originalPlayer = roster.find(p => p.id === editingPlayer.id);
      
      // Parents can edit their own contact info (but not email)
      if (isParent) {
        const userEmail = currentUser.email;
        const normalizedUserEmail = userEmail.toLowerCase();
        
        // If this parent is parent1, they can update parent1 info (except email)
        if (originalPlayer.parent1Email && originalPlayer.parent1Email.toLowerCase() === normalizedUserEmail) {
          fieldsToUpdate.parent1Name = editingPlayer.parent1Name || "";
          fieldsToUpdate.parent1Phone = editingPlayer.parent1Phone || "";
          fieldsToUpdate.parent1Email = originalPlayer.parent1Email; // Keep original email
          
          // Check if name or phone changed
          parent1InfoChanged = 
            originalPlayer.parent1Name !== fieldsToUpdate.parent1Name ||
            originalPlayer.parent1Phone !== fieldsToUpdate.parent1Phone;
        }
        
        // If this parent is parent2, they can update parent2 info (except email)
        if (originalPlayer.parent2Email && originalPlayer.parent2Email.toLowerCase() === normalizedUserEmail) {
          fieldsToUpdate.parent2Name = editingPlayer.parent2Name || "";
          fieldsToUpdate.parent2Phone = editingPlayer.parent2Phone || "";
          fieldsToUpdate.parent2Email = originalPlayer.parent2Email; // Keep original email
          
          // Check if name or phone changed
          parent2InfoChanged = 
            originalPlayer.parent2Name !== fieldsToUpdate.parent2Name ||
            originalPlayer.parent2Phone !== fieldsToUpdate.parent2Phone;
        }
      }
      
      // Coaches/admins can update all parent info
      if (canManagePlayers) {
        // Check what changed (name or phone only, email changes don't trigger sync)
        parent1InfoChanged = 
          originalPlayer.parent1Name !== editingPlayer.parent1Name ||
          originalPlayer.parent1Phone !== editingPlayer.parent1Phone;
          
        parent2InfoChanged = 
          originalPlayer.parent2Name !== editingPlayer.parent2Name ||
          originalPlayer.parent2Phone !== editingPlayer.parent2Phone;
        
        fieldsToUpdate.parent1Name = editingPlayer.parent1Name || "";
        fieldsToUpdate.parent1Phone = editingPlayer.parent1Phone || "";
        fieldsToUpdate.parent1Email = editingPlayer.parent1Email || "";
        fieldsToUpdate.parent2Name = editingPlayer.parent2Name || "";
        fieldsToUpdate.parent2Phone = editingPlayer.parent2Phone || "";
        fieldsToUpdate.parent2Email = editingPlayer.parent2Email || "";
      }
      
      const playerResult = await client.graphql({
        query: updatePlayerMutation,
        variables: {
          input: fieldsToUpdate
        }
      });
      
      if (playerResult.errors) {
        showToast('Failed to update player', 'error');
        return;
      }
      
      const updatedPlayer = playerResult.data.updatePlayer;
      
      // Sync parent info across all players if it changed
      // Use ORIGINAL email to find other players (email is the unique identifier)
      if (parent1InfoChanged || parent2InfoChanged) {
        await syncParentInfoAcrossPlayers(
          parent1InfoChanged ? originalPlayer.parent1Email : null,
          parent1InfoChanged ? { name: fieldsToUpdate.parent1Name, phone: fieldsToUpdate.parent1Phone, email: fieldsToUpdate.parent1Email } : null,
          parent2InfoChanged ? originalPlayer.parent2Email : null,
          parent2InfoChanged ? { name: fieldsToUpdate.parent2Name, phone: fieldsToUpdate.parent2Phone, email: fieldsToUpdate.parent2Email } : null
        );
      }
      
      showToast('Player updated successfully', 'success');
      
      // Reload club data to get updated roster
      await fetchClubData();
      
      setShowEditPlayer(false);
      setEditingPlayer(null);
      
    } catch (error) {
      alert('Failed to update player. Error: ' + error.message);
    }
  }
  
  async function syncParentInfoAcrossPlayers(parent1Email, parent1Info, parent2Email, parent2Info) {
    try {
      
      // Fetch ALL players in the system
      const listPlayersQuery = `
        query ListPlayers {
          listPlayers {
            items {
              id
              name
              parent1Email
              parent2Email
              parent1Name
              parent1Phone
              parent2Name
              parent2Phone
            }
          }
        }
      `;
      
      const playersResult = await client.graphql({
        query: listPlayersQuery
      });
      
      const allPlayers = playersResult.data?.listPlayers?.items || [];
      
      // Log all players with their parent emails
      allPlayers.forEach(p => {
      });
      
      const updatePlayerMutation = `
        mutation UpdatePlayer($input: UpdatePlayerInput!) {
          updatePlayer(input: $input) {
            id
            name
            parent1Name
            parent1Phone
            parent2Name
            parent2Phone
          }
        }
      `;
      
      let updatedCount = 0;
      
      // Update all matching players
      for (const player of allPlayers) {
        let needsUpdate = false;
        const updates = { id: player.id };
        
        
        // Check if parent1 info needs syncing
        if (parent1Email && player.parent1Email === parent1Email) {
          
          if (player.parent1Name !== parent1Info.name || player.parent1Phone !== parent1Info.phone) {
            updates.parent1Name = parent1Info.name;
            updates.parent1Phone = parent1Info.phone;
            updates.parent1Email = parent1Info.email;
            needsUpdate = true;
          } else {
          }
        }
        
        // Check if parent2 info needs syncing
        if (parent2Email && player.parent2Email === parent2Email) {
          
          if (player.parent2Name !== parent2Info.name || player.parent2Phone !== parent2Info.phone) {
            updates.parent2Name = parent2Info.name;
            updates.parent2Phone = parent2Info.phone;
            updates.parent2Email = parent2Info.email;
            needsUpdate = true;
          } else {
          }
        }
        
        if (needsUpdate) {
          
          const result = await client.graphql({
            query: updatePlayerMutation,
            variables: { input: updates }
          });
          
          updatedCount++;
        }
      }
      
      
      if (updatedCount > 0) {
        showToast(`Updated contact info across ${updatedCount} player${updatedCount > 1 ? 's' : ''}`, 'info');
      } else {
      }
      
    } catch (error) {
      // Don't fail the whole operation if sync fails
    }
  }

  function openEditPlayer(player) {
    // Check if parent can edit this player
    if (isParent) {
      const canEdit = currentUser.playerIds?.includes(player.id);
      if (!canEdit) {
        alert('You can only edit your own children');
        return;
      }
    }
    
    setEditingPlayer({ ...player });
    setShowEditPlayer(true);
  }
  
  async function deleteEvent(eventId) {
    if (!window.confirm('Are you sure you want to delete this event? This cannot be undone.')) {
      return;
    }
    
    try {
      const deleteEventMutation = `
        mutation DeleteEvent($input: DeleteEventInput!) {
          deleteEvent(input: $input) {
            id
          }
        }
      `;
      
      const result = await client.graphql({
        query: deleteEventMutation,
        variables: {
          input: { id: eventId }
        }
      });
      
      if (result.errors) {
        showToast('Failed to delete event', 'error');
        return;
      }
      
      showToast('Event deleted successfully', 'success');
      
      // Reload club data
      await fetchClubData();
      
    } catch (error) {
      showToast('Failed to delete event', 'error');
    }
  }
  
  async function setDefaultTeam(teamId) {
    try {
      
      // First, find the actual User record in DynamoDB by userId (Cognito ID)
      const getUserQuery = `
        query GetUserByUserId($userId: String!) {
          listUsers(filter: {userId: {eq: $userId}}) {
            items {
              id
              userId
              email
              name
              role
              defaultTeamId
            }
          }
        }
      `;
      
      const userResult = await client.graphql({
        query: getUserQuery,
        variables: {
          userId: currentUser.id  // This is the Cognito userId
        }
      });
      
      const users = userResult.data?.listUsers?.items || [];
      
      let userRecord;
      
      if (users.length === 0) {
        
        // Create User record if it doesn't exist
        const createUserMutation = `
          mutation CreateUser($input: CreateUserInput!) {
            createUser(input: $input) {
              id
              userId
              email
              name
              role
              defaultTeamId
            }
          }
        `;
        
        const createResult = await client.graphql({
          query: createUserMutation,
          variables: {
            input: {
              userId: currentUser.id,
              email: currentUser.email || user?.signInDetails?.loginId || 'unknown',
              name: currentUser.name || currentUser.email?.split('@')[0] || 'User',
              role: currentUser.role || 'parent',
              defaultTeamId: teamId
            }
          }
        });
        
        if (createResult.errors) {
          showToast('Failed to create user record', 'error');
          haptic.error();
          return;
        }
        
        userRecord = createResult.data.createUser;
        
        // Update local state
        setCurrentUser({
          ...currentUser,
          defaultTeamId: teamId
        });
        
        const teamName = accessibleTeams.find(t => t.id === teamId)?.name;
        showToast(`"${teamName}" set as default team`, 'success');
        haptic.success();
        return;
      }
      
      userRecord = users[0];
      
      // Now update the User record with the correct DynamoDB ID
      const updateUserMutation = `
        mutation UpdateUser($input: UpdateUserInput!) {
          updateUser(input: $input) {
            id
            userId
            email
            name
            role
            defaultTeamId
          }
        }
      `;
      
      const result = await client.graphql({
        query: updateUserMutation,
        variables: {
          input: {
            id: userRecord.id,  // Use the DynamoDB record ID, not Cognito userId
            defaultTeamId: teamId
          }
        }
      });
      
      
      if (result.errors && result.errors.length > 0) {
        
        // Show detailed error to user
        const errorMsg = result.errors[0].message || 'Unknown error';
        const errorType = result.errors[0].errorType || '';
        
        if (errorMsg.includes('not defined') || errorMsg.includes('defaultTeamId')) {
          showToast('Schema field missing - Add defaultTeamId to User schema and run amplify push', 'error');
        } else if (errorType.includes('ConditionalCheckFailed')) {
          showToast('Update conflict - trying to recreate user record...', 'error');
          // The record might have version issues, let's log more details
        } else {
          showToast(`Error: ${errorMsg}`, 'error');
        }
        
        haptic.error();
        return;
      }
      
      
      // Update local currentUser state
      setCurrentUser({
        ...currentUser,
        defaultTeamId: teamId
      });
      
      const teamName = accessibleTeams.find(t => t.id === teamId)?.name;
      showToast(`"${teamName}" set as default team`, 'success');
      haptic.success();
      
    } catch (error) {
      showToast('Failed to set default team - check console', 'error');
      haptic.error();
    }
  }

  async function addEvent() {
    if (!newEvent.date) {
      alert('Please fill in Date');
      return;
    }
    
    // Auto-generate title if empty
    const eventTitle = newEvent.title || (newEvent.type === "game" ? "Game" : "Practice");
    
    if (!currentTeam || !currentTeam.id) {
      alert('No team selected. Please select a team first.');
      return;
    }
    
    try {
      
      // Create event in database
      const createEventMutation = `
        mutation CreateEvent($input: CreateEventInput!) {
          createEvent(input: $input) {
            id
            type
            title
            date
            time
            endTime
            arrivalTime
            location
            opponent
            homeAway
            notes
            result
            attendance
            volunteerSlotsTotal
            volunteerSignups
            volunteerTeamIds
            teamID
          }
        }
      `;
      
      const eventInput = {
        type: newEvent.type,
        title: eventTitle,
        date: newEvent.date,
        time: newEvent.time || "",
        endTime: newEvent.endTime || "",
        arrivalTime: newEvent.arrivalTime || "",
        location: newEvent.location || "",
        opponent: newEvent.opponent || "",
        homeAway: newEvent.homeAway || "home",
        notes: newEvent.notes || "",
        result: JSON.stringify(null),
        attendance: JSON.stringify({}),
        volunteerSlotsTotal: newEvent.volunteerSlotsTotal || 0,
        volunteerSignups: newEvent.volunteerSignups || [],
        volunteerTeamIds: newEvent.volunteerTeamIds || [],
        teamID: currentTeam.id
      };
      
      const eventResult = await client.graphql({
        query: createEventMutation,
        variables: {
          input: eventInput
        }
      });
      
      if (eventResult.errors) {
        alert('Failed to create event. Check console for details.');
        return;
      }
      
      const createdEvent = eventResult.data.createEvent;
      
      // Reload club data to show new event
      await fetchClubData();
      
      
      setNewEvent({ 
        type: "game", 
        title: "", 
        date: "", 
        time: "", 
        endTime: "",
        arrivalTime: "",
        location: "", 
        opponent: "", 
        homeAway: "home", 
        notes: "" 
      });
      setShowAddEvent(false);
      showToast('Event created successfully', 'success');
      haptic.success();
      
    } catch (error) {
      showToast('Failed to create event: ' + error.message, 'error');
      haptic.error();
    }
  }

  async function editEvent() {
    if (!editingEvent.date) {
      showToast('Please fill in Date', 'error');
      return;
    }
    
    try {
      const updateEventMutation = `
        mutation UpdateEvent($input: UpdateEventInput!) {
          updateEvent(input: $input) {
            id
            type
            title
            date
            time
            endTime
            arrivalTime
            location
            opponent
            homeAway
            notes
            result
            attendance
            volunteerSlotsTotal
            volunteerSignups
            volunteerTeamIds
          }
        }
      `;
      
      const eventInput = {
        id: editingEvent.id,
        type: editingEvent.type,
        title: editingEvent.title || (editingEvent.type === "game" ? "Game" : "Practice"),
        date: editingEvent.date,
        time: editingEvent.time || "",
        endTime: editingEvent.endTime || "",
        arrivalTime: editingEvent.arrivalTime || "",
        location: editingEvent.location || "",
        opponent: editingEvent.opponent || "",
        homeAway: editingEvent.homeAway || "home",
        notes: editingEvent.notes || "",
        volunteerSlotsTotal: editingEvent.volunteerSlotsTotal || 0,
        volunteerSignups: editingEvent.volunteerSignups || [],
        volunteerTeamIds: editingEvent.volunteerTeamIds || []
      };
      
      const eventResult = await client.graphql({
        query: updateEventMutation,
        variables: {
          input: eventInput
        }
      });
      
      if (eventResult.errors) {
        showToast('Failed to update event', 'error');
        return;
      }
      
      showToast('Event updated successfully', 'success');
      
      // Reload club data
      await fetchClubData();
      
      setShowEditEvent(false);
      setEditingEvent(null);
      
    } catch (error) {
      showToast('Failed to update event', 'error');
    }
  }

  function openEditEvent(event) {
    setEditingEvent({
      id: event.id,
      type: event.type,
      title: event.title,
      date: event.date,
      time: event.time || "",
      endTime: event.endTime || "",
      arrivalTime: event.arrivalTime || "",
      location: event.location || "",
      opponent: event.opponent || "",
      homeAway: event.homeAway || "home",
      notes: event.notes || "",
      volunteerSlotsTotal: event.volunteerSlotsTotal || 0,
      volunteerSignups: event.volunteerSignups || [],
      volunteerTeamIds: event.volunteerTeamIds || []
    });
    setShowEditEvent(true);
  }

  async function sendMessage() {
    if (!newMessage.trim()) return;
    
    try {
      // Create message in database
      const createMessageMutation = `
        mutation CreateMessage($input: CreateMessageInput!) {
          createMessage(input: $input) {
            id
            author
            avatar
            text
            time
            pinned
            teamID
          }
        }
      `;
      
      const messageResult = await client.graphql({
        query: createMessageMutation,
        variables: {
          input: {
            author: currentUser.name,
            avatar: currentUser.avatar || currentUser.email.substring(0, 2).toUpperCase(),
            text: newMessage,
            time: new Date().toISOString(),
            pinned: false,
            teamID: currentTeam.id
          }
        }
      });
      
      if (messageResult.errors) {
        alert('Failed to send message. Please try again.');
        return;
      }
      
      const createdMessage = messageResult.data.createMessage;
      
      // Add to local state
      const newMessages = [...messages, createdMessage];
      updateTeam({ messages: { items: newMessages } });
      setNewMessage("");
      
    } catch (error) {
      alert('Failed to send message. Error: ' + error.message);
    }
  }

  async function sendInvite() {
    if (!inviteEmail || !inviteEmail.includes('@')) {
      showToast('Please enter a valid email address', 'error');
      return;
    }
    
    try {
      setInviteSending(true);
      
      // Don't create a User record yet - they'll get one when they actually sign up
      // Just show success message
      showToast(`Invitation noted for ${inviteEmail}! When they sign up with this email, they'll automatically get access.`, 'info');
      
      setInviteEmail("");
      setInviteRole("player");
      setShowInvite(false);
      setInviteSending(false);
      
    } catch (error) {
      showToast('Failed to send invite', 'error');
      setInviteSending(false);
    }
  }

  function calculateVolunteerHours(event) {
    if (!event.time || !event.endTime || event.time === 'TBD' || event.endTime === 'TBD') return null;
    try {
      const parseTime = (t) => {
        const [h, m] = t.replace(' AM','').replace(' PM','').split(':').map(Number);
        return h * 60 + (m || 0);
      };
      const start = parseTime(event.time);
      const end = parseTime(event.endTime);
      const diff = end - start;
      if (diff <= 0) return null;
      return Math.round(diff / 60 * 10) / 10; // Round to 1 decimal
    } catch (e) {
      return null;
    }
  }

  async function toggleVolunteer(event) {
    const userName = currentUser.name || currentUser.email;
    const isSignedUp = event.volunteerSignups?.includes(userName);
    const updatedSignups = isSignedUp
      ? (event.volunteerSignups || []).filter(n => n !== userName)
      : [...(event.volunteerSignups || []), userName];

    try {
      await client.graphql({
        query: `mutation UpdateEvent($input: UpdateEventInput!) {
          updateEvent(input: $input) { id volunteerSignups }
        }`,
        variables: { input: { id: event.id, volunteerSignups: updatedSignups } }
      });
      await fetchClubData();
      showToast(isSignedUp ? 'Removed from volunteer list' : 'Signed up to volunteer! 🙋', 'success');
    } catch (error) {
      showToast('Failed to update volunteer status', 'error');
    }
  }

  function buildGoogleCalendarUrl(event) {
    const formatDateTime = (dateStr, timeStr) => {
      const [year, month, day] = dateStr.split('-');
      if (timeStr && timeStr !== 'TBD' && timeStr.includes(':')) {
        const [hour, minute] = timeStr.replace(' AM','').replace(' PM','').split(':');
        return `${year}${month}${day}T${hour.padStart(2,'0')}${minute.padStart(2,'0')}00`;
      }
      return `${year}${month}${day}`;
    };

    const start = formatDateTime(event.date, event.time);
    const end = event.endTime && event.endTime !== 'TBD'
      ? formatDateTime(event.date, event.endTime)
      : start;

    const params = new URLSearchParams({
      action: 'TEMPLATE',
      text: `Volunteer: ${event.teamName} ${event.title}`,
      dates: `${start}/${end}`,
      details: `Volunteering for ${event.teamName} - ${event.title}`,
      location: event.location || ''
    });

    return `https://calendar.google.com/calendar/render?${params.toString()}`;
  }

  async function setAttendanceForEvent(eventId, playerId, status) {
    try {
      // Haptic feedback on tap
      haptic.light();
      
      // Find the event
      const event = events.find(e => e.id === eventId);
      if (!event) return;
      
      // Get current attendance (parse from JSON if needed)
      const currentAttendance = getAttendance(event);
      
      // Update attendance
      const updatedAttendance = { ...currentAttendance, [playerId]: status };
      
      // Update event in database
      const updateEventMutation = `
        mutation UpdateEvent($input: UpdateEventInput!) {
          updateEvent(input: $input) {
            id
            type
            title
            date
            time
            location
            opponent
            homeAway
            notes
            result
            attendance
          }
        }
      `;
      
      const eventResult = await client.graphql({
        query: updateEventMutation,
        variables: {
          input: {
            id: eventId,
            attendance: JSON.stringify(updatedAttendance)
          }
        }
      });
      
      if (eventResult.errors) {
        showToast('Failed to update attendance', 'error');
        haptic.error();
        return;
      }
      
      
      showToast('Attendance updated', 'success');
      haptic.success();
      
      // Reload club data to get updated events
      await fetchClubData();
      
    } catch (error) {
      showToast('Failed to update attendance', 'error');
      haptic.error();
    }
  }

  async function createNewClub() {
    if (!newClubName.trim()) {
      alert('Please enter a club name');
      return;
    }
    
    try {
      // Create club in database
      const createClubMutation = `
        mutation CreateClub($input: CreateClubInput!) {
          createClub(input: $input) {
            id
            name
            ownerId
          }
        }
      `;
      
      const clubResult = await client.graphql({
        query: createClubMutation,
        variables: {
          input: {
            name: newClubName,
            ownerId: newClubOwnerEmail || currentUser.id // Default to super_admin if no owner specified
          }
        }
      });
      
      if (clubResult.errors) {
        alert('Failed to create club. Please try again.');
        return;
      }
      
      const createdClub = clubResult.data.createClub;
      
      // If owner email provided, create user for them
      if (newClubOwnerEmail && newClubOwnerEmail.includes('@')) {
        const createUserMutation = `
          mutation CreateUser($input: CreateUserInput!) {
            createUser(input: $input) {
              id
              userId
              email
              name
              role
              clubId
            }
          }
        `;
        
        await client.graphql({
          query: createUserMutation,
          variables: {
            input: {
              userId: `invited-${Date.now()}`,
              email: newClubOwnerEmail,
              name: newClubOwnerEmail.split('@')[0],
              role: ROLES.CLUB_OWNER,
              clubId: createdClub.id
            }
          }
        });
      }
      
      // Add to allClubs list
      const clubWithTeams = {
        ...createdClub,
        teams: { items: [] }
      };
      setAllClubs([...allClubs, clubWithTeams]);
      
      // Select the new club
      setSelectedClubId(createdClub.id);
      setClub(clubWithTeams);
      
      alert(`Club "${newClubName}" created successfully!${newClubOwnerEmail ? ` Owner invitation sent to ${newClubOwnerEmail}.` : ''}`);
      
      setNewClubName("");
      setNewClubOwnerEmail("");
      setShowCreateClub(false);
      
    } catch (error) {
      alert('Failed to create club. Error: ' + error.message);
    }
  }

  async function assignClubOwner() {
    if (!assignOwnerEmail || !assignOwnerEmail.includes('@')) {
      alert('Please enter a valid email address');
      return;
    }
    
    if (!clubForOwnerAssignment) {
      alert('No club selected');
      return;
    }
    
    try {
      // Check if user already exists
      const listUsersQuery = `
        query ListUsers($filter: ModelUserFilterInput) {
          listUsers(filter: $filter) {
            items {
              id
              userId
              email
              name
              role
              clubId
            }
          }
        }
      `;
      
      const usersResult = await client.graphql({
        query: listUsersQuery,
        variables: {
          filter: { email: { eq: assignOwnerEmail } }
        }
      });
      
      const users = usersResult.data?.listUsers?.items || [];
      
      if (users.length > 0) {
        // User exists, update their role and clubId
        const user = users[0];
        const updateUserMutation = `
          mutation UpdateUser($input: UpdateUserInput!) {
            updateUser(input: $input) {
              id
              userId
              email
              name
              role
              clubId
            }
          }
        `;
        
        await client.graphql({
          query: updateUserMutation,
          variables: {
            input: {
              id: user.id,
              role: ROLES.CLUB_OWNER,
              clubId: clubForOwnerAssignment.id
            }
          }
        });
        
        alert(`${user.name} is now the owner of ${clubForOwnerAssignment.name}`);
      } else {
        // Create new user
        const createUserMutation = `
          mutation CreateUser($input: CreateUserInput!) {
            createUser(input: $input) {
              id
              userId
              email
              name
              role
              clubId
            }
          }
        `;
        
        await client.graphql({
          query: createUserMutation,
          variables: {
            input: {
              userId: `invited-${Date.now()}`,
              email: assignOwnerEmail,
              name: assignOwnerEmail.split('@')[0],
              role: ROLES.CLUB_OWNER,
              clubId: clubForOwnerAssignment.id
            }
          }
        });
        
        alert(`Invitation sent to ${assignOwnerEmail}. They will be the owner of ${clubForOwnerAssignment.name} when they sign up.`);
      }
      
      // Update club's ownerId
      const updateClubMutation = `
        mutation UpdateClub($input: UpdateClubInput!) {
          updateClub(input: $input) {
            id
            name
            ownerId
          }
        }
      `;
      
      await client.graphql({
        query: updateClubMutation,
        variables: {
          input: {
            id: clubForOwnerAssignment.id,
            ownerId: assignOwnerEmail // Store email as ownerId for now
          }
        }
      });
      
      setAssignOwnerEmail("");
      setShowAssignOwner(false);
      setClubForOwnerAssignment(null);
      
      // Refresh clubs
      await fetchClubData();
      
    } catch (error) {
      alert('Failed to assign owner. Error: ' + error.message);
    }
  }

  async function addTeam() {
    if (!newTeam.name || !newTeam.sport) return;
    
    try {
      // Create team in database
      const createTeamMutation = `
        mutation CreateTeam($input: CreateTeamInput!) {
          createTeam(input: $input) {
            id
            name
            sport
            season
            ageGroup
            color
            logo
            clubID
            coachIds
          }
        }
      `;
      
      const teamResult = await client.graphql({
        query: createTeamMutation,
        variables: {
          input: {
            name: newTeam.name,
            sport: newTeam.sport,
            season: newTeam.season || "",
            ageGroup: newTeam.ageGroup || "",
            color: newTeam.color,
            logo: newTeam.logo,
            clubID: club.id,
            coachIds: []
          }
        }
      });
      
      if (teamResult.errors) {
        alert('Failed to create team. Please try again.');
        return;
      }
      
      const createdTeam = teamResult.data.createTeam;
      
      // Add to local state with GraphQL structure
      const teamWithRelations = {
        ...createdTeam,
        roster: { items: [] },
        events: { items: [] },
        messages: { items: [] }
      };
      
      setClub({ 
        ...club, 
        teams: { 
          items: [...club.teams.items, teamWithRelations] 
        } 
      });
      
      setNewTeam({ name: "", sport: "", season: "", ageGroup: "", color: "#FF6B35", logo: "⚽" });
      setShowAddTeam(false);
      setSelectedTeamId(createdTeam.id); // Auto-select the new team
      
    } catch (error) {
      alert('Failed to create team. Error: ' + error.message);
    }
  }

  async function editTeam() {
    if (!editingTeam.name) return;
    
    try {
      // Update team in database
      const updateTeamMutation = `
        mutation UpdateTeam($input: UpdateTeamInput!) {
          updateTeam(input: $input) {
            id
            name
            sport
            season
            ageGroup
            color
            logo
            clubID
            coachIds
          }
        }
      `;
      
      const teamResult = await client.graphql({
        query: updateTeamMutation,
        variables: {
          input: {
            id: editingTeam.id,
            name: editingTeam.name,
            sport: editingTeam.sport,
            season: editingTeam.season || "",
            ageGroup: editingTeam.ageGroup || "",
            color: editingTeam.color,
            logo: editingTeam.logo
          }
        }
      });
      
      if (teamResult.errors) {
        alert('Failed to update team. Please try again.');
        return;
      }
      
      
      // Update local state
      setClub({
        ...club,
        teams: {
          items: club.teams.items.map(t => 
            t.id === editingTeam.id 
              ? { ...t, ...editingTeam } 
              : t
          )
        }
      });
      
      setShowEditTeam(false);
      setEditingTeam(null);
      
    } catch (error) {
      alert('Failed to update team. Error: ' + error.message);
    }
  }

  async function deleteTeam(teamId) {
    try {
      
      // Delete team from database
      const deleteTeamMutation = `
        mutation DeleteTeam($input: DeleteTeamInput!) {
          deleteTeam(input: $input) {
            id
          }
        }
      `;
      
      const teamResult = await client.graphql({
        query: deleteTeamMutation,
        variables: {
          input: {
            id: teamId
          }
        }
      });
      
      if (teamResult.errors) {
        alert('Failed to delete team. Please try again.');
        return;
      }
      
      
      // Update local state
      const updatedTeams = club.teams.items.filter(t => t.id !== teamId);
      setClub({
        ...club,
        teams: { items: updatedTeams }
      });
      
      // If deleted team was selected, switch to first remaining team
      if (selectedTeamId === teamId && updatedTeams.length > 0) {
        setSelectedTeamId(updatedTeams[0].id);
        setTab("dashboard");
      }
      
      setShowDeleteConfirm(false);
      setTeamToDelete(null);
      
    } catch (error) {
      alert('Failed to delete team. Error: ' + error.message);
    }
  }

  async function assignCoach(teamId, coachId, action) {
    try {
      // Fetch fresh team data from database first
      const getTeamQuery = `
        query GetTeam($id: ID!) {
          getTeam(id: $id) {
            id
            name
            coachIds
          }
        }
      `;
      
      const teamResult = await client.graphql({
        query: getTeamQuery,
        variables: { id: teamId }
      });
      
      const freshTeam = teamResult.data?.getTeam;
      if (!freshTeam) {
        throw new Error('Team not found');
      }
      
      // Use fresh data from database
      const currentCoachIds = freshTeam.coachIds || [];
      
      console.log('Current coachIds from database:', currentCoachIds);
      
      let updatedCoachIds;
      if (action === 'add') {
        // Only add if not already present
        if (!currentCoachIds.includes(coachId)) {
          updatedCoachIds = [...currentCoachIds, coachId];
        } else {
          console.log('Coach already assigned, skipping');
          return; // Already assigned, don't do anything
        }
      } else {
        updatedCoachIds = currentCoachIds.filter(id => id !== coachId);
      }
      
      // Update local state
      setClub({
        ...club,
        teams: {
          items: club.teams.items.map(t => {
            if (t.id !== teamId) return t;
            return { ...t, coachIds: updatedCoachIds };
          })
        }
      });

      // Update in database using GraphQL
      const updateTeamMutation = `
        mutation UpdateTeam($input: UpdateTeamInput!) {
          updateTeam(input: $input) {
            id
            coachIds
          }
        }
      `;
      
      console.log('Updating team with coachIds:', updatedCoachIds);
      
      const result = await client.graphql({
        query: updateTeamMutation,
        variables: {
          input: {
            id: freshTeam.id,
            coachIds: updatedCoachIds
          }
        }
      });
      
      console.log('Team update result:', result);
      
      // Check for errors
      if (result.errors) {
        console.error('GraphQL errors:', result.errors);
        throw new Error(result.errors[0]?.message || 'Update failed');
      }
    
      // Reload coaches
      await loadCoaches();
      
      // Also reload full club data to get fresh coachIds
      await fetchClubData();
      
      console.log('✅ Coach assigned successfully');
    } catch (error) {
      console.error('Error in assignCoach:', error);
      console.error('Error details:', {
        teamId,
        coachId,
        action,
        message: error.message,
        errors: error.errors,
        stack: error.stack
      });
      alert('Failed to assign coach. Please try again.');
    }
  }

  async function getAvailableCoaches() {
    try {
      const listCoachesQuery = `
        query ListCoaches($filter: ModelCoachFilterInput) {
          listCoaches(filter: $filter) {
            items {
              id
              userId
              name
              email
              role
              avatar
              teamIds
            }
          }
        }
      `;
      
      const result = await client.graphql({
        query: listCoachesQuery,
        variables: {
          filter: {
            or: [
              { role: { eq: 'team_manager' } },
              { role: { eq: 'coach' } }
            ]
          }
        }
      });
      
      return result.data?.listCoaches?.items || [];
    } catch (error) {
      console.error('Error fetching available coaches:', error);
      return [];
    }
  }

  async function getTeamCoaches(teamId) {
    try {
      const teams = club?.teams?.items || [];
      const team = teams.find(t => t.id === teamId);
      if (!team || !team.coachIds || team.coachIds.length === 0) {
        console.log('getTeamCoaches: No team or no coachIds', { team: team?.name, coachIds: team?.coachIds });
        return [];
      }
      
      console.log('getTeamCoaches: Looking for coaches with userIds:', team.coachIds);
      
      // Fetch all coaches using GraphQL
      const listCoachesQuery = `
        query ListCoaches {
          listCoaches {
            items {
              id
              userId
              name
              email
              role
              avatar
              teamIds
            }
          }
        }
      `;
      
      const result = await client.graphql({
        query: listCoachesQuery
      });
      
      const allCoaches = result.data?.listCoaches?.items || [];
      
      console.log('getTeamCoaches: All coaches:', allCoaches.map(c => ({ name: c.name, userId: c.userId, role: c.role })));
      
      // Filter to coaches assigned to this team
      const teamCoaches = allCoaches.filter(coach => 
        team.coachIds.includes(coach.userId)
      );
      
      console.log('getTeamCoaches: Matched coaches:', teamCoaches.map(c => c.name));
      
      return teamCoaches;
    } catch (error) {
      console.error('Error fetching team coaches:', error);
      return [];
    }
  }

  const navItems = [
    { id: "dashboard", icon: "🏠", label: "Dashboard" },
    ...(isSuperAdmin ? [{ id: "clubs", icon: "🏢", label: "All Clubs" }] : []),
    ...(isOwner ? [{ id: "teams", icon: "🏆", label: "Teams" }] : []),
    ...(isOwner ? [{ id: "users", icon: "👤", label: "Users" }] : []),
    { id: "roster", icon: "👥", label: "Roster" },
    { id: "schedule", icon: "📅", label: "Schedule" },
    { id: "attendance", icon: "✅", label: "Attendance" },
    { id: "messages", icon: "📧", label: "Email" },
    ...(currentTeam?.financialSheetUrl && (isOwner || isTeamManager || isParent) && !isPlayer ? [{ id: "financials", icon: "💰", label: "Financials" }] : []),
    ...(volunteerEvents.length > 0 ? [{ id: "volunteer", icon: "🙋", label: "Volunteer" }] : []),
  ];

  return (
    <>
      <style>{css}</style>
      <div className="top-banner">Open Team Manager</div>
      
      {/* Phase 1: Offline Indicator */}
      {!isOnline && (
        <div className="offline-banner">
          ⚠️ No internet connection
        </div>
      )}

      {/* Phase 1: Toast Notifications */}
      <div className="toast-container">
        {toasts.map(toast => (
          <div key={toast.id} className={`toast ${toast.type}`}>
            <span>{toast.message}</span>
          </div>
        ))}
      </div>
      
      {/* Mobile Header */}
      <div className="mobile-header" style={{flexDirection: allClubs.length > 1 ? 'column' : 'row', padding: allClubs.length > 1 ? '8px 12px' : '12px', gap: allClubs.length > 1 ? '4px' : '0'}}>
        {/* Top row: Hamburger + Club Switcher (if multiple clubs) */}
        {allClubs.length > 1 && (
          <div style={{display: 'flex', alignItems: 'center', width: '100%', gap: '8px'}}>
            {(isSuperAdmin || isOwner) && (
              <button className="hamburger" onClick={() => setMobileMenuOpen(true)} style={{marginRight: '0'}}>
                ☰
              </button>
            )}
            <select 
              value={selectedClubId || ''}
              onChange={e => {
                const clubId = e.target.value;
                setSelectedClubId(clubId);
                const selected = allClubs.find(c => c.id === clubId);
                if (selected) {
                  setClub(selected);
                  setSelectedTeamId(null);
                }
              }}
              style={{
                flex: 1,
                textAlign: 'center',
                fontSize: '13px',
                fontWeight: '500',
                border: 'none',
                background: 'transparent',
                color: palette.textMuted,
                appearance: 'none',
                WebkitAppearance: 'none',
                MozAppearance: 'none',
                cursor: 'pointer',
                padding: '4px 20px 4px 4px',
                backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23999' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 2px center',
                backgroundSize: '14px'
              }}
            >
              {allClubs.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
            {!(isSuperAdmin || isOwner) && <div style={{width: '40px'}}></div>}
          </div>
        )}
        
        {/* Bottom row (or only row): Hamburger + Team Name/Switcher */}
        <div style={{display: 'flex', alignItems: 'center', width: '100%', gap: '8px'}}>
          {allClubs.length <= 1 && (isSuperAdmin || isOwner) && (
            <button className="hamburger" onClick={() => setMobileMenuOpen(true)}>
              ☰
            </button>
          )}
        
        {/* Team Name / Switcher */}
        {canSwitchTeams && accessibleTeams.length > 1 ? (
          <div style={{flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'}}>
            <select 
              className="mobile-header-title"
              value={selectedTeamId}
              onChange={e => setSelectedTeamId(e.target.value)}
              style={{
                textAlign: 'center',
                fontSize: '16px',
                fontWeight: '600',
                border: 'none',
                background: 'transparent',
                color: palette.text,
                appearance: 'none',
                WebkitAppearance: 'none',
                MozAppearance: 'none',
                cursor: 'pointer',
                padding: '0 24px 0 4px',
                backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23666' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 4px center',
                backgroundSize: '16px'
              }}
            >
              {accessibleTeams.map(team => (
                <option key={team.id} value={team.id}>
                  {team.name} {team.ageGroup ? `(${team.ageGroup})` : ''}
                </option>
              ))}
            </select>
            
            {/* Star button to set as default */}
            <button
              onClick={() => setDefaultTeam(selectedTeamId)}
              title={currentUser?.defaultTeamId === selectedTeamId ? "This is your default team" : "Set as default team"}
              style={{
                background: 'transparent',
                border: 'none',
                padding: '4px 8px',
                cursor: 'pointer',
                fontSize: '22px',
                lineHeight: 1,
                color: currentUser?.defaultTeamId === selectedTeamId ? '#FFD700' : '#666',
                transition: 'all 0.2s',
                WebkitTapHighlightColor: 'transparent',
                flexShrink: 0
              }}
              onMouseDown={e => e.currentTarget.style.transform = 'scale(0.9)'}
              onMouseUp={e => e.currentTarget.style.transform = 'scale(1)'}
              onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
            >
              {currentUser?.defaultTeamId === selectedTeamId ? '⭐' : '☆'}
            </button>
          </div>
        ) : (
          <div className="mobile-header-title" style={{flex: 1, textAlign: 'center'}}>
            {currentTeam?.name || club?.name}
          </div>
        )}
        
        {allClubs.length <= 1 && (isSuperAdmin || isOwner) && <div style={{width: '40px'}}></div>}
        </div>
      </div>

      {/* Mobile Overlay */}
      <div 
        className={`mobile-overlay${mobileMenuOpen ? ' active' : ''}`}
        onClick={() => setMobileMenuOpen(false)}
      ></div>
      
      {/* Phase 1: Pull-to-Refresh wrapper */}
      <div 
        className="pull-to-refresh"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {isPulling && (
          <div className="pull-to-refresh-indicator pulling">↓</div>
        )}
      
      <div className="app">
        {/* SIDEBAR */}
        <aside className={`sidebar${mobileMenuOpen ? ' mobile-open' : ''}`}>
          <div className="sidebar-brand">
            {allClubs.length > 1 && (
              <select 
                className="form-select" 
                value={selectedClubId || ''}
                onChange={e => {
                  const clubId = e.target.value;
                  setSelectedClubId(clubId);
                  const selected = allClubs.find(c => c.id === clubId);
                  if (selected) {
                    setClub(selected);
                    // Reset team selection - let accessibleTeams logic handle it
                    setSelectedTeamId(null);
                  }
                }}
                style={{marginBottom:"12px", fontSize:"14px", fontWeight:"700", background: isSuperAdmin ? palette.accentSoft : palette.surfaceAlt, color: isSuperAdmin ? palette.accent : palette.text, border:`1px solid ${isSuperAdmin ? palette.accent : palette.border}`}}
              >
                {allClubs.map(club => (
                  <option key={club.id} value={club.id}>
                    🏢 {club.name}
                  </option>
                ))}
              </select>
            )}
            
            {canSwitchTeams && accessibleTeams.length > 1 ? (
              <div style={{display: 'flex', alignItems: 'center', gap: '8px', marginBottom:"12px"}}>
                <select 
                  className="form-select" 
                  value={selectedTeamId}
                  onChange={e => setSelectedTeamId(e.target.value)}
                  style={{flex: 1, fontSize:"14px", fontWeight:"600", marginBottom: 0}}
                >
                  {accessibleTeams.map(team => (
                    <option key={team.id} value={team.id}>
                      {team.name} {team.ageGroup ? `(${team.ageGroup})` : ''}
                    </option>
                  ))}
                </select>
                
                {/* Star button to set as default */}
                <button
                  onClick={() => setDefaultTeam(selectedTeamId)}
                  title={currentUser?.defaultTeamId === selectedTeamId ? "This is your default team" : "Set as default team"}
                  style={{
                    background: currentUser?.defaultTeamId === selectedTeamId ? palette.accentSoft : 'transparent',
                    border: `1px solid ${currentUser?.defaultTeamId === selectedTeamId ? palette.accent : palette.border}`,
                    padding: '6px 10px',
                    cursor: 'pointer',
                    fontSize: '16px',
                    lineHeight: 1,
                    borderRadius: '6px',
                    transition: 'all 0.2s',
                    WebkitTapHighlightColor: 'transparent'
                  }}
                  onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.05)'}
                  onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
                >
                  {currentUser?.defaultTeamId === selectedTeamId ? '⭐' : '☆'}
                </button>
              </div>
            ) : null}
            <div className="brand-logo" style={{marginTop: canSwitchTeams ? "0" : "0"}}>
              <div className="logo-badge">{currentTeam?.logo || "🏆"}</div>
              <div>
                <div className="brand-name" style={{fontSize: canSwitchTeams ? "14px" : "16px"}}>{currentTeam?.name || club?.name || 'No Club'}</div>
              </div>
            </div>
            <div className="brand-meta">{currentTeam ? `${currentTeam.sport} · ${currentTeam.season}` : club ? "No teams yet" : "Select a club"}</div>
            {currentTeam?.ageGroup && <div className="brand-meta" style={{marginTop:"2px", fontSize:"11px", color: palette.accent}}>{currentTeam.ageGroup}</div>}
          </div>

          <nav className="sidebar-nav">
            <div className="nav-section">Team</div>
            {navItems.map(item => (
              <div
                key={item.id}
                className={`nav-item${tab === item.id ? " active" : ""}`}
                onClick={() => {
                  setTab(item.id);
                  setMobileMenuOpen(false);
                }}
              >
                <span className="nav-icon">{item.icon}</span>
                {item.label}
              </div>
            ))}
          </nav>

          <div className="sidebar-footer">
            <div className="coach-badge">
              <div className="coach-avatar" style={{background: currentUser.role === "coach" ? palette.accent : palette.blue}}>
                {currentUser.avatar}
              </div>
              <div className="coach-info">
                <div className="coach-name">{currentUser.name}</div>
                <div className="coach-role" style={{textTransform:"capitalize"}}>{currentUser.role}</div>
              </div>
              <button
                onClick={() => {
                  signOut();
                }}
                title="Sign out"
                style={{background:"none", border:"none", cursor:"pointer", color: palette.textDim, fontSize:"16px", padding:"4px", marginLeft:"auto", transition:"color 0.15s"}}
                onMouseOver={e => e.currentTarget.style.color = palette.red}
                onMouseOut={e => e.currentTarget.style.color = palette.textDim}
              >⎋</button>
            </div>
          </div>
        </aside>

        {/* MAIN CONTENT */}
        <main className="main">

          {/* DASHBOARD */}
          {/* SUPER ADMIN: All Clubs Tab */}
          {isSuperAdmin && tab === "clubs" && (
            <>
              <div className="page-header">
                <div>
                  <div className="page-title">All Clubs</div>
                  <div className="page-sub">{allClubs.length} clubs on the platform</div>
                </div>
                <button className="btn btn-primary" onClick={() => setShowCreateClub(true)}>
                  + Create New Club
                </button>
              </div>
              <div className="content">
                <div style={{display: 'grid', gap: '16px'}}>
                  {allClubs.map(club => (
                    <div key={club.id} className="card" style={{padding: '20px'}}>
                      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start'}}>
                        <div style={{flex: 1}}>
                          <div style={{
                            fontSize: '18px',
                            fontWeight: '700',
                            color: palette.text,
                            marginBottom: '8px'
                          }}>
                            {club.name}
                          </div>
                          <div style={{fontSize: '14px', color: palette.textMuted}}>
                            {club.teams?.items?.length || 0} teams · Owner: {club.ownerId}
                          </div>
                        </div>
                        <div style={{display: 'flex', gap: '8px'}}>
                          <button 
                            className="btn btn-ghost btn-sm"
                            onClick={() => {
                              setSelectedClubId(club.id);
                              setClub(club);
                              if (club.teams?.items?.length > 0) {
                                setSelectedTeamId(club.teams.items[0].id);
                              }
                              setTab('dashboard');
                            }}
                          >
                            👁 View
                          </button>
                          <button 
                            className="btn btn-ghost btn-sm"
                            onClick={() => {
                              setClubForOwnerAssignment(club);
                              setShowAssignOwner(true);
                            }}
                          >
                            👤 Assign Owner
                          </button>
                          <button 
                            className="btn btn-ghost btn-sm"
                            onClick={async () => {
                              if (window.confirm(`Delete "${club.name}"? This will permanently delete the club and all its teams, players, events, and messages. This cannot be undone.`)) {
                                try {
                                  const deleteClubMutation = `
                                    mutation DeleteClub($input: DeleteClubInput!) {
                                      deleteClub(input: $input) {
                                        id
                                      }
                                    }
                                  `;
                                  
                                  await client.graphql({
                                    query: deleteClubMutation,
                                    variables: {
                                      input: { id: club.id }
                                    }
                                  });
                                  
                                  // Remove from local state
                                  setAllClubs(allClubs.filter(c => c.id !== club.id));
                                  
                                  // If this was the selected club, clear selection
                                  if (selectedClubId === club.id) {
                                    setSelectedClubId(null);
                                    setClub(null);
                                    setSelectedTeamId(null);
                                  }
                                  
                                  alert(`"${club.name}" deleted successfully`);
                                } catch (error) {
                                  alert('Failed to delete club. Error: ' + error.message);
                                }
                              }
                            }}
                            style={{color: palette.red}}
                          >
                            🗑️ Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                
                {allClubs.length === 0 && (
                  <div style={{
                    textAlign: 'center',
                    padding: '80px 20px',
                    color: palette.textMuted
                  }}>
                    <div style={{fontSize: '64px', marginBottom: '20px'}}>🏢</div>
                    <div style={{
                      fontSize: '24px',
                      fontWeight: '700',
                      marginBottom: '12px',
                      color: palette.text
                    }}>
                      No clubs yet
                    </div>
                    <div style={{fontSize: '16px', marginBottom: '24px'}}>
                      Create your first club to get started.
                    </div>
                    <button 
                      className="btn btn-primary"
                      onClick={() => setShowCreateClub(true)}
                      style={{fontSize: '16px', padding: '12px 24px'}}
                    >
                      + Create First Club
                    </button>
                  </div>
                )}
              </div>
            </>
          )}

          {tab === "dashboard" && (
            <>
              <div className="page-header">
                <div>
                  <div className="page-title">Dashboard</div>
                  <div className="page-sub">Welcome back, {currentUser.name}</div>
                </div>
                <div className="header-actions">
                  {canManageEvents && <button className="btn btn-ghost" onClick={() => setShowAddEvent(true)}>+ Add Event</button>}
                  {canManagePlayers && <button className="btn btn-primary" onClick={() => setShowAddPlayer(true)}>+ Add Player</button>}
                </div>
              </div>
              <div className="content">
                {/* NEW: Show welcome screen if no teams exist */}
                {(!accessibleTeams || accessibleTeams.length === 0) ? (
                  <div style={{
                    textAlign: 'center',
                    padding: '80px 20px',
                    color: palette.textMuted
                  }}>
                    <div style={{fontSize: '64px', marginBottom: '20px'}}>🏆</div>
                    <div style={{
                      fontSize: '24px',
                      fontWeight: '700',
                      marginBottom: '12px',
                      color: palette.text
                    }}>
                      Welcome to Team Manager!
                    </div>
                    <div style={{fontSize: '16px', marginBottom: '24px'}}>
                      Get started by creating your first team.
                    </div>
                    {isOwner && (
                      <button 
                        className="btn btn-primary"
                        onClick={() => setShowAddTeam(true)}
                        style={{fontSize: '16px', padding: '12px 24px'}}
                      >
                        + Create First Team
                      </button>
                    )}
                  </div>
                ) : (
                <div className="dashboard-upcoming">
                  {upcomingEvents.length === 0 && (
                    <div style={{textAlign:"center", padding:"60px", color: palette.textMuted}}>
                      <div style={{fontSize:"48px", marginBottom:"12px"}}>📅</div>
                      <div style={{fontFamily:"'Barlow Condensed',sans-serif", fontSize:"18px", fontWeight:"700", marginBottom:"8px"}}>No Upcoming Events</div>
                      <div style={{fontSize:"14px"}}>Add an event to get started</div>
                    </div>
                  )}
                  {upcomingEvents.map(event => {
                    const d = formatDate(event.date);
                    const attendance = getAttendance(event);
                    
                    // Only count attendance for current roster players
                    const rosterPlayerIds = roster.map(p => p.id);
                    const validAttendance = {};
                    rosterPlayerIds.forEach(playerId => {
                      if (attendance[playerId]) {
                        validAttendance[playerId] = attendance[playerId];
                      }
                    });
                    
                    const confirmed = Object.values(validAttendance).filter(v => v === "yes").length;
                    const declined = Object.values(validAttendance).filter(v => v === "no").length;
                    const maybe = Object.values(validAttendance).filter(v => v === "maybe").length;
                    const responded = Object.keys(validAttendance).length;
                    const noResponse = Math.max(0, roster.length - responded);
                    const attendancePct = roster.length ? Math.round((confirmed / roster.length) * 100) : 0;
                    return (
                      <div 
                        key={event.id} 
                        className="card" 
                        style={{
                          marginBottom:"8px", 
                          padding:"10px 14px",
                          display:"flex",
                          alignItems:"center",
                          gap:"10px",
                          cursor: isParent ? "default" : "pointer"
                        }}
                        onClick={isParent ? undefined : () => { setSelectedEvent(event); setTab("attendance"); }}
                      >
                        {/* Date badge */}
                        <div style={{
                          width:"36px", height:"36px", borderRadius:"6px",
                          background: event.type === "game" ? "rgba(59,130,246,0.15)" : "rgba(34,197,94,0.15)",
                          display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", flexShrink:0, gap:"1px"
                        }}>
                          <div style={{fontSize:"7px", textTransform:"uppercase", letterSpacing:"0.3px", color: event.type === "game" ? palette.blue : palette.green, fontWeight:"600", textAlign:"center", width:"100%", lineHeight:"1"}}>{d.month}</div>
                          <div style={{fontFamily:"'Barlow Condensed',sans-serif", fontSize:"16px", fontWeight:"900", lineHeight:"1", color: event.type === "game" ? palette.blue : palette.green, textAlign:"center", width:"100%"}}>{d.day}</div>
                        </div>
                        
                        {/* Event info */}
                        <div style={{flex:1, minWidth:0}}>
                          <div style={{display:"flex", alignItems:"center", gap:"6px", marginBottom:"2px"}}>
                            <div style={{fontFamily:"'Barlow Condensed',sans-serif", fontSize:"14px", fontWeight:"900", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>{event.title}</div>
                            <span className={`event-type-badge ${event.type === "game" ? "type-game" : "type-practice"}`} style={{fontSize:"7px", padding:"2px 5px"}}>{event.type}</span>
                          </div>
                          <div style={{fontSize:"11px", color: palette.textMuted, display:"flex", gap:"8px", alignItems:"center"}}>
                            {event.time && <span>⏰ {event.time}</span>}
                            {event.location && <span>📍 {event.location}</span>}
                            {/* Inline attendance breakdown */}
                            <span style={{display:"flex", gap:"4px", marginLeft:"4px", alignItems:"center"}}>
                              {confirmed > 0 && <span style={{color: palette.green, fontWeight:"600"}}>{confirmed}✓</span>}
                              {maybe > 0 && <span style={{color: palette.yellow, fontWeight:"600"}}>{maybe}?</span>}
                              {declined > 0 && <span style={{color: palette.red, fontWeight:"600"}}>{declined}✗</span>}
                              {noResponse > 0 && <span style={{color: palette.textDim, fontWeight:"600"}}>{noResponse}○</span>}
                            </span>
                            {/* Volunteer info - coaches/managers only */}
                            {(canManageEvents || isOwner) && event.volunteerSlotsTotal > 0 && (
                              <span style={{
                                color: (event.volunteerSignups?.length || 0) >= event.volunteerSlotsTotal ? palette.green : palette.yellow,
                                fontWeight:"600",
                                fontSize:"11px"
                              }}>
                                🙋 {event.volunteerSignups?.length || 0}/{event.volunteerSlotsTotal}
                                {event.volunteerSignups?.length > 0 && ` · ${event.volunteerSignups.join(', ')}`}
                              </span>
                            )}
                          </div>
                        </div>
                        
                        {/* Quick RSVP for anyone with linked players */}
                        {currentUser.playerIds?.length > 0 && (
                          <div style={{display:"flex", gap:"2px"}}>
                            {roster
                              .filter(p => 
                                currentUser.playerIds?.includes(p.id) ||
                                (currentUser.email && (
                                  p.parent1Email?.toLowerCase() === currentUser.email.toLowerCase() ||
                                  p.parent2Email?.toLowerCase() === currentUser.email.toLowerCase() ||
                                  p.email?.toLowerCase() === currentUser.email.toLowerCase()
                                ))
                              )
                              .map(player => {
                              const playerId = player.id;
                              const status = attendance[playerId];
                              return (
                                <div key={playerId} style={{display:"flex", gap:"2px"}}>
                                  {["yes","maybe","no"].map(s => (
                                    <button
                                      key={s}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setAttendanceForEvent(event.id, playerId, s);
                                      }}
                                      style={{
                                        padding: "4px 6px",
                                        fontSize: "10px",
                                        fontWeight: status === s ? "700" : "500",
                                        border: `1px solid ${s === "yes" ? palette.green : s === "maybe" ? palette.yellow : palette.red}`,
                                        borderRadius: "3px",
                                        background: status === s ? (s === "yes" ? palette.green : s === "maybe" ? palette.yellow : palette.red) : "transparent",
                                        color: status === s ? "white" : (s === "yes" ? palette.green : s === "maybe" ? palette.yellow : palette.red),
                                        cursor: "pointer",
                                        transition: "all 0.15s"
                                      }}
                                    >
                                      {s === "yes" ? "✓" : s === "maybe" ? "?" : "✗"}
                                    </button>
                                  ))}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>)}
              </div>
            </>
          )}

          {/* TEAMS (Owner Only) */}
          {isOwner && tab === "teams" && (
            <>
              <div className="page-header">
                <div>
                  <div className="page-title">Teams Management</div>
                  <div className="page-sub">{club.teams.items.length} teams in {club.name}</div>
                </div>
                <button className="btn btn-primary" onClick={() => setShowAddTeam(true)}>+ Add Team</button>
              </div>
              <div className="content">
                <div style={{display:"grid", gap:"16px"}}>
                  {club.teams.items.map(team => {
                    const teamRoster = team.roster?.items || [];
                    const teamEvents = team.events?.items || [];
                    const upcomingGames = teamEvents.filter(e => e.type === "game" && new Date(e.date) >= today).length;
                    return (
                      <div key={team.id} className="card" style={{padding:"20px 24px"}}>
                        <div style={{display:"flex", alignItems:"flex-start", gap:"16px"}}>
                          <div style={{
                            width:"60px", height:"60px", background: team.color,
                            borderRadius:"12px", display:"flex", alignItems:"center",
                            justifyContent:"center", fontSize:"30px", flexShrink:0,
                            boxShadow:`0 4px 12px ${team.color}40`
                          }}>
                            {team.logo}
                          </div>
                          <div style={{flex:1}}>
                            <div style={{display:"flex", alignItems:"center", gap:"12px", marginBottom:"6px"}}>
                              <div style={{fontFamily:"'Barlow Condensed', sans-serif", fontSize:"22px", fontWeight:"900"}}>
                                {team.name}
                              </div>
                              {team.ageGroup && (
                                <span style={{
                                  fontSize:"11px", fontWeight:"700", padding:"3px 8px",
                                  borderRadius:"20px", background: `${team.color}20`,
                                  color: team.color, textTransform:"uppercase", letterSpacing:"0.5px"
                                }}>
                                  {team.ageGroup}
                                </span>
                              )}
                            </div>
                            <div style={{fontSize:"13px", color: palette.textMuted, marginBottom:"12px"}}>
                              {team.sport} · {team.season}
                            </div>
                          </div>
                          <div style={{display:"flex", gap:"8px"}}>
                            <button 
                              className="btn btn-ghost btn-sm"
                              onClick={() => { setTeamForCoachAssignment(team); setShowAssignCoach(true); }}
                              title="Assign coaches"
                            >
                              👤 Coaches
                            </button>
                            <button
                              className="btn btn-ghost btn-sm"
                              title={team.financialSheetUrl ? "Edit financial sheet" : "Link financial sheet"}
                              onClick={() => {
                                const url = prompt('Paste Google Sheet embed URL:', team.financialSheetUrl || '');
                                if (url !== null) {
                                  const embedUrl = url.includes('/pub?')
                                    ? url.replace('/pub?', '/pub?widget=true&')
                                    : url;
                                  client.graphql({
                                    query: `mutation UpdateTeam($input: UpdateTeamInput!) { updateTeam(input: $input) { id financialSheetUrl } }`,
                                    variables: { input: { id: team.id, financialSheetUrl: embedUrl } }
                                  }).then(() => fetchClubData());
                                }
                              }}
                            >
                              {team.financialSheetUrl ? '💰 Sheet ✓' : '💰 Link Sheet'}
                            </button>
                            <button 
                              className="btn btn-ghost btn-sm"
                              onClick={() => { setSelectedTeamId(team.id); setTab("dashboard"); }}
                              title="View team"
                            >
                              👁 View
                            </button>
                            <button 
                              className="btn btn-ghost btn-sm"
                              onClick={() => { setEditingTeam({...team}); setShowEditTeam(true); }}
                              title="Edit team"
                            >
                              ✏️ Edit
                            </button>
                            <button 
                              className="btn btn-ghost btn-sm"
                              onClick={() => {
                                setTeamToDelete(team);
                                setShowDeleteConfirm(true);
                              }}
                              title="Delete team"
                              style={{color: palette.red}}
                            >
                              🗑️ Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}

          {/* USERS (Owner Only) */}
          {(isSuperAdmin || isOwner) && tab === "users" && (
            <>
              <div className="page-header">
                <div>
                  <div className="page-title">User Management</div>
                  <div className="page-sub">Manage roles and permissions</div>
                </div>
              </div>
              <div className="content">
                <div style={{display:"flex", flexDirection:"column", gap:"12px"}}>
                  {allUsers.map(u => (
                    <div key={u.id} className="card" style={{padding:"16px 20px"}}>
                      <div style={{display:"flex", alignItems:"center", gap:"16px"}}>
                        <div style={{
                          width:"48px", height:"48px", borderRadius:"50%",
                          background: palette.accent, display:"flex", alignItems:"center",
                          justifyContent:"center", fontSize:"18px", fontWeight:"700", color:"white"
                        }}>
                          {u.avatar || u.email[0].toUpperCase()}
                        </div>
                        <div style={{flex:1}}>
                          <div style={{fontSize:"16px", fontWeight:"600"}}>{u.name}</div>
                          <div style={{fontSize:"13px", color: palette.textMuted}}>{u.email}</div>
                        </div>
                        <select
                          className="form-select"
                          value={u.role}
                          onChange={(e) => updateUserRole(u.userId, e.target.value)}
                          style={{width:"200px"}}
                        >
                          <option value={ROLES.SUPER_ADMIN}>Super Admin</option>
                          <option value={ROLES.CLUB_OWNER}>Club Owner</option>
                          <option value={ROLES.TEAM_MANAGER}>Team Manager</option>
                          <option value={ROLES.COACH}>Coach</option>
                          <option value={ROLES.PARENT}>Parent</option>
                          <option value={ROLES.PLAYER}>Player</option>
                        </select>
                        <button 
                          className="btn btn-ghost btn-sm"
                          onClick={async () => {
                            if (window.confirm(`Delete ${u.name}? This will remove them from the database.`)) {
                              try {
                                const deleteUserMutation = `
                                  mutation DeleteUser($input: DeleteUserInput!) {
                                    deleteUser(input: $input) {
                                      id
                                    }
                                  }
                                `;
                                
                                await client.graphql({
                                  query: deleteUserMutation,
                                  variables: {
                                    input: { id: u.id }
                                  }
                                });
                                
                                // Remove from local state
                                setAllUsers(allUsers.filter(user => user.id !== u.id));
                                alert(`${u.name} deleted successfully`);
                              } catch (error) {
                                alert('Failed to delete user');
                              }
                            }
                          }}
                          style={{color: palette.red}}
                        >
                          🗑️ Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                
                {allUsers.length === 0 && (
                  <div style={{
                    textAlign: 'center',
                    padding: '60px 20px',
                    color: palette.textMuted
                  }}>
                    <div style={{fontSize: '48px', marginBottom: '16px'}}>👥</div>
                    <div style={{fontSize: '18px', fontWeight: '600', marginBottom: '8px', color: palette.text}}>
                      No users yet
                    </div>
                    <div style={{fontSize: '14px'}}>
                      Users will appear here when they sign up or are invited
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
          
          {/* ROSTER */}
          {tab === "roster" && (
            <>
              <div className="page-header">
                <div>
                  <div className="page-title">Roster</div>
                  <div className="page-sub">{roster.length} players · {activeCount} active</div>
                </div>
                <div style={{display:"flex", gap:"8px", alignItems:"center"}}>
                  {(isOwner || isTeamManager || isCoach) && (
                    <button className="btn btn-ghost" onClick={() => setShowInvite(true)}>
                      ✉️ Invite
                    </button>
                  )}
                  {isOwner && (
                    <button className="btn btn-ghost" onClick={() => { setTeamForCoachAssignment(currentTeam); setShowAssignCoach(true); }}>
                      👤 Manage Coaches
                    </button>
                  )}
                  {canManagePlayers && <button className="btn btn-primary" onClick={() => setShowAddPlayer(true)}>+ Add Player</button>}
                </div>
              </div>
              <div className="content">
                {/* Coaching Staff Section */}
                {teamCoachList.length > 0 && (
                  <div className="card" style={{marginBottom:"20px", padding:"16px 20px"}}>
                    <div style={{fontSize:"12px", fontWeight:"700", textTransform:"uppercase", letterSpacing:"1px", color: palette.textMuted, marginBottom:"12px"}}>
                      Coaching Staff
                    </div>
                    <div style={{display:"flex", gap:"12px", flexWrap:"wrap"}}>
                      {teamCoachList.map(coach => (
                        <div key={coach.id} style={{
                          display:"flex", alignItems:"center", gap:"10px",
                          background: palette.surfaceAlt, border:`1px solid ${palette.border}`,
                          borderRadius:"8px", padding:"8px 12px"
                        }}>
                          <div style={{
                            width:"32px", height:"32px", borderRadius:"50%",
                            background: coach.role === 'team_manager' ? palette.accent : palette.yellow,
                            display:"flex", alignItems:"center", justifyContent:"center",
                            fontSize:"12px", fontWeight:"700", color:"white"
                          }}>
                            {coach.avatar}
                          </div>
                          <div>
                            <div style={{fontSize:"13px", fontWeight:"600"}}>{coach.name}</div>
                            <div style={{fontSize:"11px", color: palette.textMuted, textTransform:"capitalize"}}>
                              {coach.role === 'team_manager' ? 'Team Manager' : 'Coach'}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                <div className="search-bar">
                  <span>🔍</span>
                  <input
                    placeholder="Search players by name or position..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                  />
                </div>
                <div className="card">
                  <div style={{overflowX:"auto"}} className="roster-table-wrapper">
                    <table className="roster-table">
                      <thead>
                        <tr>
                          <th>Headshot</th>
                          <th>#</th>
                          <th>Player Name</th>
                          <th>Position</th>
                          <th>Status</th>
                          <th>Parent 1</th>
                          <th>Parent 2</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredRoster.map(player => (
                          <tr key={player.id}>
                            <td>
                              <div style={{
                                width:"50px", height:"50px", borderRadius:"8px", overflow:"hidden",
                                background: headshotUrls[player.id] ? `url(${headshotUrls[player.id]}) center/cover` : getAvatarColor(player.id),
                                display:"flex", alignItems:"center", justifyContent:"center",
                                fontSize:"18px", fontWeight:"700", color:"white", flexShrink:0
                              }}>
                                {!headshotUrls[player.id] && player.avatar}
                              </div>
                            </td>
                            <td><div className="number-badge">#{player.number}</div></td>
                            <td>
                              <div style={{fontWeight:"600", fontSize:"15px"}}>{player.name}</div>
                            </td>
                            <td><span className="position-badge">{player.position}</span></td>
                            <td>
                              <span>
                                <span className={`status-dot status-${player.status}`}></span>
                                <span style={{fontSize:"13px", textTransform:"capitalize"}}>{player.status}</span>
                              </span>
                            </td>
                            <td>
                              {player.parent1Name ? (
                                <div style={{position:"relative"}}>
                                  <div
                                    onClick={() => setExpandedParent(expandedParent === `${player.id}-parent1` ? null : `${player.id}-parent1`)}
                                    style={{
                                      cursor:"pointer", padding:"6px 10px", borderRadius:"6px",
                                      background: expandedParent === `${player.id}-parent1` ? palette.accentSoft : palette.surfaceAlt,
                                      border: `1px solid ${expandedParent === `${player.id}-parent1` ? palette.accent : palette.border}`,
                                      fontSize:"13px", fontWeight:"500", transition:"all 0.15s"
                                    }}
                                  >
                                    {player.parent1Name} {expandedParent === `${player.id}-parent1` ? "▼" : "▶"}
                                  </div>
                                  {expandedParent === `${player.id}-parent1` && (
                                    <div style={{
                                      position:"absolute", top:"100%", left:0, marginTop:"4px",
                                      background: palette.surface, border:`1px solid ${palette.border}`,
                                      borderRadius:"8px", padding:"10px 12px", minWidth:"200px",
                                      boxShadow:"0 4px 12px rgba(0,0,0,0.3)", zIndex:10
                                    }}>
                                      <div style={{fontSize:"11px", color: palette.textMuted, marginBottom:"4px"}}>📞 Phone</div>
                                      <div style={{fontSize:"13px", fontWeight:"500", marginBottom:"8px"}}>{formatPhone(player.parent1Phone)}</div>
                                      <div style={{fontSize:"11px", color: palette.textMuted, marginBottom:"4px"}}>📧 Email</div>
                                      <div style={{fontSize:"13px", fontWeight:"500"}}>{player.parent1Email}</div>
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <span style={{color: palette.textDim, fontSize:"12px"}}>—</span>
                              )}
                            </td>
                            <td>
                              {player.parent2Name ? (
                                <div style={{position:"relative"}}>
                                  <div
                                    onClick={() => setExpandedParent(expandedParent === `${player.id}-parent2` ? null : `${player.id}-parent2`)}
                                    style={{
                                      cursor:"pointer", padding:"6px 10px", borderRadius:"6px",
                                      background: expandedParent === `${player.id}-parent2` ? palette.accentSoft : palette.surfaceAlt,
                                      border: `1px solid ${expandedParent === `${player.id}-parent2` ? palette.accent : palette.border}`,
                                      fontSize:"13px", fontWeight:"500", transition:"all 0.15s"
                                    }}
                                  >
                                    {player.parent2Name} {expandedParent === `${player.id}-parent2` ? "▼" : "▶"}
                                  </div>
                                  {expandedParent === `${player.id}-parent2` && (
                                    <div style={{
                                      position:"absolute", top:"100%", left:0, marginTop:"4px",
                                      background: palette.surface, border:`1px solid ${palette.border}`,
                                      borderRadius:"8px", padding:"10px 12px", minWidth:"200px",
                                      boxShadow:"0 4px 12px rgba(0,0,0,0.3)", zIndex:10
                                    }}>
                                      <div style={{fontSize:"11px", color: palette.textMuted, marginBottom:"4px"}}>📞 Phone</div>
                                      <div style={{fontSize:"13px", fontWeight:"500", marginBottom:"8px"}}>{formatPhone(player.parent2Phone)}</div>
                                      <div style={{fontSize:"11px", color: palette.textMuted, marginBottom:"4px"}}>📧 Email</div>
                                      <div style={{fontSize:"13px", fontWeight:"500"}}>{player.parent2Email}</div>
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <span style={{color: palette.textDim, fontSize:"12px"}}>—</span>
                              )}
                            </td>
                            <td>
                              {(canManagePlayers || (isParent && currentUser.playerIds?.includes(player.id))) && (
                                <div style={{display:"flex", gap:"6px"}}>
                                  <button
                                    className="btn btn-ghost btn-sm"
                                    onClick={() => openEditPlayer(player)}
                                    title="Edit player"
                                  >✏️</button>
                                  {canManagePlayers && (
                                    <button
                                      className="btn btn-ghost btn-sm"
                                      onClick={() => deletePlayer(player.id)}
                                      title="Remove player"
                                    >✕</button>
                                  )}
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  {/* Mobile Card View (shown on mobile, hidden on desktop) */}
                  <div style={{display:'none', background: palette.surface, borderRadius: '8px', overflow: 'hidden'}} className="roster-mobile-view">
                    {filteredRoster.map(player => {
                      const isExpanded = expandedPlayerCard === player.id;
                      return (
                        <div 
                          key={player.id} 
                          className="player-card-mobile"
                          onClick={() => setExpandedPlayerCard(isExpanded ? null : player.id)}
                        >
                          {/* Collapsed View - Always Visible */}
                          <div className="player-card-mobile-header">
                            <div 
                              className="player-card-mobile-avatar"
                              style={{
                                background: headshotUrls[player.id] ? `url(${headshotUrls[player.id]}) center/cover` : getAvatarColor(player.id)
                              }}
                            >
                              {!headshotUrls[player.id] && player.avatar}
                            </div>
                            <div className="player-card-mobile-info">
                              <div className="player-card-mobile-name">
                                <span className="player-name-text">{player.name}</span>
                                <span className="player-number">#{player.number || '—'}</span>
                              </div>
                            </div>
                            <div style={{
                              fontSize: '16px',
                              color: palette.textMuted,
                              transition: 'transform 0.2s',
                              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                              flexShrink: 0
                            }}>
                              ▼
                            </div>
                          </div>
                          
                          {/* Expanded View - Only When Tapped */}
                          {isExpanded && (
                            <div 
                              style={{
                                paddingTop: '12px',
                                marginTop: '12px',
                                borderTop: `1px solid ${palette.border}`,
                                animation: 'slideDown 0.2s ease-out'
                              }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              {/* Player Info */}
                              <div style={{marginBottom: '12px'}}>
                                <div style={{fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', color: palette.textMuted, marginBottom: '8px'}}>
                                  Player Info
                                </div>
                                <div style={{fontSize: '13px', color: palette.text, marginBottom: '4px'}}>
                                  <span style={{color: palette.textMuted}}>Position:</span> {player.position}
                                </div>
                                <div style={{fontSize: '13px', color: palette.text, marginBottom: '4px'}}>
                                  <span style={{color: palette.textMuted}}>Status:</span> {player.status}
                                </div>
                                {player.email && (isSuperAdmin || isOwner || isTeamManager || isCoach ||
                                  (isParent && currentUser.playerIds?.includes(player.id))) && (
                                  <div style={{fontSize: '13px', color: palette.text, marginBottom: '4px'}}>
                                    <span style={{color: palette.textMuted}}>Email:</span> {player.email}
                                  </div>
                                )}
                                {player.phone && (
                                  <div style={{fontSize: '13px', color: palette.text, marginBottom: '4px'}}>
                                    <span style={{color: palette.textMuted}}>Phone:</span> {player.phone}
                                  </div>
                                )}
                              </div>

                              {/* Parent 1 Info */}
                              {player.parent1Name && (
                                <div style={{marginBottom: '12px'}}>
                                  <div style={{fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', color: palette.textMuted, marginBottom: '8px'}}>
                                    Parent 1
                                  </div>
                                  <div style={{fontSize: '13px', color: palette.text, marginBottom: '4px'}}>
                                    <span style={{color: palette.textMuted}}>Name:</span> {player.parent1Name}
                                  </div>
                                  {player.parent1Phone && (
                                    <div style={{fontSize: '13px', color: palette.text, marginBottom: '4px'}}>
                                      <span style={{color: palette.textMuted}}>Phone:</span> <a href={`tel:${player.parent1Phone}`} style={{color: palette.accent, textDecoration: 'none'}}>{formatPhone(player.parent1Phone)}</a>
                                    </div>
                                  )}
                                  {player.parent1Email && (
                                    <div style={{fontSize: '13px', color: palette.text, marginBottom: '4px'}}>
                                      <span style={{color: palette.textMuted}}>Email:</span> <a href={`mailto:${player.parent1Email}`} style={{color: palette.accent, textDecoration: 'none'}}>{player.parent1Email}</a>
                                    </div>
                                  )}
                                </div>
                              )}

                              {/* Parent 2 Info */}
                              {player.parent2Name && (
                                <div style={{marginBottom: '12px'}}>
                                  <div style={{fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', color: palette.textMuted, marginBottom: '8px'}}>
                                    Parent 2
                                  </div>
                                  <div style={{fontSize: '13px', color: palette.text, marginBottom: '4px'}}>
                                    <span style={{color: palette.textMuted}}>Name:</span> {player.parent2Name}
                                  </div>
                                  {player.parent2Phone && (
                                    <div style={{fontSize: '13px', color: palette.text, marginBottom: '4px'}}>
                                      <span style={{color: palette.textMuted}}>Phone:</span> <a href={`tel:${player.parent2Phone}`} style={{color: palette.accent, textDecoration: 'none'}}>{formatPhone(player.parent2Phone)}</a>
                                    </div>
                                  )}
                                  {player.parent2Email && (
                                    <div style={{fontSize: '13px', color: palette.text, marginBottom: '4px'}}>
                                      <span style={{color: palette.textMuted}}>Email:</span> <a href={`mailto:${player.parent2Email}`} style={{color: palette.accent, textDecoration: 'none'}}>{player.parent2Email}</a>
                                    </div>
                                  )}
                                </div>
                              )}

                              {/* Action Buttons */}
                              {(canManagePlayers || (isParent && currentUser.playerIds?.includes(player.id))) && (
                                <div className="player-card-mobile-actions">
                                  <button
                                    className="btn btn-ghost btn-sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openEditPlayer(player);
                                    }}
                                  >
                                    ✏️ Edit
                                  </button>
                                  {canManagePlayers && (
                                    <button
                                      className="btn btn-ghost btn-sm"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        deletePlayer(player.id);
                                      }}
                                      style={{color: palette.red}}
                                    >
                                      ✕ Remove
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  
                  {filteredRoster.length === 0 && (
                    <div className="loading-container" style={{padding: '60px 20px'}}>
                      <div style={{fontSize: '48px', marginBottom: '16px'}}>👥</div>
                      <div style={{fontSize: '18px', fontWeight: '700', marginBottom: '8px', color: palette.text}}>No Players Yet</div>
                      <div style={{fontSize: '14px', marginBottom: '24px', color: palette.textMuted}}>
                        {search ? 'No players match your search' : 'Get started by adding your first team member'}
                      </div>
                      {canManagePlayers && !search && (
                        <button className="btn btn-primary" onClick={() => setShowAddPlayer(true)}>
                          + Add Player
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          {/* SCHEDULE */}
          {tab === "schedule" && (
            <>
              <div className="page-header">
                <div>
                  <div className="page-title">Schedule</div>
                  <div className="page-sub">{events.length} total events</div>
                </div>
                <div className="header-actions">
                  <button className="btn btn-ghost" onClick={() => setShowSubscribe(true)}>
                    📆 Subscribe to Calendar
                  </button>
                  {canManageEvents && <button className="btn btn-primary" onClick={() => setShowAddEvent(true)}>+ Add Event</button>}
                </div>
              </div>
              <div className="content">
                <div className="schedule-filters">
                  {["all","game","practice","past"].map(f => (
                    <button
                      key={f}
                      className={`filter-btn${scheduleFilter === f ? " active" : ""}`}
                      onClick={() => setScheduleFilter(f)}
                    >
                      {f === "all" ? "All Events" : f === "game" ? "🏆 Games" : f === "practice" ? "🏃 Practices" : "📜 Past Events"}
                    </button>
                  ))}
                </div>
                {filteredEvents.map(event => {
                  const d = formatDate(event.date);
                  // Parse in local timezone
                  const [year, month, day] = event.date.split('-').map(Number);
                  const eventDate = new Date(year, month - 1, day);
                  const isPast = eventDate < today;
                  
                  // Get attendance for this event
                  const attendance = getAttendance(event);
                  const rosterPlayerIds = roster.map(p => p.id);
                  const validAttendance = {};
                  rosterPlayerIds.forEach(playerId => {
                    if (attendance[playerId]) {
                      validAttendance[playerId] = attendance[playerId];
                    }
                  });
                  const confirmed = Object.values(validAttendance).filter(v => v === "yes").length;
                  const declined = Object.values(validAttendance).filter(v => v === "no").length;
                  const maybe = Object.values(validAttendance).filter(v => v === "maybe").length;
                  const responded = Object.keys(validAttendance).length;
                  const noResponse = Math.max(0, roster.length - responded);
                  
                  return (
                    <div 
                      key={event.id} 
                      className="card"
                      style={{
                        marginBottom:"8px",
                        padding:"10px 14px",
                        display:"flex",
                        alignItems:"center",
                        gap:"10px",
                        opacity: isPast ? 0.6 : 1,
                        cursor: "pointer"
                      }}
                      onClick={() => { setSelectedEvent(event); setTab("attendance"); }}
                    >
                      {/* Date badge */}
                      <div style={{
                        width:"36px", height:"36px", borderRadius:"6px",
                        background: event.type === "game" ? "rgba(59,130,246,0.15)" : "rgba(34,197,94,0.15)",
                        display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", flexShrink:0, gap:"1px"
                      }}>
                        <div style={{fontSize:"7px", textTransform:"uppercase", letterSpacing:"0.3px", color: event.type === "game" ? palette.blue : palette.green, fontWeight:"600", textAlign:"center", width:"100%", lineHeight:"1"}}>{d.month}</div>
                        <div style={{fontFamily:"'Barlow Condensed',sans-serif", fontSize:"16px", fontWeight:"900", lineHeight:"1", color: event.type === "game" ? palette.blue : palette.green, textAlign:"center", width:"100%"}}>{d.day}</div>
                      </div>
                      
                      {/* Event info */}
                      <div style={{flex:1, minWidth:0}}>
                        <div style={{display:"flex", alignItems:"center", gap:"6px", marginBottom:"2px"}}>
                          <div style={{fontFamily:"'Barlow Condensed',sans-serif", fontSize:"14px", fontWeight:"900", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>{event.title}</div>
                          <span className={`event-type-badge ${event.type === "game" ? "type-game" : "type-practice"}`} style={{fontSize:"7px", padding:"2px 5px"}}>{event.type}</span>
                        </div>
                        <div style={{fontSize:"11px", color: palette.textMuted, display:"flex", gap:"8px", alignItems:"center", flexWrap:"wrap"}}>
                          {event.time && <span>⏰ {event.time}{event.endTime && `-${event.endTime}`}</span>}
                          {event.location && <span>📍 {event.location}</span>}
                          {event.homeAway && event.type === "game" && <span>{event.homeAway === "home" ? "🏠" : "✈️"}</span>}
                          {!isPast && (
                            <span style={{display:"flex", gap:"4px", marginLeft:"4px", alignItems:"center"}}>
                              {confirmed > 0 && <span style={{color: palette.green, fontWeight:"600"}}>{confirmed}✓</span>}
                              {maybe > 0 && <span style={{color: palette.yellow, fontWeight:"600"}}>{maybe}?</span>}
                              {declined > 0 && <span style={{color: palette.red, fontWeight:"600"}}>{declined}✗</span>}
                              {noResponse > 0 && <span style={{color: palette.textDim, fontWeight:"600"}}>{noResponse}○</span>}
                            </span>
                          )}
                          {/* Volunteer info - visible to coaches and managers */}
                          {(canManageEvents || isOwner) && event.volunteerSlotsTotal > 0 && (
                            <span style={{
                              color: (event.volunteerSignups?.length || 0) >= event.volunteerSlotsTotal ? palette.green : palette.yellow,
                              fontWeight:"600",
                              fontSize:"11px"
                            }}>
                              🙋 {event.volunteerSignups?.length || 0}/{event.volunteerSlotsTotal}
                              {event.volunteerSignups?.length > 0 && ` · ${event.volunteerSignups.join(', ')}`}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {/* Right side - result or buttons */}
                      {event.result ? (
                        <div style={{display:"flex", gap:"4px", alignItems:"center"}}>
                          <div className={`result-badge ${event.result.won ? "result-win" : "result-loss"}`} style={{fontSize:"12px", padding:"3px 8px"}}>
                            {event.result.score}
                          </div>
                          {canManageEvents && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteEvent(event.id);
                              }}
                              style={{
                                fontSize:"11px", color: palette.textMuted,
                                padding:"3px 6px", borderRadius:"4px", border:`1px solid ${palette.border}`,
                                background: palette.surfaceAlt,
                                transition:"all 0.15s", cursor:"pointer"
                              }}
                              onMouseOver={e => { e.currentTarget.style.color = palette.red; e.currentTarget.style.borderColor = palette.red; }}
                              onMouseOut={e => { e.currentTarget.style.color = palette.textMuted; e.currentTarget.style.borderColor = palette.border; }}
                            >
                              🗑️
                            </button>
                          )}
                        </div>
                      ) : canManageEvents ? (
                        <div style={{display:"flex", gap:"3px"}}>
                          {!isPast && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                openEditEvent(event);
                              }}
                              style={{
                                fontSize:"11px", color: palette.textMuted,
                                padding:"3px 6px", borderRadius:"4px", border:`1px solid ${palette.border}`,
                                background: palette.surfaceAlt,
                                transition:"all 0.15s", cursor:"pointer"
                              }}
                              onMouseOver={e => { e.currentTarget.style.color = palette.accent; e.currentTarget.style.borderColor = palette.accent; }}
                              onMouseOut={e => { e.currentTarget.style.color = palette.textMuted; e.currentTarget.style.borderColor = palette.border; }}
                            >
                              ✏️
                            </button>
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteEvent(event.id);
                            }}
                            style={{
                              fontSize:"11px", color: palette.textMuted,
                              padding:"3px 6px", borderRadius:"4px", border:`1px solid ${palette.border}`,
                              background: palette.surfaceAlt,
                              transition:"all 0.15s", cursor:"pointer"
                            }}
                            onMouseOver={e => { e.currentTarget.style.color = palette.red; e.currentTarget.style.borderColor = palette.red; }}
                            onMouseOut={e => { e.currentTarget.style.color = palette.textMuted; e.currentTarget.style.borderColor = palette.border; }}
                          >
                            🗑️
                          </button>
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/* ATTENDANCE */}
          {tab === "attendance" && (
            <>
              <div className="page-header">
                <div>
                  <div className="page-title">Attendance</div>
                  <div className="page-sub">Track player availability for upcoming events</div>
                </div>
              </div>
              <div className="content">
                {upcomingEvents.length === 0 ? (
                  <div style={{textAlign:"center", padding:"60px", color: palette.textMuted}}>
                    <div style={{fontSize:"48px", marginBottom:"12px"}}>📅</div>
                    <div style={{fontSize:"18px", fontFamily:"'Barlow Condensed', sans-serif", fontWeight:"700", marginBottom:"8px"}}>No Upcoming Events</div>
                    <div style={{fontSize:"14px"}}>Add an event to start tracking attendance</div>
                  </div>
                ) : (
                  <div style={{display:"flex", flexDirection:"column", gap:"20px"}}>
                    {upcomingEvents.map(event => {
                      const d = formatDate(event.date);
                      const attendance = getAttendance(event);
                      
                      // Only count attendance for current roster players
                      const rosterPlayerIds = roster.map(p => p.id);
                      const validAttendance = {};
                      rosterPlayerIds.forEach(playerId => {
                        if (attendance[playerId]) {
                          validAttendance[playerId] = attendance[playerId];
                        }
                      });
                      
                      const confirmed = Object.values(validAttendance).filter(v => v === "yes").length;
                      const declined = Object.values(validAttendance).filter(v => v === "no").length;
                      const maybe = Object.values(validAttendance).filter(v => v === "maybe").length;
                      
                      const playersToShow = roster.filter(player => {
                        // Managers/coaches/owners see everyone
                        if (canManageEvents || isOwner) return true;
                        // Parents and players only see their own linked players
                        if (currentUser.playerIds?.includes(player.id)) return true;
                        return false;
                      });
                      
                      return (
                        <div key={event.id} className="card" style={{padding:"20px 24px"}}>
                          {/* Event Header */}
                          <div style={{display:"flex", alignItems:"center", gap:"16px", marginBottom:"20px", paddingBottom:"16px", borderBottom:`1px solid ${palette.border}`}}>
                            <div style={{
                              width:"56px", height:"56px", borderRadius:"12px",
                              background: event.type === "game" ? "rgba(59,130,246,0.15)" : "rgba(34,197,94,0.15)",
                              display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", flexShrink:0
                            }}>
                              <div style={{fontSize:"9px", textTransform:"uppercase", letterSpacing:"1px", color: event.type === "game" ? palette.blue : palette.green, fontWeight:"600"}}>{d.month}</div>
                              <div style={{fontFamily:"'Barlow Condensed',sans-serif", fontSize:"26px", fontWeight:"900", lineHeight:1, color: event.type === "game" ? palette.blue : palette.green}}>{d.day}</div>
                            </div>
                            <div style={{flex:1}}>
                              <div style={{display:"flex", alignItems:"center", gap:"10px", marginBottom:"6px"}}>
                                <div style={{fontFamily:"'Barlow Condensed', sans-serif", fontSize:"20px", fontWeight:"900"}}>{event.title}</div>
                                <span className={`event-type-badge ${event.type === "game" ? "type-game" : "type-practice"}`}>{event.type}</span>
                              </div>
                              <div style={{display:"flex", flexDirection:"column", gap:"4px"}}>
                                {event.time && (
                                  <div style={{fontSize:"15px", color: palette.text, fontWeight:"500"}}>
                                    ⏰ {event.time}
                                  </div>
                                )}
                                {event.location && (
                                  <div style={{fontSize:"15px", color: palette.text, fontWeight:"500"}}>
                                    📍 {event.location}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Player Attendance List */}
                          <div style={{display:"flex", flexDirection:"column", gap:"6px"}}>
                            {playersToShow.map(player => {
                              const status = attendance[player.id];
                              // Anyone can mark attendance for their own linked players, regardless of role
                              const hasLinkedPlayer = currentUser.playerIds?.includes(player.id);
                              const canEdit = canManageEvents || hasLinkedPlayer;
                              
                              return (
                                <div key={player.id} style={{
                                  display:"flex", alignItems:"center", gap:"10px",
                                  background: palette.surfaceAlt, borderRadius:"6px", padding:"8px 12px"
                                }} className="attendance-player-row">
                                  <div className="player-avatar" style={{background: getAvatarColor(player.id), width:"26px", height:"26px", fontSize:"10px"}}>
                                    {player.avatar}
                                  </div>
                                  <div style={{flex:1, minWidth:0}}>
                                    <div style={{fontSize:"13px", fontWeight:"600", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>{player.name}</div>
                                  </div>
                                  <div style={{display:"flex", gap:"4px", flexShrink:0}} className="attendance-buttons">
                                    {["yes","maybe","no"].map(s => (
                                      <button
                                        key={s}
                                        className={`att-btn att-${s}${status === s ? " selected" : ""}`}
                                        onClick={() => canEdit && setAttendanceForEvent(event.id, player.id, s)}
                                        style={{
                                          fontWeight: status === s ? "700" : "500", 
                                          opacity: canEdit ? 1 : 0.5, 
                                          cursor: canEdit ? "pointer" : "not-allowed",
                                          padding: "4px 10px",
                                          fontSize: "11px"
                                        }}
                                        disabled={!canEdit}
                                      >
                                        {s === "yes" ? "✓" : s === "maybe" ? "?" : "✗"}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              );
                            })}
                          </div>

                          {/* Summary Stats */}
                          {(canManageEvents) && (
                            <div style={{marginTop:"16px", paddingTop:"16px", borderTop:`1px solid ${palette.border}`, display:"flex", gap:"20px", fontSize:"15px", justifyContent:"center"}}>
                              <div style={{display:"flex", alignItems:"center", gap:"6px"}}>
                                <span style={{fontWeight:"700", color: palette.green, fontSize:"18px"}}>{confirmed}</span>
                                <span style={{color: palette.green, fontSize:"14px"}}>✓</span>
                              </div>
                              <div style={{display:"flex", alignItems:"center", gap:"6px"}}>
                                <span style={{fontWeight:"700", color: palette.yellow, fontSize:"18px"}}>{maybe}</span>
                                <span style={{color: palette.yellow, fontSize:"14px"}}>?</span>
                              </div>
                              <div style={{display:"flex", alignItems:"center", gap:"6px"}}>
                                <span style={{fontWeight:"700", color: palette.red, fontSize:"18px"}}>{declined}</span>
                                <span style={{color: palette.red, fontSize:"14px"}}>✗</span>
                              </div>
                              <div style={{display:"flex", alignItems:"center", gap:"6px"}}>
                                <span style={{fontWeight:"700", color: palette.textDim, fontSize:"18px"}}>{Math.max(0, roster.length - Object.keys(validAttendance).length)}</span>
                                <span style={{color: palette.textDim, fontSize:"14px"}}>○</span>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}

          {/* MESSAGES */}
          {tab === "messages" && (
            <>
              <div className="page-header">
                <div>
                  <div className="page-title">📧 Email Team</div>
                  <div className="page-sub">Quick email helpers for your team</div>
                </div>
              </div>

              <div style={{padding:"20px", maxWidth:"700px"}}>
                {/* Email All Parents */}
                <div style={{
                  background: palette.surfaceAlt,
                  border: `1px solid ${palette.border}`,
                  borderRadius: "12px",
                  padding: "20px",
                  marginBottom: "16px"
                }}>
                  <div style={{display:"flex", alignItems:"center", gap:"12px", marginBottom:"12px"}}>
                    <div style={{
                      width:"48px", height:"48px", borderRadius:"10px",
                      background: "rgba(59,130,246,0.15)",
                      display:"flex", alignItems:"center", justifyContent:"center",
                      fontSize:"24px"
                    }}>👨‍👩‍👧‍👦</div>
                    <div style={{flex:1}}>
                      <div style={{fontSize:"16px", fontWeight:"700", marginBottom:"4px"}}>
                        Email Team Parents
                      </div>
                      <div style={{fontSize:"13px", color: palette.textMuted}}>
                        Copy all parent emails to clipboard (excludes coaches)
                      </div>
                    </div>
                  </div>
                  <button
                    className="btn btn-primary"
                    style={{width:"100%"}}
                    onClick={() => {
                      const parentEmails = roster
                        .flatMap(player => [player.parent1Email, player.parent2Email])
                        .filter(email => email && email.trim());
                      
                      const uniqueEmails = [...new Set(parentEmails)];
                      
                      if (uniqueEmails.length === 0) {
                        showToast('No parent emails found', 'error');
                        return;
                      }
                      
                      const emailList = uniqueEmails.join(', ');
                      navigator.clipboard.writeText(emailList);
                      showToast(`Copied ${uniqueEmails.length} parent emails to clipboard!`, 'success');
                    }}
                  >
                    📋 Copy {roster.flatMap(p => [p.parent1Email, p.parent2Email]).filter(e => e && e.trim()).length > 0 
                      ? [...new Set(roster.flatMap(p => [p.parent1Email, p.parent2Email]).filter(e => e && e.trim()))].length 
                      : 0} Parent Email{[...new Set(roster.flatMap(p => [p.parent1Email, p.parent2Email]).filter(e => e && e.trim()))].length !== 1 ? 's' : ''}
                  </button>
                </div>

                {/* Email Parents + Coaches */}
                <div style={{
                  background: palette.surfaceAlt,
                  border: `1px solid ${palette.border}`,
                  borderRadius: "12px",
                  padding: "20px",
                  marginBottom: "16px"
                }}>
                  <div style={{display:"flex", alignItems:"center", gap:"12px", marginBottom:"12px"}}>
                    <div style={{
                      width:"48px", height:"48px", borderRadius:"10px",
                      background: "rgba(34,197,94,0.15)",
                      display:"flex", alignItems:"center", justifyContent:"center",
                      fontSize:"24px"
                    }}>👥</div>
                    <div style={{flex:1}}>
                      <div style={{fontSize:"16px", fontWeight:"700", marginBottom:"4px"}}>
                        Email Team + Coaches
                      </div>
                      <div style={{fontSize:"13px", color: palette.textMuted}}>
                        Copy all parent and coach emails to clipboard
                      </div>
                    </div>
                  </div>
                  <button
                    className="btn btn-primary"
                    style={{width:"100%"}}
                    onClick={() => {
                      const parentEmails = roster
                        .flatMap(player => [player.parent1Email, player.parent2Email])
                        .filter(email => email && email.trim());
                      
                      const coachEmails = teamCoachList.map(coach => coach.email).filter(email => email && email.trim());
                      
                      const allEmails = [...parentEmails, ...coachEmails];
                      const uniqueEmails = [...new Set(allEmails)];
                      
                      if (uniqueEmails.length === 0) {
                        showToast('No emails found', 'error');
                        return;
                      }
                      
                      const emailList = uniqueEmails.join(', ');
                      navigator.clipboard.writeText(emailList);
                      showToast(`Copied ${uniqueEmails.length} emails to clipboard!`, 'success');
                    }}
                  >
                    📋 Copy {(() => {
                      const parentEmails = roster.flatMap(p => [p.parent1Email, p.parent2Email]).filter(e => e && e.trim());
                      const coachEmails = teamCoachList.map(c => c.email).filter(e => e && e.trim());
                      const total = [...new Set([...parentEmails, ...coachEmails])].length;
                      return total;
                    })()} Total Email{(() => {
                      const parentEmails = roster.flatMap(p => [p.parent1Email, p.parent2Email]).filter(e => e && e.trim());
                      const coachEmails = teamCoachList.map(c => c.email).filter(e => e && e.trim());
                      const total = [...new Set([...parentEmails, ...coachEmails])].length;
                      return total !== 1 ? 's' : '';
                    })()}
                  </button>
                </div>

                {/* Email Specific People */}
                <div style={{
                  background: palette.surfaceAlt,
                  border: `1px solid ${palette.border}`,
                  borderRadius: "12px",
                  padding: "20px"
                }}>
                  <div style={{display:"flex", alignItems:"center", gap:"12px", marginBottom:"12px"}}>
                    <div style={{
                      width:"48px", height:"48px", borderRadius:"10px",
                      background: "rgba(245,158,11,0.15)",
                      display:"flex", alignItems:"center", justifyContent:"center",
                      fontSize:"24px"
                    }}>✉️</div>
                    <div style={{flex:1}}>
                      <div style={{fontSize:"16px", fontWeight:"700", marginBottom:"4px"}}>
                        Email Specific People
                      </div>
                      <div style={{fontSize:"13px", color: palette.textMuted}}>
                        Select individual parents or coaches to email
                      </div>
                    </div>
                  </div>
                  <button
                    className="btn btn-secondary"
                    style={{width:"100%"}}
                    onClick={() => setShowEmailPicker(true)}
                  >
                    📝 Select Recipients
                  </button>
                </div>

                {/* Info Box */}
                <div style={{
                  marginTop:"24px",
                  padding:"16px",
                  background: "rgba(59,130,246,0.08)",
                  border: "1px solid rgba(59,130,246,0.2)",
                  borderRadius:"10px",
                  fontSize:"13px",
                  color: palette.textMuted,
                  lineHeight:"1.6"
                }}>
                  💡 <strong style={{color: palette.text}}>Tip:</strong> After copying emails, paste them into the BCC field of your email app to protect everyone's privacy. Never put all emails in the TO or CC field!
                </div>
              </div>
            </>
          )}

          {/* FINANCIALS TAB */}
          {tab === "financials" && (isOwner || isTeamManager || isParent) && !isPlayer && (
            <>
              <div className="page-header">
                <div>
                  <div className="page-title">💰 Financials</div>
                  <div className="page-sub">{currentTeam?.name} — Account balances & transactions</div>
                </div>
                {isOwner && (
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => {
                      const url = prompt('Paste Google Sheet published URL:', currentTeam?.financialSheetUrl || '');
                      if (url !== null) {
                        const embedUrl = url.includes('/pub?')
                          ? url.replace('/pub?', '/pub?widget=true&')
                          : url;
                        client.graphql({
                          query: `mutation UpdateTeam($input: UpdateTeamInput!) { updateTeam(input: $input) { id financialSheetUrl } }`,
                          variables: { input: { id: currentTeam.id, financialSheetUrl: embedUrl } }
                        }).then(() => fetchClubData());
                      }
                    }}
                  >
                    ⚙️ Edit Sheet URL
                  </button>
                )}
              </div>

              {currentTeam?.financialSheetUrl ? (
                <div style={{padding:"0 20px 20px"}}>
                  <iframe
                    src={currentTeam.financialSheetUrl}
                    width="100%"
                    style={{
                      border: `1px solid ${palette.border}`,
                      borderRadius: "12px",
                      height: "calc(100vh - 200px)",
                      minHeight: "500px",
                      display: "block"
                    }}
                    title="Team Financials"
                  />
                </div>
              ) : (
                <div style={{padding:"40px 20px", textAlign:"center", color: palette.textMuted}}>
                  <div style={{fontSize:"48px", marginBottom:"16px"}}>📊</div>
                  <div style={{fontSize:"18px", fontWeight:"600", marginBottom:"8px", color: palette.text}}>No financial sheet linked yet</div>
                  {isOwner && (
                    <>
                      <div style={{marginBottom:"24px", fontSize:"14px"}}>
                        Link a Google Sheet to display financial data for this team.
                      </div>
                      <div style={{
                        background: palette.surfaceAlt,
                        border: `1px solid ${palette.border}`,
                        borderRadius:"12px",
                        padding:"20px",
                        maxWidth:"500px",
                        margin:"0 auto",
                        textAlign:"left"
                      }}>
                        <div style={{fontWeight:"600", marginBottom:"12px"}}>How to get the embed URL:</div>
                        <ol style={{margin:0, paddingLeft:"20px", lineHeight:"2", fontSize:"13px", color: palette.textMuted}}>
                          <li>Open your Google Sheet for <strong>{currentTeam?.name}</strong></li>
                          <li>Click <strong>File → Share → Publish to web</strong></li>
                          <li>Select <strong>Entire Document</strong> and <strong>Web page</strong></li>
                          <li>Click <strong>Publish</strong></li>
                          <li>Copy the link and paste it below</li>
                        </ol>
                        <button
                          className="btn btn-primary"
                          style={{marginTop:"16px", width:"100%"}}
                          onClick={() => {
                            const url = prompt('Paste Google Sheet published URL:');
                            if (url) {
                              const embedUrl = url.includes('/pub?')
                                ? url.replace('/pub?', '/pub?widget=true&')
                                : url;
                              client.graphql({
                                query: `mutation UpdateTeam($input: UpdateTeamInput!) { updateTeam(input: $input) { id financialSheetUrl } }`,
                                variables: { input: { id: currentTeam.id, financialSheetUrl: embedUrl } }
                              }).then(() => fetchClubData());
                            }
                          }}
                        >
                          + Link Google Sheet
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </>
          )}

          {/* VOLUNTEER TAB */}
          {tab === "volunteer" && (
            <>
              <div className="page-header">
                <div>
                  <div className="page-title">🙋 Volunteer Opportunities</div>
                  <div className="page-sub">Sign up to help at a younger team's practice</div>
                </div>
              </div>

              <div style={{padding:"0 20px 20px"}}>
                {volunteerEvents.length === 0 ? (
                  <div style={{textAlign:"center", padding:"60px 20px", color: palette.textMuted}}>
                    <div style={{fontSize:"48px", marginBottom:"16px"}}>🙋</div>
                    <div style={{fontSize:"18px", fontWeight:"600", color: palette.text, marginBottom:"8px"}}>No volunteer opportunities yet</div>
                    <div style={{fontSize:"14px"}}>Check back later for upcoming opportunities.</div>
                  </div>
                ) : (
                  volunteerEvents.map(event => {
                    const userName = currentUser.name || currentUser.email;
                    const isSignedUp = event.volunteerSignups?.includes(userName);
                    const spotsTotal = event.volunteerSlotsTotal || 0;
                    const signedUpCount = event.volunteerSignups?.length || 0;
                    const spotsLeft = Math.max(0, spotsTotal - signedUpCount);
                    const isFull = spotsLeft === 0 && !isSignedUp;

                    return (
                      <div key={event.id} className="card" style={{marginBottom:"16px", padding:"20px 24px"}}>
                        {/* Event Header */}
                        <div style={{display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:"12px"}}>
                          <div>
                            <div style={{fontSize:"16px", fontWeight:"700", color: palette.text, marginBottom:"2px"}}>
                              {event.teamName} — {event.title}
                            </div>
                            <div style={{fontSize:"13px", color: palette.textMuted}}>
                              {new Date(event.date + 'T12:00:00').toLocaleDateString('en-US', {weekday:'short', month:'short', day:'numeric'})}
                              {event.time && ` · ${event.time}`}
                              {event.location && ` · ${event.location}`}
                            </div>
                          </div>
                          <div style={{
                            fontSize:"12px", fontWeight:"600", padding:"4px 10px", borderRadius:"20px",
                            background: isFull ? palette.surfaceAlt : 'rgba(34,197,94,0.1)',
                            color: isFull ? palette.textMuted : palette.green,
                            whiteSpace:"nowrap"
                          }}>
                            {isFull ? 'Full' : `${spotsLeft} spot${spotsLeft !== 1 ? 's' : ''} left`}
                          </div>
                        </div>

                        {/* Signup List */}
                        <div style={{marginBottom:"16px"}}>
                          {Array.from({length: spotsTotal}).map((_, i) => {
                            const name = event.volunteerSignups?.[i];
                            return (
                              <div key={i} style={{
                                display:"flex", alignItems:"center", gap:"8px",
                                padding:"6px 0",
                                borderBottom: i < spotsTotal - 1 ? `1px solid ${palette.border}` : 'none',
                                fontSize:"13px"
                              }}>
                                {name ? (
                                  <>
                                    <span style={{color: palette.green, fontWeight:"700"}}>✓</span>
                                    <span style={{color: palette.text, fontWeight: name === userName ? '700' : '400'}}>
                                      {name}{name === userName ? ' (you)' : ''}
                                    </span>
                                  </>
                                ) : (
                                  <>
                                    <span style={{color: palette.border}}>○</span>
                                    <span style={{color: palette.textMuted}}>Open spot</span>
                                  </>
                                )}
                              </div>
                            );
                          })}
                        </div>

                        {/* Action Buttons */}
                        <div style={{display:"flex", gap:"8px", flexWrap:"wrap"}}>
                          {!isFull || isSignedUp ? (
                            <button
                              className={`btn ${isSignedUp ? 'btn-ghost' : 'btn-primary'}`}
                              onClick={() => toggleVolunteer(event)}
                              style={{flex:1}}
                            >
                              {isSignedUp ? '✕ Cancel Signup' : '🙋 Sign Up to Volunteer'}
                            </button>
                          ) : (
                            <button className="btn btn-ghost" disabled style={{flex:1, opacity:0.5}}>
                              Full
                            </button>
                          )}

                          {isSignedUp && (
                            <a
                              href={buildGoogleCalendarUrl(event)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="btn btn-ghost"
                              style={{textDecoration:"none"}}
                            >
                              📅 Add to Google Calendar
                            </a>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              {/* Volunteer History - only for players/parents */}
              {(isParent || isPlayer || currentUser.playerIds?.length > 0) && (() => {
                const userName = currentUser.name || currentUser.email;
                const today = new Date().toISOString().split('T')[0];

                // Show ALL events user signed up for (past and future)
                const history = (club?.teams?.items || [])
                  .flatMap(t => (t.events?.items || t.events || [])
                    .filter(e => e.volunteerSignups?.includes(userName))
                    .map(e => ({ ...e, teamName: t.name }))
                  )
                  .sort((a, b) => b.date.localeCompare(a.date));

                if (history.length === 0) return null;

                // Only count past events toward total hours
                const totalHours = history
                  .filter(e => e.date < today)
                  .reduce((sum, e) => sum + (calculateVolunteerHours(e) || 0), 0);

                return (
                  <div style={{padding:"0 20px 20px"}}>
                    <div style={{
                      background: palette.surfaceAlt,
                      border: `1px solid ${palette.border}`,
                      borderRadius:"12px",
                      padding:"20px"
                    }}>
                      <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"16px"}}>
                        <div style={{fontSize:"16px", fontWeight:"700", color: palette.text}}>📋 My Volunteer History</div>
                        {totalHours > 0 && (
                          <div style={{
                            background:'rgba(34,197,94,0.1)',
                            color: palette.green,
                            padding:"4px 12px",
                            borderRadius:"20px",
                            fontSize:"13px",
                            fontWeight:"700"
                          }}>
                            {totalHours.toFixed(1)} hrs total
                          </div>
                        )}
                      </div>
                      <div style={{display:"flex", flexDirection:"column"}}>
                        {history.map((event, i) => {
                          const hours = calculateVolunteerHours(event);
                          return (
                            <div key={event.id} style={{
                              display:"flex", justifyContent:"space-between", alignItems:"center",
                              padding:"10px 0",
                              borderBottom: i < history.length - 1 ? `1px solid ${palette.border}` : 'none',
                              fontSize:"13px"
                            }}>
                              <div>
                                <div style={{fontWeight:"600", color: palette.text}}>
                                  {event.teamName} — {event.title}
                                </div>
                                <div style={{color: palette.textMuted, fontSize:"12px"}}>
                                  {new Date(event.date + 'T12:00:00').toLocaleDateString('en-US', {weekday:'short', month:'short', day:'numeric'})}
                                  {event.time && event.endTime && ` · ${event.time} – ${event.endTime}`}
                                </div>
                              </div>
                              <div style={{
                                color: event.date >= today ? palette.blue : (hours ? palette.green : palette.textMuted),
                                fontWeight:"600",
                                whiteSpace:"nowrap",
                                marginLeft:"12px",
                                fontSize:"12px"
                              }}>
                                {event.date >= today ? '📅 Upcoming' : (hours ? `${hours} hr${hours !== 1 ? 's' : ''}` : '—')}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              })()}

            </>
          )}

        </main>
      </div>

      {/* ADD PLAYER MODAL */}
      {canManagePlayers && showAddPlayer && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowAddPlayer(false)}>
          <div className="modal" style={{width:"600px", maxHeight:"85vh", overflowY:"auto"}}>
            <div className="modal-title">Add New Player</div>
            
            <div className="form-group">
              <label className="form-label">Headshot Photo (optional)</label>
              <div style={{display:"flex", gap:"12px", alignItems:"center"}}>
                <div style={{
                  width:"80px", height:"80px", borderRadius:"10px", overflow:"hidden",
                  background: newPlayer.headshot ? `url(${newPlayer.headshot}) center/cover` : palette.surfaceAlt,
                  display:"flex", alignItems:"center", justifyContent:"center",
                  fontSize:"24px", fontWeight:"700", color: palette.textMuted, border:`2px solid ${palette.border}`
                }}>
                  {!newPlayer.headshot && "📷"}
                </div>
                <div style={{flex:1}}>
                  <input 
                    type="file" 
                    accept="image/*"
                    onChange={async (e) => {
                      const file = e.target.files[0];
                      if (file) {
                        // Show loading state (optional)
                        const uploadedUrl = await uploadHeadshot(file);
                        if (uploadedUrl) {
                          setNewPlayer({...newPlayer, headshot: uploadedUrl});
                        }
                      }
                    }}
                    style={{
                      fontSize:"13px", padding:"8px 12px", border:`1px solid ${palette.border}`,
                      borderRadius:"8px", background: palette.bg, color: palette.text,
                      cursor:"pointer", width:"100%", fontFamily:"'Inter', sans-serif"
                    }}
                  />
                  {newPlayer.headshot && (
                    <button 
                      className="btn btn-ghost btn-sm" 
                      onClick={() => setNewPlayer({...newPlayer, headshot: null})}
                      style={{marginTop:"6px"}}
                    >
                      Remove Photo
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Full Name *</label>
                <input className="form-input" placeholder="Alex Johnson" value={newPlayer.name} onChange={e => setNewPlayer({...newPlayer, name: e.target.value})} />
              </div>
              <div className="form-group">
                <label className="form-label">Jersey Number</label>
                <input className="form-input" type="number" placeholder="10" value={newPlayer.number} onChange={e => setNewPlayer({...newPlayer, number: e.target.value})} />
              </div>
            </div>
            
            <div className="form-group">
              <label className="form-label">Position</label>
              <select className="form-select" value={newPlayer.position} onChange={e => setNewPlayer({...newPlayer, position: e.target.value})}>
                {POSITIONS.map(p => <option key={p}>{p}</option>)}
              </select>
            </div>

            <div style={{borderTop:`1px solid ${palette.border}`, marginTop:"20px", paddingTop:"20px"}}>
              <div style={{fontSize:"14px", fontWeight:"700", marginBottom:"12px", color: palette.text}}>Parent 1 Information</div>
              <div className="form-group">
                <label className="form-label">Parent 1 Name</label>
                <input className="form-input" placeholder="Parent name" value={newPlayer.parent1Name || ""} onChange={e => setNewPlayer({...newPlayer, parent1Name: e.target.value})} />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Parent 1 Phone</label>
                  <input className="form-input" type="tel" placeholder="555-0100" value={newPlayer.parent1Phone || ""} onChange={e => setNewPlayer({...newPlayer, parent1Phone: e.target.value})} />
                </div>
                <div className="form-group">
                  <label className="form-label">Parent 1 Email</label>
                  <input className="form-input" type="email" placeholder="parent@email.com" value={newPlayer.parent1Email || ""} onChange={e => setNewPlayer({...newPlayer, parent1Email: e.target.value})} />
                </div>
              </div>
            </div>

            <div style={{borderTop:`1px solid ${palette.border}`, marginTop:"20px", paddingTop:"20px"}}>
              <div style={{fontSize:"14px", fontWeight:"700", marginBottom:"12px", color: palette.text}}>Parent 2 Information (optional)</div>
              <div className="form-group">
                <label className="form-label">Parent 2 Name</label>
                <input className="form-input" placeholder="Parent name" value={newPlayer.parent2Name || ""} onChange={e => setNewPlayer({...newPlayer, parent2Name: e.target.value})} />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Parent 2 Phone</label>
                  <input className="form-input" type="tel" placeholder="555-0100" value={newPlayer.parent2Phone || ""} onChange={e => setNewPlayer({...newPlayer, parent2Phone: e.target.value})} />
                </div>
                <div className="form-group">
                  <label className="form-label">Parent 2 Email</label>
                  <input className="form-input" type="email" placeholder="parent@email.com" value={newPlayer.parent2Email || ""} onChange={e => setNewPlayer({...newPlayer, parent2Email: e.target.value})} />
                </div>
              </div>
            </div>

            <div style={{borderTop:`1px solid ${palette.border}`, marginTop:"20px", paddingTop:"20px"}}>
              <div style={{fontSize:"14px", fontWeight:"700", marginBottom:"4px", color: palette.text}}>Player Login Email (optional)</div>
              <div style={{fontSize:"12px", color: palette.textMuted, marginBottom:"12px"}}>
                If the player has their own email, they can log into the app directly to mark availability.
              </div>
              <div className="form-group">
                <label className="form-label">Player Email</label>
                <input
                  className="form-input"
                  type="email"
                  placeholder="player@email.com"
                  value={newPlayer.email || ""}
                  onChange={e => setNewPlayer({...newPlayer, email: e.target.value})}
                />
              </div>
            </div>

            <div className="modal-actions">
              <button className="btn btn-ghost" onClick={() => setShowAddPlayer(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={addPlayer}>Add Player</button>
            </div>
          </div>
        </div>
      )}

      {/* EDIT PLAYER MODAL */}
      {(canManagePlayers || isParent) && showEditPlayer && editingPlayer && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowEditPlayer(false)}>
          <div className="modal" style={{width:"600px", maxHeight:"85vh", overflowY:"auto"}}>
            <div className="modal-title">Edit Player</div>
            
            <div className="form-group">
              <label className="form-label">Headshot Photo (optional)</label>
              <div style={{display:"flex", gap:"12px", alignItems:"center"}}>
                <div style={{
                  width:"80px", height:"80px", borderRadius:"10px", overflow:"hidden",
                  background: editingPlayer.headshot ? `url(${editingPlayer.headshot}) center/cover` : getAvatarColor(editingPlayer.id),
                  display:"flex", alignItems:"center", justifyContent:"center",
                  fontSize:"24px", fontWeight:"700", color:"white", border:`2px solid ${palette.border}`
                }}>
                  {!editingPlayer.headshot && editingPlayer.avatar}
                </div>
                <div style={{flex:1}}>
                  <input 
                    type="file" 
                    accept="image/*"
                    onChange={async (e) => {
                      const file = e.target.files[0];
                      if (file) {
                        const uploadedUrl = await uploadHeadshot(file);
                        if (uploadedUrl) {
                          setEditingPlayer({...editingPlayer, headshot: uploadedUrl});
                        }
                      }
                    }}
                    style={{
                      fontSize:"13px", padding:"8px 12px", border:`1px solid ${palette.border}`,
                      borderRadius:"8px", background: palette.bg, color: palette.text,
                      cursor:"pointer", width:"100%", fontFamily:"'Inter', sans-serif"
                    }}
                  />
                  {editingPlayer.headshot && (
                    <button 
                      className="btn btn-ghost btn-sm" 
                      onClick={() => setEditingPlayer({...editingPlayer, headshot: null})}
                      style={{marginTop:"6px"}}
                    >
                      Remove Photo
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Full Name *</label>
                <input className="form-input" placeholder="Alex Johnson" value={editingPlayer.name} onChange={e => setEditingPlayer({...editingPlayer, name: e.target.value})} />
              </div>
              <div className="form-group">
                <label className="form-label">Jersey Number</label>
                <input className="form-input" type="number" placeholder="10" value={editingPlayer.number} onChange={e => setEditingPlayer({...editingPlayer, number: e.target.value})} />
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Position</label>
                <select className="form-select" value={editingPlayer.position} onChange={e => setEditingPlayer({...editingPlayer, position: e.target.value})}>
                  {POSITIONS.map(p => <option key={p}>{p}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Status</label>
                <select className="form-select" value={editingPlayer.status} onChange={e => setEditingPlayer({...editingPlayer, status: e.target.value})}>
                  <option value="active">Active</option>
                  <option value="injured">Injured</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>

            {/* Parent Contact Info Section */}
            {(canManagePlayers || isParent) && (
              <>
                {/* Parent 1 Information */}
                <div style={{borderTop:`1px solid ${palette.border}`, marginTop:"20px", paddingTop:"20px"}}>
                  <div style={{fontSize:"14px", fontWeight:"700", marginBottom:"12px", color: palette.text}}>
                    Parent 1 Information
                    {isParent && editingPlayer.parent1Email === currentUser.email && (
                      <span style={{fontSize:"12px", fontWeight:"400", color: palette.textMuted, marginLeft:"8px"}}>
                        (Your Contact Info)
                      </span>
                    )}
                  </div>
                  <div className="form-group">
                    <label className="form-label">Parent 1 Name</label>
                    <input 
                      className="form-input" 
                      placeholder="Parent name" 
                      value={editingPlayer.parent1Name || ""} 
                      onChange={e => setEditingPlayer({...editingPlayer, parent1Name: e.target.value})} 
                      disabled={isParent && editingPlayer.parent1Email !== currentUser.email}
                      style={{opacity: (isParent && editingPlayer.parent1Email !== currentUser.email) ? 0.6 : 1}}
                    />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Parent 1 Phone</label>
                      <input 
                        className="form-input" 
                        type="tel" 
                        placeholder="555-0100" 
                        value={editingPlayer.parent1Phone || ""} 
                        onChange={e => setEditingPlayer({...editingPlayer, parent1Phone: e.target.value})} 
                        disabled={isParent && editingPlayer.parent1Email !== currentUser.email}
                        style={{opacity: (isParent && editingPlayer.parent1Email !== currentUser.email) ? 0.6 : 1}}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">
                        Parent 1 Email
                        {isParent && editingPlayer.parent1Email === currentUser.email && (
                          <span style={{fontSize:"11px", fontWeight:"400", color: palette.textMuted, marginLeft:"8px"}}>
                            (cannot be changed)
                          </span>
                        )}
                      </label>
                      <input 
                        className="form-input" 
                        type="email" 
                        placeholder="parent@email.com" 
                        value={editingPlayer.parent1Email || ""} 
                        onChange={e => setEditingPlayer({...editingPlayer, parent1Email: e.target.value})} 
                        disabled={isParent}
                        style={{opacity: isParent ? 0.6 : 1}}
                      />
                    </div>
                  </div>
                </div>

                {/* Parent 2 Information */}
                <div style={{borderTop:`1px solid ${palette.border}`, marginTop:"20px", paddingTop:"20px"}}>
                  <div style={{fontSize:"14px", fontWeight:"700", marginBottom:"12px", color: palette.text}}>
                    Parent 2 Information (optional)
                    {isParent && editingPlayer.parent2Email === currentUser.email && (
                      <span style={{fontSize:"12px", fontWeight:"400", color: palette.textMuted, marginLeft:"8px"}}>
                        (Your Contact Info)
                      </span>
                    )}
                  </div>
                  <div className="form-group">
                    <label className="form-label">Parent 2 Name</label>
                    <input 
                      className="form-input" 
                      placeholder="Parent name" 
                      value={editingPlayer.parent2Name || ""} 
                      onChange={e => setEditingPlayer({...editingPlayer, parent2Name: e.target.value})} 
                      disabled={isParent && editingPlayer.parent2Email !== currentUser.email}
                      style={{opacity: (isParent && editingPlayer.parent2Email !== currentUser.email) ? 0.6 : 1}}
                    />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Parent 2 Phone</label>
                      <input 
                        className="form-input" 
                        type="tel" 
                        placeholder="555-0100" 
                        value={editingPlayer.parent2Phone || ""} 
                        onChange={e => setEditingPlayer({...editingPlayer, parent2Phone: e.target.value})} 
                        disabled={isParent && editingPlayer.parent2Email !== currentUser.email}
                        style={{opacity: (isParent && editingPlayer.parent2Email !== currentUser.email) ? 0.6 : 1}}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">
                        Parent 2 Email
                        {isParent && editingPlayer.parent2Email === currentUser.email && (
                          <span style={{fontSize:"11px", fontWeight:"400", color: palette.textMuted, marginLeft:"8px"}}>
                            (cannot be changed)
                          </span>
                        )}
                      </label>
                      <input 
                        className="form-input" 
                        type="email" 
                        placeholder="parent@email.com" 
                        value={editingPlayer.parent2Email || ""} 
                        onChange={e => setEditingPlayer({...editingPlayer, parent2Email: e.target.value})} 
                        disabled={isParent}
                        style={{opacity: isParent ? 0.6 : 1}}
                      />
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Player Login Email - editable by managers/owners and player's own parents */}
            {(canManagePlayers || (isParent && currentUser.playerIds?.includes(editingPlayer?.id))) && (
              <div style={{borderTop:`1px solid ${palette.border}`, marginTop:"20px", paddingTop:"20px"}}>
                <div style={{fontSize:"14px", fontWeight:"700", marginBottom:"4px", color: palette.text}}>Player Login Email (optional)</div>
                <div style={{fontSize:"12px", color: palette.textMuted, marginBottom:"12px"}}>
                  If the player has their own email, they can log into the app directly to mark availability.
                </div>
                <div className="form-group">
                  <label className="form-label">Player Email</label>
                  <input
                    className="form-input"
                    type="email"
                    placeholder="player@email.com"
                    value={editingPlayer.email || ""}
                    onChange={e => setEditingPlayer({...editingPlayer, email: e.target.value})}
                  />
                </div>
              </div>
            )}

            <div className="modal-actions">
              <button className="btn btn-ghost" onClick={() => { setShowEditPlayer(false); setEditingPlayer(null); }}>Cancel</button>
              <button className="btn btn-primary" onClick={editPlayer}>Save Changes</button>
            </div>
          </div>
        </div>
      )}

      {/* ADD EVENT MODAL */}
      {canManageEvents && showAddEvent && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowAddEvent(false)}>
          <div className="modal">
            <div className="modal-title">Add Event</div>
            <div className="form-group">
              <label className="form-label">Event Type</label>
              <select className="form-select" value={newEvent.type} onChange={e => setNewEvent({...newEvent, type: e.target.value})}>
                <option value="game">Game</option>
                <option value="practice">Practice</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Title</label>
              <input className="form-input" placeholder={newEvent.type === "game" ? "vs. Opponent FC" : "Team Practice"} value={newEvent.title} onChange={e => setNewEvent({...newEvent, title: e.target.value})} />
              <div style={{fontSize:"12px", color: palette.textMuted, marginTop:"4px"}}>
                Leave blank for auto-generated title
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Date *</label>
                <input className="form-input" type="date" value={newEvent.date} onChange={e => setNewEvent({...newEvent, date: e.target.value})} />
              </div>
              <div className="form-group">
                <label className="form-label">{newEvent.type === "game" ? "Game Time" : "Start Time"}</label>
                <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                  <input 
                    className="form-input" 
                    type="time" 
                    value={newEvent.time} 
                    onChange={e => setNewEvent({...newEvent, time: e.target.value})}
                    disabled={newEvent.time === 'TBD'}
                    style={{flex: 1}}
                  />
                  <label style={{display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', whiteSpace: 'nowrap'}}>
                    <input 
                      type="checkbox" 
                      checked={newEvent.time === 'TBD'}
                      onChange={e => setNewEvent({...newEvent, time: e.target.checked ? 'TBD' : ''})}
                      style={{cursor: 'pointer'}}
                    />
                    <span style={{fontSize: '14px', color: palette.text}}>TBD</span>
                  </label>
                </div>
              </div>
            </div>
            
            {/* Additional time fields based on event type */}
            {newEvent.type === "game" && (
              <div className="form-group">
                <label className="form-label">Arrival Time</label>
                <input 
                  className="form-input" 
                  type="time" 
                  value={newEvent.arrivalTime} 
                  onChange={e => setNewEvent({...newEvent, arrivalTime: e.target.value})}
                  placeholder="When players should arrive"
                />
                <div style={{fontSize:"12px", color: palette.textMuted, marginTop:"4px"}}>
                  When players should arrive (e.g., 30 min before game time)
                </div>
              </div>
            )}
            
            {newEvent.type === "practice" && (
              <div className="form-group">
                <label className="form-label">End Time</label>
                <input 
                  className="form-input" 
                  type="time" 
                  value={newEvent.endTime} 
                  onChange={e => setNewEvent({...newEvent, endTime: e.target.value})}
                />
              </div>
            )}
            
            <div className="form-group">
              <label className="form-label">Location</label>
              <input className="form-input" placeholder="Stadium or training ground" value={newEvent.location} onChange={e => setNewEvent({...newEvent, location: e.target.value})} />
            </div>
            {newEvent.type === "game" && (
              <>
                <div className="form-group">
                  <label className="form-label">Opponent</label>
                  <input className="form-input" placeholder="Opponent team name" value={newEvent.opponent} onChange={e => setNewEvent({...newEvent, opponent: e.target.value})} />
                </div>
                <div className="form-group">
                  <label className="form-label">Home / Away</label>
                  <select className="form-select" value={newEvent.homeAway} onChange={e => setNewEvent({...newEvent, homeAway: e.target.value})}>
                    <option value="home">Home</option>
                    <option value="away">Away</option>
                  </select>
                </div>
              </>
            )}
            <div className="form-group">
              <label className="form-label">Notes</label>
              <input className="form-input" placeholder={newEvent.type === "game" ? "e.g., Bring dark jerseys" : "e.g., Focus on set pieces"} value={newEvent.notes} onChange={e => setNewEvent({...newEvent, notes: e.target.value})} />
            </div>

            {/* Volunteer Slots - owners only */}
            {isOwner && (
              <div style={{borderTop:`1px solid ${palette.border}`, marginTop:"20px", paddingTop:"20px"}}>
                <div style={{fontSize:"14px", fontWeight:"700", marginBottom:"4px", color: palette.text}}>🙋 Volunteer Slots (optional)</div>
                <div style={{fontSize:"12px", color: palette.textMuted, marginBottom:"12px"}}>
                  Allow members of another team to volunteer at this event.
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Spots Needed</label>
                    <input
                      className="form-input"
                      type="number"
                      min="0"
                      max="20"
                      placeholder="0"
                      value={newEvent.volunteerSlotsTotal || ""}
                      onChange={e => setNewEvent({...newEvent, volunteerSlotsTotal: parseInt(e.target.value) || 0})}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Open To Team</label>
                    <select
                      className="form-select"
                      value={newEvent.volunteerTeamIds?.[0] || ""}
                      onChange={e => setNewEvent({...newEvent, volunteerTeamIds: e.target.value ? [e.target.value] : []})}
                    >
                      <option value="">-- Select Team --</option>
                      {club?.teams?.items?.filter(t => t.id !== currentTeam?.id).map(t => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            )}

            <div className="modal-actions">
              <button className="btn btn-ghost" onClick={() => setShowAddEvent(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={addEvent}>Add Event</button>
            </div>
          </div>
        </div>
      )}

      {/* EDIT EVENT MODAL */}
      {canManageEvents && showEditEvent && editingEvent && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowEditEvent(false)}>
          <div className="modal">
            <div className="modal-title">Edit Event</div>
            <div className="form-group">
              <label className="form-label">Event Type</label>
              <select className="form-select" value={editingEvent.type} onChange={e => setEditingEvent({...editingEvent, type: e.target.value})}>
                <option value="game">Game</option>
                <option value="practice">Practice</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Title</label>
              <input className="form-input" placeholder={editingEvent.type === "game" ? "vs. Opponent FC" : "Team Practice"} value={editingEvent.title} onChange={e => setEditingEvent({...editingEvent, title: e.target.value})} />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Date *</label>
                <input className="form-input" type="date" value={editingEvent.date} onChange={e => setEditingEvent({...editingEvent, date: e.target.value})} />
              </div>
              <div className="form-group">
                <label className="form-label">{editingEvent.type === "game" ? "Game Time" : "Start Time"}</label>
                <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                  <input 
                    className="form-input" 
                    type="time" 
                    value={editingEvent.time} 
                    onChange={e => setEditingEvent({...editingEvent, time: e.target.value})}
                    disabled={editingEvent.time === 'TBD'}
                    style={{flex: 1}}
                  />
                  <label style={{display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', whiteSpace: 'nowrap'}}>
                    <input 
                      type="checkbox" 
                      checked={editingEvent.time === 'TBD'}
                      onChange={e => setEditingEvent({...editingEvent, time: e.target.checked ? 'TBD' : ''})}
                      style={{cursor: 'pointer'}}
                    />
                    <span style={{fontSize: '14px', color: palette.text}}>TBD</span>
                  </label>
                </div>
              </div>
            </div>
            
            {editingEvent.type === "game" && (
              <div className="form-group">
                <label className="form-label">Arrival Time</label>
                <input 
                  className="form-input" 
                  type="time" 
                  value={editingEvent.arrivalTime} 
                  onChange={e => setEditingEvent({...editingEvent, arrivalTime: e.target.value})}
                  placeholder="When players should arrive"
                />
                <div style={{fontSize:"12px", color: palette.textMuted, marginTop:"4px"}}>
                  When players should arrive (e.g., 30 min before game time)
                </div>
              </div>
            )}
            
            {editingEvent.type === "practice" && (
              <div className="form-group">
                <label className="form-label">End Time</label>
                <input 
                  className="form-input" 
                  type="time" 
                  value={editingEvent.endTime} 
                  onChange={e => setEditingEvent({...editingEvent, endTime: e.target.value})}
                />
              </div>
            )}
            
            <div className="form-group">
              <label className="form-label">Location</label>
              <input className="form-input" placeholder="Stadium or training ground" value={editingEvent.location} onChange={e => setEditingEvent({...editingEvent, location: e.target.value})} />
            </div>
            {editingEvent.type === "game" && (
              <>
                <div className="form-group">
                  <label className="form-label">Opponent</label>
                  <input className="form-input" placeholder="Opponent team name" value={editingEvent.opponent} onChange={e => setEditingEvent({...editingEvent, opponent: e.target.value})} />
                </div>
                <div className="form-group">
                  <label className="form-label">Home / Away</label>
                  <select className="form-select" value={editingEvent.homeAway} onChange={e => setEditingEvent({...editingEvent, homeAway: e.target.value})}>
                    <option value="home">Home</option>
                    <option value="away">Away</option>
                  </select>
                </div>
              </>
            )}
            <div className="form-group">
              <label className="form-label">Notes</label>
              <input className="form-input" placeholder={editingEvent.type === "game" ? "e.g., Bring dark jerseys" : "e.g., Focus on set pieces"} value={editingEvent.notes} onChange={e => setEditingEvent({...editingEvent, notes: e.target.value})} />
            </div>

            {/* Volunteer Slots - owners only */}
            {isOwner && (
              <div style={{borderTop:`1px solid ${palette.border}`, marginTop:"20px", paddingTop:"20px"}}>
                <div style={{fontSize:"14px", fontWeight:"700", marginBottom:"4px", color: palette.text}}>🙋 Volunteer Slots (optional)</div>
                <div style={{fontSize:"12px", color: palette.textMuted, marginBottom:"12px"}}>
                  Allow members of another team to volunteer at this event.
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Spots Needed</label>
                    <input
                      className="form-input"
                      type="number"
                      min="0"
                      max="20"
                      placeholder="0"
                      value={editingEvent.volunteerSlotsTotal || ""}
                      onChange={e => setEditingEvent({...editingEvent, volunteerSlotsTotal: parseInt(e.target.value) || 0})}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Open To Team</label>
                    <select
                      className="form-select"
                      value={editingEvent.volunteerTeamIds?.[0] || ""}
                      onChange={e => setEditingEvent({...editingEvent, volunteerTeamIds: e.target.value ? [e.target.value] : []})}
                    >
                      <option value="">-- Select Team --</option>
                      {club?.teams?.items?.filter(t => t.id !== currentTeam?.id).map(t => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
                {editingEvent.volunteerSignups?.length > 0 && (
                  <div style={{marginTop:"8px", fontSize:"12px", color: palette.textMuted}}>
                    Currently signed up: {editingEvent.volunteerSignups.join(", ")}
                  </div>
                )}
              </div>
            )}

            <div className="modal-actions">
              <button className="btn btn-ghost" onClick={() => {
                setShowEditEvent(false);
                setEditingEvent(null);
              }}>Cancel</button>
              <button className="btn btn-primary" onClick={editEvent}>Save Changes</button>
            </div>
          </div>
        </div>
      )}

      {/* SUBSCRIBE MODAL */}
      {showSubscribe && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowSubscribe(false)}>
          <div className="modal" style={{width:"550px", maxHeight:"90vh", overflowY:"auto"}}>
            <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:"6px"}}>
              <div className="modal-title" style={{marginBottom:0}}>📅 Subscribe to Calendar</div>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowSubscribe(false)}>✕</button>
            </div>
            <p style={{fontSize:"13px", color: palette.textMuted, marginBottom:"24px", lineHeight:"1.5"}}>
              Add {currentTeam?.name || club.name} schedule to your calendar. Auto-updates when events change!
            </p>

            {/* Auto-Updating Subscription (Recommended) */}
            <div style={{
              background:"rgba(34,197,94,0.08)", border:`2px solid ${palette.green}`,
              borderRadius:"12px", padding:"18px 20px", marginBottom:"16px"
            }}>
              <div style={{display:"flex", alignItems:"center", gap:"12px", marginBottom:"12px"}}>
                <div style={{
                  width:"48px", height:"48px", borderRadius:"10px", flexShrink:0,
                  background: palette.green, display:"flex", alignItems:"center", justifyContent:"center", fontSize:"24px"
                }}>✨</div>
                <div style={{flex:1}}>
                  <div style={{fontWeight:"700", fontSize:"15px", marginBottom:"3px", color: palette.green}}>
                    Auto-Updating Subscription (Recommended)
                  </div>
                  <div style={{fontSize:"12px", color: palette.textMuted}}>
                    Events automatically sync when you add/edit/delete them
                  </div>
                </div>
              </div>

              {/* Calendar URL */}
              <div style={{
                background: palette.surface, border:`1px solid ${palette.border}`,
                borderRadius:"8px", padding:"12px", marginBottom:"12px"
              }}>
                <div style={{fontSize:"11px", fontWeight:"600", textTransform:"uppercase", letterSpacing:"0.5px", color: palette.textMuted, marginBottom:"8px"}}>
                  Calendar URL
                </div>
                <div style={{
                  fontFamily:"'Monaco', 'Courier New', monospace",
                  fontSize:"11px", color: palette.text,
                  background: palette.surfaceAlt, padding:"8px 10px", borderRadius:"6px",
                  border:`1px solid ${palette.border}`, wordBreak:"break-all", lineHeight:"1.4"
                }}>
                  {CALENDAR_API_URL === 'YOUR_API_GATEWAY_URL_HERE/calendar' ? (
                    <span style={{color: palette.yellow}}>⚠️ Calendar API not configured yet - see setup instructions</span>
                  ) : (
                    `${CALENDAR_API_URL}/${currentTeam?.id || 'team-id'}`
                  )}
                </div>
                {CALENDAR_API_URL !== 'YOUR_API_GATEWAY_URL_HERE/calendar' && (
                  <button
                    className="btn btn-ghost btn-sm"
                    style={{marginTop:"8px", width:"100%"}}
                    onClick={() => {
                      const url = `${CALENDAR_API_URL}/${currentTeam?.id}`;
                      navigator.clipboard.writeText(url);
                      showToast('Calendar URL copied!', 'success');
                    }}
                  >
                    📋 Copy URL
                  </button>
                )}
              </div>

              {/* Platform Instructions */}
              <div style={{fontSize:"12px", color: palette.textMuted, lineHeight:"1.6"}}>
                <div style={{fontWeight:"600", color: palette.text, marginBottom:"8px"}}>How to Subscribe:</div>
                
                <div style={{marginBottom:"8px"}}>
                  <strong style={{color: palette.text}}>📱 iPhone/iPad:</strong><br/>
                  Copy URL above → Safari → Paste → Open → Add to Calendar
                </div>
                
                <div style={{marginBottom:"8px"}}>
                  <strong style={{color: palette.text}}>📅 Google Calendar:</strong><br/>
                  Settings → Add calendar → From URL → Paste URL → Add calendar
                </div>
                
                <div>
                  <strong style={{color: palette.text}}>💼 Outlook:</strong><br/>
                  Calendar → Add calendar → Subscribe from web → Paste URL → Import
                </div>
              </div>
            </div>

            {/* Divider */}
            <div style={{
              display:"flex", alignItems:"center", gap:"12px",
              margin:"24px 0", color: palette.textDim, fontSize:"12px", fontWeight:"600"
            }}>
              <div style={{flex:1, height:"1px", background: palette.border}}></div>
              OR
              <div style={{flex:1, height:"1px", background: palette.border}}></div>
            </div>

            {/* One-Time Download */}
            <div style={{
              background: palette.surfaceAlt, border:`1px solid ${palette.border}`,
              borderRadius:"12px", padding:"18px 20px", marginBottom:"12px"
            }}>
              <div style={{display:"flex", alignItems:"center", gap:"12px", marginBottom:"12px"}}>
                <div style={{
                  width:"44px", height:"44px", borderRadius:"10px", flexShrink:0,
                  background:"rgba(255,107,53,0.15)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"22px"
                }}>📥</div>
                <div style={{flex:1}}>
                  <div style={{fontWeight:"700", fontSize:"14px", marginBottom:"3px"}}>One-Time Download</div>
                  <div style={{fontSize:"12px", color: palette.textMuted}}>
                    Download .ics file (won't auto-update)
                  </div>
                </div>
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() => downloadICS(events, currentTeam?.name || club.name)}
                >
                  Download
                </button>
              </div>
              <div style={{fontSize:"11px", color: palette.textDim, lineHeight:"1.5"}}>
                💡 This is a snapshot. Re-download whenever events change.
              </div>
            </div>

            {/* Setup Notice */}
            {CALENDAR_API_URL === 'YOUR_API_GATEWAY_URL_HERE/calendar' && (
              <div style={{
                marginTop:"16px", padding:"14px 16px", borderRadius:"10px",
                background:"rgba(245,158,11,0.08)", border:"1px solid rgba(245,158,11,0.2)"
              }}>
                <div style={{fontSize:"13px", color: palette.yellow, lineHeight:"1.5", fontWeight:"500"}}>
                  ⚠️ Auto-updating calendar not configured yet
                </div>
                <div style={{fontSize:"12px", color: palette.textMuted, lineHeight:"1.5", marginTop:"6px"}}>
                  Follow the setup instructions in CALENDAR_SETUP_INSTRUCTIONS.md to enable auto-updating subscriptions.
                  For now, use the one-time download option above.
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ADD TEAM MODAL (Owner Only) */}
      {isOwner && showAddTeam && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowAddTeam(false)}>
          <div className="modal">
            <div className="modal-title">Add New Team</div>
            
            {/* Show which club this team will be added to */}
            {isSuperAdmin && club && (
              <div style={{
                padding:"12px 16px", 
                background: palette.accentSoft, 
                border:`1px solid ${palette.accent}`,
                borderRadius:"8px",
                fontSize:"13px",
                color: palette.text,
                marginBottom:"16px",
                fontWeight:"600"
              }}>
                🏢 Adding team to: <strong>{club.name}</strong>
              </div>
            )}
            
            <div className="form-group">
              <label className="form-label">Team Name *</label>
              <input className="form-input" placeholder="Thunder Hawks FC" value={newTeam.name} onChange={e => setNewTeam({...newTeam, name: e.target.value})} />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Sport *</label>
                <input className="form-input" placeholder="Soccer" value={newTeam.sport} onChange={e => setNewTeam({...newTeam, sport: e.target.value})} />
              </div>
              <div className="form-group">
                <label className="form-label">Season</label>
                <input className="form-input" placeholder="Spring 2026" value={newTeam.season} onChange={e => setNewTeam({...newTeam, season: e.target.value})} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Age Group</label>
              <input className="form-input" placeholder="U12 Boys" value={newTeam.ageGroup} onChange={e => setNewTeam({...newTeam, ageGroup: e.target.value})} />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Team Color</label>
                <input type="color" className="form-input" value={newTeam.color} onChange={e => setNewTeam({...newTeam, color: e.target.value})} style={{height:"42px", padding:"4px"}} />
              </div>
              <div className="form-group">
                <label className="form-label">Logo Emoji</label>
                <input className="form-input" placeholder="⚽" value={newTeam.logo} onChange={e => setNewTeam({...newTeam, logo: e.target.value})} maxLength="2" />
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn btn-ghost" onClick={() => setShowAddTeam(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={addTeam}>Create Team</button>
            </div>
          </div>
        </div>
      )}

      {/* EDIT TEAM MODAL (Owner Only) */}
      {isOwner && showEditTeam && editingTeam && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowEditTeam(false)}>
          <div className="modal">
            <div className="modal-title">Edit Team</div>
            <div className="form-group">
              <label className="form-label">Team Name *</label>
              <input className="form-input" placeholder="Thunder Hawks FC" value={editingTeam.name} onChange={e => setEditingTeam({...editingTeam, name: e.target.value})} />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Sport *</label>
                <input className="form-input" placeholder="Soccer" value={editingTeam.sport} onChange={e => setEditingTeam({...editingTeam, sport: e.target.value})} />
              </div>
              <div className="form-group">
                <label className="form-label">Season</label>
                <input className="form-input" placeholder="Spring 2026" value={editingTeam.season} onChange={e => setEditingTeam({...editingTeam, season: e.target.value})} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Age Group</label>
              <input className="form-input" placeholder="U12 Boys" value={editingTeam.ageGroup} onChange={e => setEditingTeam({...editingTeam, ageGroup: e.target.value})} />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Team Color</label>
                <input type="color" className="form-input" value={editingTeam.color} onChange={e => setEditingTeam({...editingTeam, color: e.target.value})} style={{height:"42px", padding:"4px"}} />
              </div>
              <div className="form-group">
                <label className="form-label">Logo Emoji</label>
                <input className="form-input" placeholder="⚽" value={editingTeam.logo} onChange={e => setEditingTeam({...editingTeam, logo: e.target.value})} maxLength="2" />
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn btn-ghost" onClick={() => { setShowEditTeam(false); setEditingTeam(null); }}>Cancel</button>
              <button className="btn btn-primary" onClick={editTeam}>Save Changes</button>
            </div>
          </div>
        </div>
      )}

      {/* ASSIGN COACHES MODAL (Owner and Team Manager) */}
      {isOwner && showAssignCoach && teamForCoachAssignment && (
        <div className="modal-overlay" onClick={e => {
          if (e.target === e.currentTarget) {
            setShowAssignCoach(false);
            setTeamForCoachAssignment(null);
          }
        }}>
          <div className="modal" style={{maxWidth:"600px"}}>
            <div className="modal-title">Manage Coaches - {teamForCoachAssignment.name}</div>
            
            {/* Current Coaches */}
            <div style={{marginBottom:"24px"}}>
              <div style={{fontSize:"12px", fontWeight:"700", textTransform:"uppercase", letterSpacing:"1px", color: palette.textMuted, marginBottom:"12px"}}>
                Current Coaches ({teamCoachList.length})
              </div>
              {teamCoachList.length > 0 ? (
                <div style={{display:"grid", gap:"8px"}}>
                  {teamCoachList.map(coach => (
                    <div key={coach.id} style={{
                      display:"flex", alignItems:"center", justifyContent:"space-between",
                      background: palette.surfaceAlt, border:`1px solid ${palette.border}`,
                      borderRadius:"8px", padding:"12px 14px"
                    }}>
                      <div style={{display:"flex", alignItems:"center", gap:"12px"}}>
                        <div style={{
                          width:"36px", height:"36px", borderRadius:"50%",
                          background: coach.role === 'team_manager' ? palette.accent : palette.yellow,
                          display:"flex", alignItems:"center", justifyContent:"center",
                          fontSize:"13px", fontWeight:"700", color:"white"
                        }}>
                          {coach.avatar}
                        </div>
                        <div>
                          <div style={{fontSize:"14px", fontWeight:"600"}}>{coach.name}</div>
                          <div style={{fontSize:"12px", color: palette.textMuted}}>
                            {coach.role === 'team_manager' ? 'Team Manager' : 'Coach'}
                          </div>
                        </div>
                      </div>
                      <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => assignCoach(teamForCoachAssignment.id, coach.userId, 'remove')}
                        style={{color: palette.red}}
                        disabled={coach.role === 'team_manager' && !isOwner}
                        title={coach.role === 'team_manager' && !isOwner ? "Only owner can remove head coach" : "Remove coach"}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{textAlign:"center", padding:"20px", color: palette.textDim, fontSize:"13px"}}>
                  No coaches assigned yet
                </div>
              )}
            </div>

            {/* Available Coaches to Add */}
            <div style={{borderTop:`1px solid ${palette.border}`, paddingTop:"20px", marginBottom:"20px"}}>
              <div style={{fontSize:"12px", fontWeight:"700", textTransform:"uppercase", letterSpacing:"1px", color: palette.textMuted, marginBottom:"12px"}}>
                Available Coaches
              </div>
              <div style={{display:"grid", gap:"8px"}}>
                {availableCoaches
                  .filter(coach => {
                    const isAssigned = teamForCoachAssignment.coachIds?.includes(coach.userId);
                    const canAssign = isOwner || (isTeamManager && coach.role === 'coach');
                    return !isAssigned && canAssign;
                  })
                  .map(coach => (
                    <div key={coach.id} style={{
                      display:"flex", alignItems:"center", justifyContent:"space-between",
                      background: palette.surfaceAlt, border:`1px solid ${palette.border}`,
                      borderRadius:"8px", padding:"12px 14px"
                    }}>
                      <div style={{display:"flex", alignItems:"center", gap:"12px"}}>
                        <div style={{
                          width:"36px", height:"36px", borderRadius:"50%",
                          background: coach.role === 'team_manager' ? palette.accent : palette.yellow,
                          display:"flex", alignItems:"center", justifyContent:"center",
                          fontSize:"13px", fontWeight:"700", color:"white"
                        }}>
                          {coach.avatar}
                        </div>
                        <div>
                          <div style={{fontSize:"14px", fontWeight:"600"}}>{coach.name}</div>
                          <div style={{fontSize:"12px", color: palette.textMuted}}>
                            {coach.role === 'team_manager' ? 'Team Manager' : 'Coach'}
                          </div>
                        </div>
                      </div>
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() => assignCoach(teamForCoachAssignment.id, coach.userId, 'add')}
                      >
                        Add
                      </button>
                    </div>
                  ))}
                {availableCoaches.filter(coach => {
                  const isAssigned = teamForCoachAssignment.coachIds?.includes(coach.userId);
                  const canAssign = isOwner || (isTeamManager && coach.role === 'coach');
                  return !isAssigned && canAssign;
                }).length === 0 && (
                  <div style={{textAlign:"center", padding:"20px", color: palette.textDim, fontSize:"13px"}}>
                    {isTeamManager ? "No assistant coaches available to assign" : "All coaches are already assigned"}
                  </div>
                )}
              </div>
            </div>

            {/* Invite New Coach */}
            <div style={{borderTop:`1px solid ${palette.border}`, paddingTop:"20px"}}>
              <div style={{fontSize:"12px", fontWeight:"700", textTransform:"uppercase", letterSpacing:"1px", color: palette.textMuted, marginBottom:"12px"}}>
                Invite New Coach
              </div>
              
              {/* Debug: Clean Duplicates Button */}
              {isOwner && (
                <div style={{marginBottom:"16px"}}>
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={async () => {
                      if (!window.confirm('Remove duplicate coach IDs from this team?')) return;
                      
                      try {
                        // Get fresh team data
                        const getTeamQuery = `
                          query GetTeam($id: ID!) {
                            getTeam(id: $id) {
                              id
                              name
                              coachIds
                            }
                          }
                        `;
                        
                        const teamResult = await client.graphql({
                          query: getTeamQuery,
                          variables: { id: teamForCoachAssignment.id }
                        });
                        
                        const team = teamResult.data?.getTeam;
                        if (!team) {
                          showToast('Team not found', 'error');
                          return;
                        }
                        
                        const originalCoachIds = team.coachIds || [];
                        const uniqueCoachIds = [...new Set(originalCoachIds)];
                        
                        if (originalCoachIds.length === uniqueCoachIds.length) {
                          showToast('No duplicates found!', 'success');
                          return;
                        }
                        
                        console.log('Removing duplicates:', originalCoachIds, '→', uniqueCoachIds);
                        
                        // Update with unique IDs only
                        const updateTeamMutation = `
                          mutation UpdateTeam($input: UpdateTeamInput!) {
                            updateTeam(input: $input) {
                              id
                              coachIds
                            }
                          }
                        `;
                        
                        await client.graphql({
                          query: updateTeamMutation,
                          variables: {
                            input: {
                              id: team.id,
                              coachIds: uniqueCoachIds
                            }
                          }
                        });
                        
                        showToast(`Removed ${originalCoachIds.length - uniqueCoachIds.length} duplicate(s)!`, 'success');
                        
                        // Reload
                        await fetchClubData();
                        await loadCoaches();
                        
                      } catch (error) {
                        console.error('Cleanup error:', error);
                        showToast('Cleanup failed: ' + error.message, 'error');
                      }
                    }}
                    style={{color: palette.yellow}}
                  >
                    🧹 Clean Duplicate Coaches
                  </button>
                </div>
              )}
              
              <div style={{background: palette.surfaceAlt, border:`1px solid ${palette.border}`, borderRadius:"8px", padding:"16px"}}>
                <div style={{fontSize:"13px", color: palette.textMuted, marginBottom:"12px"}}>
                  Send an email invitation to a coach. They'll receive a link to create their account and join your team.
                </div>
                <div style={{display:"flex", gap:"8px", alignItems:"flex-end"}}>
                  <div style={{flex:1}}>
                    <label style={{fontSize:"12px", fontWeight:"600", display:"block", marginBottom:"4px"}}>Coach Email</label>
                    <input
                      type="email"
                      className="form-input"
                      placeholder="coach@example.com"
                      style={{width:"100%"}}
                    />
                  </div>
                  <div style={{width:"140px"}}>
                    <label style={{fontSize:"12px", fontWeight:"600", display:"block", marginBottom:"4px"}}>Role</label>
                    <select className="form-input" style={{width:"100%"}}>
                      <option value="coach">Coach</option>
                      {isOwner && <option value="team_manager">Team Manager</option>}
                    </select>
                  </div>
                  <button 
                    className="btn btn-primary"
                    onClick={async () => {
                      const emailInput = document.querySelector('input[type="email"][placeholder="coach@example.com"]');
                      const roleSelect = emailInput?.parentElement?.parentElement?.querySelector('select');
                      const email = emailInput?.value?.trim();
                      const role = roleSelect?.value;
                      
                      if (!email || !email.includes('@')) {
                        showToast('Please enter a valid email address', 'error');
                        return;
                      }
                      
                      try {
                        // Check if user exists using GraphQL
                        const listUsersQuery = `
                          query ListUsers($filter: ModelUserFilterInput) {
                            listUsers(filter: $filter) {
                              items {
                                id
                                userId
                                email
                                name
                                role
                              }
                            }
                          }
                        `;
                        
                        const usersResult = await client.graphql({
                          query: listUsersQuery,
                          variables: {
                            filter: { email: { eq: email } }
                          }
                        });
                        
                        const users = usersResult.data?.listUsers?.items || [];
                        
                        if (users.length > 0) {
                          // User exists - update their role and create coach profile
                          const existingUser = users[0];
                          const userId = existingUser.userId;
                          const userName = existingUser.name || email.split('@')[0];
                          
                          // Update user role to coach using GraphQL
                          const updateUserMutation = `
                            mutation UpdateUser($input: UpdateUserInput!) {
                              updateUser(input: $input) {
                                id
                                role
                              }
                            }
                          `;
                          
                          await client.graphql({
                            query: updateUserMutation,
                            variables: {
                              input: {
                                id: existingUser.id,
                                role: role
                              }
                            }
                          });
                          
                          // Check if coach profile already exists using GraphQL
                          const listCoachesQuery = `
                            query ListCoaches($filter: ModelCoachFilterInput) {
                              listCoaches(filter: $filter) {
                                items {
                                  id
                                  userId
                                  name
                                  email
                                  role
                                  avatar
                                }
                              }
                            }
                          `;
                          
                          const coachesResult = await client.graphql({
                            query: listCoachesQuery,
                            variables: {
                              filter: { userId: { eq: userId } }
                            }
                          });
                          
                          const existingCoaches = coachesResult.data?.listCoaches?.items || [];
                          
                          let coachProfile;
                          if (existingCoaches.length > 0) {
                            // Update existing coach profile using GraphQL
                            console.log('Coach profile already exists, updating role');
                            const updateCoachMutation = `
                              mutation UpdateCoach($input: UpdateCoachInput!) {
                                updateCoach(input: $input) {
                                  id
                                  userId
                                  role
                                }
                              }
                            `;
                            
                            await client.graphql({
                              query: updateCoachMutation,
                              variables: {
                                input: {
                                  id: existingCoaches[0].id,
                                  role: role
                                }
                              }
                            });
                            coachProfile = existingCoaches[0];
                            console.log('Updated coach profile:', coachProfile);
                          } else {
                            // Create coach profile
                            console.log('Creating new coach profile');
                            coachProfile = await createCoachProfile(userId, userName, email, role);
                            console.log('Created coach profile:', coachProfile);
                          }
                          
                          // Add coach to this team
                          console.log('Assigning coach to team:', { coachUserId: coachProfile?.userId, teamId: teamForCoachAssignment.id });
                          if (coachProfile && coachProfile.userId) {
                            await assignCoach(teamForCoachAssignment.id, coachProfile.userId, 'add');
                          }
                          
                          showToast(`${userName} added as ${role === 'team_manager' ? 'Team Manager' : 'Coach'}!`, 'success');
                          
                          console.log('Reloading club data and coaches...');
                          // Reload data
                          await fetchClubData();
                          await loadCoaches();
                          console.log('Reload complete');
                          
                        } else {
                          // User doesn't exist yet
                          showToast(`User not found. They need to sign up first with email: ${email}`, 'error');
                        }
                        
                        // Clear inputs
                        if (emailInput) emailInput.value = '';
                        if (roleSelect) roleSelect.value = 'coach';
                        
                      } catch (error) {
                        console.error('Error assigning coach:', error);
                        console.error('Error details:', {
                          message: error.message,
                          errors: error.errors,
                          stack: error.stack
                        });
                        showToast(`Failed to assign coach: ${error.message || 'Unknown error'}`, 'error');
                      }
                    }}
                  >
                    Send Invite
                  </button>
                </div>
                <div style={{fontSize:"11px", color: palette.textDim, marginTop:"8px"}}>
                  💡 Note: This records the invitation. When the coach signs up with this email, they'll automatically be added to your club as a coach. You can then assign them to teams from "Available Coaches" above.
                </div>
              </div>
            </div>

            <div className="modal-actions" style={{marginTop:"20px"}}>
              <button className="btn btn-ghost" onClick={() => { setShowAssignCoach(false); setTeamForCoachAssignment(null); }}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* DELETE TEAM CONFIRMATION MODAL (Owner Only) */}
      {isOwner && showDeleteConfirm && teamToDelete && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowDeleteConfirm(false)}>
          <div className="modal" style={{maxWidth:"500px"}}>
            <div style={{fontSize:"48px", textAlign:"center", marginBottom:"16px"}}>⚠️</div>
            <div className="modal-title" style={{textAlign:"center", color: palette.red}}>Delete Team?</div>
            <div style={{fontSize:"14px", color: palette.textMuted, lineHeight:"1.6", marginBottom:"20px", textAlign:"center"}}>
              Are you sure you want to delete <strong style={{color: palette.text}}>{teamToDelete.name}</strong>?
              <br/><br/>
              All players ({teamToDelete.roster?.length || 0}), events ({teamToDelete.events?.length || 0}), and messages will be permanently lost.
              <br/><br/>
              <strong style={{color: palette.red}}>This action cannot be undone.</strong>
            </div>
            <div className="modal-actions">
              <button className="btn btn-ghost" onClick={() => { setShowDeleteConfirm(false); setTeamToDelete(null); }}>Cancel</button>
              <button 
                className="btn btn-primary" 
                onClick={() => { 
                  deleteTeam(teamToDelete.id); 
                  setShowDeleteConfirm(false); 
                  setTeamToDelete(null);
                }}
                style={{background: palette.red, boxShadow: `0 2px 8px ${palette.red}40`}}
              >
                Delete Team
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Invite Modal */}
      {(isOwner || isTeamManager || isCoach) && showInvite && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowInvite(false)}>
          <div className="modal" style={{maxWidth:"500px"}}>
            <div className="modal-title">Invite to {currentTeam?.name || 'Team'}</div>
            <div style={{fontSize:"14px", color: palette.textMuted, marginBottom:"20px", lineHeight:"1.6"}}>
              Send an invitation to join this team. They'll need to sign up with the email address you provide.
            </div>
            
            <div className="form-group">
              <label className="form-label">Email Address</label>
              <input 
                className="form-input" 
                type="email"
                placeholder="player@example.com" 
                value={inviteEmail} 
                onChange={e => setInviteEmail(e.target.value)}
                disabled={inviteSending}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Role</label>
              <select 
                className="form-select" 
                value={inviteRole} 
                onChange={e => setInviteRole(e.target.value)}
                disabled={inviteSending}
              >
                <option value="player">Player</option>
                <option value="parent">Parent</option>
                {(isOwner || isTeamManager) && <option value="coach">Coach</option>}
              </select>
            </div>

            <div style={{
              padding:"12px 16px", 
              background: palette.surfaceAlt, 
              border:`1px solid ${palette.border}`,
              borderRadius:"8px",
              fontSize:"13px",
              color: palette.textMuted,
              lineHeight:"1.5",
              marginBottom:"20px"
            }}>
              <strong style={{color: palette.text}}>📧 How it works:</strong><br/>
              • The recipient will receive instructions to sign up<br/>
              • They must create an account with this exact email<br/>
              • Once signed up, they'll have access to {currentTeam?.name || 'the team'}
            </div>

            <div className="modal-actions">
              <button 
                className="btn btn-ghost" 
                onClick={() => { 
                  setShowInvite(false); 
                  setInviteEmail(""); 
                  setInviteRole("player");
                }}
                disabled={inviteSending}
              >
                Cancel
              </button>
              <button 
                className="btn btn-primary" 
                onClick={sendInvite}
                disabled={inviteSending || !inviteEmail}
              >
                {inviteSending ? 'Sending...' : '✉️ Send Invite'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Club Modal (Super Admin Only) */}
      {isSuperAdmin && showCreateClub && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowCreateClub(false)}>
          <div className="modal" style={{maxWidth:"500px"}}>
            <div className="modal-title">Create New Club</div>
            <div style={{fontSize:"14px", color: palette.textMuted, marginBottom:"20px", lineHeight:"1.6"}}>
              Create a new club on the platform. You can assign an owner now or later.
            </div>
            
            <div className="form-group">
              <label className="form-label">Club Name *</label>
              <input 
                className="form-input" 
                type="text"
                placeholder="Thunder Hawks Sports Club" 
                value={newClubName} 
                onChange={e => setNewClubName(e.target.value)}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Owner Email (Optional)</label>
              <input 
                className="form-input" 
                type="email"
                placeholder="owner@example.com" 
                value={newClubOwnerEmail} 
                onChange={e => setNewClubOwnerEmail(e.target.value)}
              />
              <div style={{fontSize:"12px", color: palette.textMuted, marginTop:"4px"}}>
                Leave blank to assign yourself as owner initially
              </div>
            </div>

            <div className="modal-actions">
              <button 
                className="btn btn-ghost" 
                onClick={() => { 
                  setShowCreateClub(false); 
                  setNewClubName(""); 
                  setNewClubOwnerEmail("");
                }}
              >
                Cancel
              </button>
              <button 
                className="btn btn-primary" 
                onClick={createNewClub}
                disabled={!newClubName.trim()}
              >
                🏢 Create Club
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assign Club Owner Modal (Super Admin Only) */}
      {isSuperAdmin && showAssignOwner && clubForOwnerAssignment && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowAssignOwner(false)}>
          <div className="modal" style={{maxWidth:"500px"}}>
            <div className="modal-title">Assign Owner to {clubForOwnerAssignment.name}</div>
            <div style={{fontSize:"14px", color: palette.textMuted, marginBottom:"20px", lineHeight:"1.6"}}>
              Assign a club owner who will have full management access to this club.
              <br/><br/>
              Current owner: <strong style={{color: palette.text}}>{clubForOwnerAssignment.ownerId || 'None'}</strong>
            </div>
            
            <div className="form-group">
              <label className="form-label">New Owner Email</label>
              <input 
                className="form-input" 
                type="email"
                placeholder="newowner@example.com" 
                value={assignOwnerEmail} 
                onChange={e => setAssignOwnerEmail(e.target.value)}
              />
            </div>

            <div style={{
              padding:"12px 16px", 
              background: palette.surfaceAlt, 
              border:`1px solid ${palette.border}`,
              borderRadius:"8px",
              fontSize:"13px",
              color: palette.textMuted,
              lineHeight:"1.5",
              marginBottom:"20px"
            }}>
              <strong style={{color: palette.text}}>📧 How it works:</strong><br/>
              • If the user exists, they'll be updated to club_owner<br/>
              • If they don't exist, an invitation will be sent<br/>
              • They'll have full access to manage {clubForOwnerAssignment.name}
            </div>

            <div className="modal-actions">
              <button 
                className="btn btn-ghost" 
                onClick={() => { 
                  setShowAssignOwner(false); 
                  setAssignOwnerEmail("");
                  setClubForOwnerAssignment(null);
                }}
              >
                Cancel
              </button>
              <button 
                className="btn btn-primary" 
                onClick={assignClubOwner}
                disabled={!assignOwnerEmail}
              >
                👤 Assign Owner
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Phase 1: Bottom Tab Navigation (Mobile Only) */}
      <nav className="bottom-nav">
        <div className="bottom-nav-content">
          <button 
            className={`bottom-nav-item ${tab === 'dashboard' ? 'active' : ''}`}
            onClick={() => { setTab('dashboard'); setMobileMenuOpen(false); }}
          >
            <div className="bottom-nav-icon">🏠</div>
            <div>Home</div>
          </button>
          
          <button 
            className={`bottom-nav-item ${tab === 'roster' ? 'active' : ''}`}
            onClick={() => { setTab('roster'); setMobileMenuOpen(false); }}
          >
            <div className="bottom-nav-icon">👥</div>
            <div>Team</div>
          </button>
          
          <button 
            className={`bottom-nav-item ${tab === 'schedule' ? 'active' : ''}`}
            onClick={() => { setTab('schedule'); setMobileMenuOpen(false); }}
          >
            <div className="bottom-nav-icon">📅</div>
            <div>Schedule</div>
          </button>
          
          <button 
            className={`bottom-nav-item ${tab === 'attendance' ? 'active' : ''}`}
            onClick={() => { setTab('attendance'); setMobileMenuOpen(false); }}
          >
            <div className="bottom-nav-icon">✅</div>
            <div>Attend</div>
          </button>
          
          <button 
            className={`bottom-nav-item ${tab === 'messages' ? 'active' : ''}`}
            onClick={() => { setTab('messages'); setMobileMenuOpen(false); }}
          >
            <div className="bottom-nav-icon">📧</div>
            <div>Email</div>
          </button>

          {currentTeam?.financialSheetUrl && (isOwner || isTeamManager || isParent) && !isPlayer && (
            <button
              className={`bottom-nav-item ${tab === 'financials' ? 'active' : ''}`}
              onClick={() => { setTab('financials'); setMobileMenuOpen(false); }}
            >
              <div className="bottom-nav-icon">💰</div>
              <div>Finances</div>
            </button>
          )}

          {volunteerEvents.length > 0 && (
            <button
              className={`bottom-nav-item ${tab === 'volunteer' ? 'active' : ''}`}
              onClick={() => { setTab('volunteer'); setMobileMenuOpen(false); }}
            >
              <div className="bottom-nav-icon">🙋</div>
              <div>Volunteer</div>
            </button>
          )}
        </div>
      </nav>

      {/* EMAIL PICKER MODAL */}
      {showEmailPicker && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowEmailPicker(false)}>
          <div className="modal" style={{width:"600px", maxHeight:"85vh", overflowY:"auto"}}>
            <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:"16px"}}>
              <div className="modal-title" style={{marginBottom:0}}>Select Email Recipients</div>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowEmailPicker(false)}>✕</button>
            </div>

            {/* Parents Section */}
            <div style={{marginBottom:"20px"}}>
              <div style={{
                fontSize:"13px", fontWeight:"600", textTransform:"uppercase",
                letterSpacing:"0.5px", color: palette.textMuted, marginBottom:"12px"
              }}>
                Parents ({roster.length} players)
              </div>
              
              {roster.map(player => {
                const parent1 = player.parent1Email ? { name: player.parent1Name || 'Parent 1', email: player.parent1Email, id: `${player.id}-p1` } : null;
                const parent2 = player.parent2Email ? { name: player.parent2Name || 'Parent 2', email: player.parent2Email, id: `${player.id}-p2` } : null;
                
                return (
                  <div key={player.id} style={{marginBottom:"8px"}}>
                    <div style={{fontSize:"12px", fontWeight:"600", color: palette.textMuted, marginBottom:"4px"}}>
                      {player.name}
                    </div>
                    {parent1 && (
                      <label style={{
                        display:"flex", alignItems:"center", gap:"10px",
                        padding:"8px 12px", background: palette.surfaceAlt,
                        borderRadius:"6px", cursor:"pointer", marginBottom:"4px"
                      }}>
                        <input
                          type="checkbox"
                          checked={selectedEmailRecipients.includes(parent1.email)}
                          onChange={e => {
                            if (e.target.checked) {
                              setSelectedEmailRecipients([...selectedEmailRecipients, parent1.email]);
                            } else {
                              setSelectedEmailRecipients(selectedEmailRecipients.filter(email => email !== parent1.email));
                            }
                          }}
                          style={{width:"18px", height:"18px", cursor:"pointer"}}
                        />
                        <div style={{flex:1, fontSize:"13px"}}>
                          <div style={{fontWeight:"500"}}>{parent1.name}</div>
                          <div style={{fontSize:"12px", color: palette.textMuted}}>{parent1.email}</div>
                        </div>
                      </label>
                    )}
                    {parent2 && (
                      <label style={{
                        display:"flex", alignItems:"center", gap:"10px",
                        padding:"8px 12px", background: palette.surfaceAlt,
                        borderRadius:"6px", cursor:"pointer"
                      }}>
                        <input
                          type="checkbox"
                          checked={selectedEmailRecipients.includes(parent2.email)}
                          onChange={e => {
                            if (e.target.checked) {
                              setSelectedEmailRecipients([...selectedEmailRecipients, parent2.email]);
                            } else {
                              setSelectedEmailRecipients(selectedEmailRecipients.filter(email => email !== parent2.email));
                            }
                          }}
                          style={{width:"18px", height:"18px", cursor:"pointer"}}
                        />
                        <div style={{flex:1, fontSize:"13px"}}>
                          <div style={{fontWeight:"500"}}>{parent2.name}</div>
                          <div style={{fontSize:"12px", color: palette.textMuted}}>{parent2.email}</div>
                        </div>
                      </label>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Coaches Section */}
            {teamCoachList.length > 0 && (
              <div style={{marginBottom:"20px"}}>
                <div style={{
                  fontSize:"13px", fontWeight:"600", textTransform:"uppercase",
                  letterSpacing:"0.5px", color: palette.textMuted, marginBottom:"12px"
                }}>
                  Coaches ({teamCoachList.length})
                </div>
                
                {teamCoachList.map(coach => (
                  <label key={coach.id} style={{
                    display:"flex", alignItems:"center", gap:"10px",
                    padding:"8px 12px", background: palette.surfaceAlt,
                    borderRadius:"6px", cursor:"pointer", marginBottom:"4px"
                  }}>
                    <input
                      type="checkbox"
                      checked={selectedEmailRecipients.includes(coach.email)}
                      onChange={e => {
                        if (e.target.checked) {
                          setSelectedEmailRecipients([...selectedEmailRecipients, coach.email]);
                        } else {
                          setSelectedEmailRecipients(selectedEmailRecipients.filter(email => email !== coach.email));
                        }
                      }}
                      style={{width:"18px", height:"18px", cursor:"pointer"}}
                    />
                    <div style={{flex:1, fontSize:"13px"}}>
                      <div style={{fontWeight:"500"}}>{coach.name}</div>
                      <div style={{fontSize:"12px", color: palette.textMuted}}>{coach.email}</div>
                    </div>
                  </label>
                ))}
              </div>
            )}

            {/* Selected Count & Actions */}
            <div style={{
              display:"flex", gap:"12px", paddingTop:"16px",
              borderTop:`1px solid ${palette.border}`
            }}>
              <button
                className="btn btn-ghost"
                onClick={() => {
                  setSelectedEmailRecipients([]);
                  setShowEmailPicker(false);
                }}
                style={{flex:1}}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={() => {
                  if (selectedEmailRecipients.length === 0) {
                    showToast('Please select at least one recipient', 'error');
                    return;
                  }
                  
                  const emailList = [...new Set(selectedEmailRecipients)].join(', ');
                  navigator.clipboard.writeText(emailList);
                  showToast(`Copied ${selectedEmailRecipients.length} email${selectedEmailRecipients.length !== 1 ? 's' : ''} to clipboard!`, 'success');
                  setSelectedEmailRecipients([]);
                  setShowEmailPicker(false);
                }}
                style={{flex:2}}
                disabled={selectedEmailRecipients.length === 0}
              >
                📋 Copy {selectedEmailRecipients.length} Email{selectedEmailRecipients.length !== 1 ? 's' : ''}
              </button>
            </div>
          </div>
        </div>
      )}

      </div> {/* Close pull-to-refresh wrapper */}
    </>
  );
}
